import { supabase } from "./supabase";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
    { id: "Infrastructure", icon: "🏗️", color: "#60a5fa" },
    { id: "Construction", icon: "🏛️", color: "#a78bfa" },
    { id: "Technology", icon: "💻", color: "#34d399" },
    { id: "Energy", icon: "⚡", color: "#fbbf24" },
    { id: "Healthcare", icon: "🏥", color: "#f87171" },
    { id: "Education", icon: "📚", color: "#4ade80" },
    { id: "Defence", icon: "🛡️", color: "#94a3b8" },
    { id: "Agriculture", icon: "🌾", color: "#86efac" },
    { id: "Transport", icon: "🚆", color: "#c084fc" },
    { id: "Water & Sanitation", icon: "💧", color: "#38bdf8" },
    { id: "Smart City", icon: "🏙️", color: "#f472b6" },
    { id: "Other", icon: "📦", color: "#64748b" },
];

const DEPARTMENTS = [
    "Administration Department",
    "Facilities & Maintenance",
    "Projects & Infrastructure",
    "Security & IT",
    "Finance & Accounts",
    "Human Resources",
    "Operations",
    "Procurement & Stores",
    "Sales & Marketing",
    "Legal & Compliance",
    "Research & Development",
    "Quality Assurance",
    "Public Works Department (PWD)",
    "Urban Development Authority",
    "Water Resources Department",
    "Other",
];

const TENDER_TYPES = [
    "Open Tender (OT)",
    "Limited Tender Enquiry (LTE)",
    "Expression of Interest (EOI)",
    "Request for Proposal (RFP)",
    "Request for Quotation (RFQ)",
    "Single Tender",
    "Empanelment",
    "Auction",
];

const CONTRACT_TYPES = [
    "Item Rate Contract",
    "Lump Sum Contract",
    "Percentage Rate Contract",
    "Cost Plus Contract",
    "Turnkey Contract",
    "EPC Contract",
    "PPP / BOT",
    "Framework Agreement",
];

const VALUE_RANGES = [
    { label: "Any Value", min: 0, max: Infinity },
    { label: "< ₹10L", min: 0, max: 1000000 },
    { label: "₹10L – ₹1Cr", min: 1000000, max: 10000000 },
    { label: "₹1Cr – ₹5Cr", min: 10000000, max: 50000000 },
    { label: "₹5Cr – ₹25Cr", min: 50000000, max: 250000000 },
    { label: "> ₹25Cr", min: 250000000, max: Infinity },
];

const SORT_OPTIONS = [
    { value: "newest", label: "Newest First" },
    { value: "deadline", label: "Deadline (Soonest)" },
    { value: "value_desc", label: "Value: High → Low" },
    { value: "value_asc", label: "Value: Low → High" },
    { value: "bids_desc", label: "Most Bids" },
    { value: "title", label: "Title A–Z" },
];

const STATUS_CONFIG = {
    active: { dot: "#16a34a", bg: "rgba(22,163,74,0.10)", color: "#15803d", label: "Active" },
    closed: { dot: "#dc2626", bg: "rgba(220,38,38,0.10)", color: "#b91c1c", label: "Closed" },
    draft: { dot: "#d97706", bg: "rgba(217,119,6,0.10)", color: "#b45309", label: "Draft" },
    submitted: { dot: "#1d4ed8", bg: "rgba(29,78,216,0.10)", color: "#1d4ed8", label: "Submitted" },
    awarded: { dot: "#16a34a", bg: "rgba(22,163,74,0.10)", color: "#15803d", label: "Awarded" },
    rejected: { dot: "#dc2626", bg: "rgba(220,38,38,0.10)", color: "#b91c1c", label: "Rejected" },
    withdrawn: { dot: "#64748b", bg: "rgba(100,116,139,0.10)", color: "#475569", label: "Withdrawn" },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const fmt = n => n >= 10000000 ? "₹" + (n / 10000000).toFixed(2) + " Cr" : n >= 100000 ? "₹" + (n / 100000).toFixed(1) + "L" : "₹" + Number(n).toLocaleString("en-IN");
const fmtFull = n => "₹" + Number(n).toLocaleString("en-IN");
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const timeAgo = d => {
    if (!d) return "";
    const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (s < 60) return "Just now";
    if (s < 3600) return `Posted ${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `Posted ${Math.floor(s / 3600)}h ago`;
    if (s < 2592000) return `Posted ${Math.floor(s / 86400)}d ago`;
    return `Posted ${fmtDate(d)}`;
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = e => EMAIL_RE.test(String(e || "").trim());
const fmtTime = d => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const daysDiff = d => Math.ceil((new Date(d) - new Date()) / 86400000);

// ─────────────────────────────────────────────────────────────────────────────
// DEEPSEEK AI  —  bid rating + content-aware document verification
// ─────────────────────────────────────────────────────────────────────────────
//
// ⚠️  SECURITY NOTE
// The key below lives in client-side JS, so anyone who opens the site can read it
// and spend against your DeepSeek quota. For production, proxy these calls through
// the Supabase Edge Function in `supabase/functions/verify-document` (shipped
// alongside this file) and set DEEPSEEK_ENDPOINT to that function's URL. Then this
// key can be removed from the browser entirely.
//
const DEEPSEEK_KEY = "sk-659e17c3c40e4999a5dc2db5f991947f";
const DEEPSEEK_ENDPOINT = "https://api.deepseek.com/chat/completions";

// Current V4 model IDs (deepseek-chat / deepseek-reasoner were retired in 2026).
const DS_TEXT_MODEL = "deepseek-v4-flash";
const DS_VISION_MODEL = "deepseek-v4-flash-vision-exp"; // multimodal (reads images)

// ── Concurrency limiter: parallel uploads must not all hit the API at once,
//    or some get rate-limited/timed-out and fail. Cap simultaneous calls. ───────
let _dsActive = 0;
const _dsQueue = [];
const DS_MAX_CONCURRENT = 2;
const _dsSettle = () => { _dsActive--; _dsPump(); };
function _dsPump() {
    while (_dsActive < DS_MAX_CONCURRENT && _dsQueue.length) {
        const { fn, resolve, reject } = _dsQueue.shift();
        _dsActive++;
        Promise.resolve().then(fn).then(resolve, reject).finally(_dsSettle);
    }
}
const runLimited = (fn) => new Promise((resolve, reject) => { _dsQueue.push({ fn, resolve, reject }); _dsPump(); });
const _sleep = (ms) => new Promise(r => setTimeout(r, ms));

// ── One HTTP attempt (timeout, JSON mode, HTTP-error surfacing) ───────────────
const _deepSeekOnce = async ({ model, messages, maxTokens, temperature, json, timeoutMs }) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
        const res = await fetch(DEEPSEEK_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${DEEPSEEK_KEY}` },
            body: JSON.stringify({
                model,
                messages,
                max_tokens: maxTokens,
                temperature,
                ...(json ? { response_format: { type: "json_object" } } : {}),
            }),
            signal: ctrl.signal,
        });
        if (!res.ok) {
            const body = await res.text().catch(() => "");
            const err = new Error(`DeepSeek HTTP ${res.status} ${body.slice(0, 180)}`);
            err.status = res.status;
            err.retryable = res.status === 429 || res.status >= 500; // rate limit / server error
            throw err;
        }
        const data = await res.json();
        if (data.error) throw new Error(data.error.message || "DeepSeek API error");
        return data.choices?.[0]?.message?.content || "";
    } finally {
        clearTimeout(timer);
    }
};

// ── Low-level caller: concurrency-limited, with retry + backoff on transient
//    failures (429, 5xx, network drop, timeout). Same doc => same result. ───────
const callDeepSeek = ({ model, messages, maxTokens = 400, temperature = 0.1, json = true, timeoutMs = 60000, retries = 3 }) =>
    runLimited(async () => {
        let lastErr;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                return await _deepSeekOnce({ model, messages, maxTokens, temperature, json, timeoutMs });
            } catch (e) {
                lastErr = e;
                const isAbort = e.name === "AbortError";           // our timeout
                const isNetwork = e instanceof TypeError;          // fetch could not reach server
                const retryable = e.retryable || isAbort || isNetwork;
                if (!retryable || attempt === retries) break;
                await _sleep(Math.min(8000, 700 * Math.pow(2, attempt)) + Math.random() * 400);
            }
        }
        throw lastErr;
    });

// ── Tolerant JSON extraction (models sometimes wrap or prefix their JSON) ──────
const parseJsonLoose = (raw) => {
    if (!raw) return null;
    const s = String(raw).replace(/```json/gi, "").replace(/```/g, "").trim();
    try { return JSON.parse(s); } catch { /* fall through */ }
    const m = s.match(/\{[\s\S]*\}/);
    if (m) { try { return JSON.parse(m[0]); } catch { /* fall through */ } }
    return null;
};

// ── Parse the structured bid message into its parts ───────────────────────────
// New bids store labelled sections; legacy bids are treated as proposal-only.
const parseBidMessage = (message = "") => {
    const m = message || "";
    const grab = (re) => { const x = m.match(re); return x ? x[1].trim() : ""; };
    const experience = grab(/Years of Experience:\s*(.+)/i);
    const projects = grab(/Company Experience[^\n:]*:\s*\n?([\s\S]*?)(?:\n\nTechnical Proposal:|\n\nEMD Reference:|$)/i);
    const proposal = grab(/Technical Proposal:\s*\n?([\s\S]*?)(?:\n\nEMD Reference:|$)/i);
    const emdRef = grab(/EMD Reference:\s*(.+)/i);
    const anyLabel = /Years of Experience:|Company Experience|Technical Proposal:|EMD Reference:/i.test(m);
    return { experience, projects, proposal: proposal || (!anyLabel ? m.trim() : ""), emdRef };
};

// ─────────────────────────────────────────────────────────────────────────────
// BID RATING
// ─────────────────────────────────────────────────────────────────────────────

const rateBidWithAI = async (tender, bid, bidder) => {
    const pct = ((bid.amount / tender.value) * 100).toFixed(1);
    const belowBy = (100 - pct).toFixed(1);
    const parts = parseBidMessage(bid.message);

    const prompt = `You are a senior procurement officer evaluating a bid for a tender. Analyse this bid objectively and rate it out of 10.

═══ TENDER INFORMATION ═══
Title: ${tender.title}
Reference No: ${tender.reference_no || "N/A"}
Department: ${tender.dept || "N/A"}
Category: ${tender.category}
Estimated Value: Rs ${Number(tender.value).toLocaleString("en-IN")}
Contract Duration: ${tender.contract_duration || "N/A"}
Scope of Work: ${tender.description || "N/A"}
Eligibility Criteria: ${tender.eligibility_criteria || "Not specified"}
Technical Specifications: ${tender.technical_specs || "Not specified"}

═══ BID DETAILS ═══
Bidder Company: ${bidder?.company_name || "Unknown"}
Registration No: ${bidder?.reg_no || "Not provided"}
Bid Amount: Rs ${Number(bid.amount).toLocaleString("en-IN")}
Bid as % of Estimate: ${pct}% (${bid.amount <= tender.value ? belowBy + "% below estimate, competitive" : Math.abs(belowBy) + "% above estimate, over-priced"})
Years of Experience (stated by bidder): ${parts.experience || "Not provided"}
Company Experience / Past Projects (stated by bidder): ${parts.projects || "Not provided"}
Technical Proposal / Cover Note: ${parts.proposal || "Not provided"}

═══ SCORING CRITERIA (total 10) ═══
1. Price Competitiveness (3 pts): Is the bid realistic vs the estimate? Extremely low without justification is suspicious; above estimate is unfavourable.
2. Experience & Track Record (3 pts): Judge the stated years of experience and the relevance, scale and credibility of the past projects described against this tender's scope. Vague, placeholder, or missing experience/projects scores low. Do NOT assume authenticity; judge only what is described.
3. Technical Proposal Quality (2 pts): Does the proposal show real understanding of THIS project's requirements?
4. Completeness & Risk (2 pts): Did the bidder provide experience, past projects, a proposal, and registration details, with no red flags (placeholder text like 'asdasd', missing reg no, extreme pricing)?

Respond ONLY with a valid JSON object, no markdown, no text outside JSON:
{"rating":7,"price_score":3,"experience_score":2,"proposal_score":2,"completeness_score":2,"risk":"Low","summary":"Competitive bid at 94% of estimate; bidder states 6 years of experience with two relevant projects, and the proposal addresses the scope.","recommendation":"Recommended","flags":"None"}

Possible recommendation values: "Highly Recommended" | "Recommended" | "Needs Review" | "Not Recommended"
Possible risk values: "Low" | "Medium" | "High"`;

    try {
        const raw = await callDeepSeek({
            model: DS_TEXT_MODEL,
            messages: [{ role: "user", content: prompt }],
            maxTokens: 400,
            temperature: 0.3,
        });
        return parseJsonLoose(raw);
    } catch (e) {
        console.error("DeepSeek rating error:", e);
        return null;
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// DOCUMENT VERIFICATION  (content-aware)
//
// Return contract (unchanged, so the UI needs no edits):
//   { valid: boolean, confidence: "high"|"medium"|"low", message: string, suggestion: string }
// ─────────────────────────────────────────────────────────────────────────────

const IMAGE_EXTS = ["jpg", "jpeg", "png", "webp", "gif", "bmp", "tiff", "tif"];
const PDF_EXTS = ["pdf"];
const DOCX_EXTS = ["docx"];
const LEGACY_DOC_EXTS = ["doc"];
const BAD_EXTS = ["exe", "zip", "rar", "7z", "mp4", "mp3", "avi", "mov", "bat", "sh", "js", "msi", "apk", "dll"];

// ── File → base64 data URL ────────────────────────────────────────────────────
const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // "data:<mime>;base64,..."
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const loadImageEl = (src) => new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("image decode failed"));
    im.src = src;
});

// ── Normalise any raster image to a downscaled JPEG data URL the vision model
//    reliably accepts (handles bmp/tiff decode when the browser can, transparency,
//    and 10 MB monsters). Falls back to the raw data URL if canvas fails. ───────
const normalizeImageForVision = async (file, maxDim = 1600) => {
    const rawUrl = await fileToBase64(file);
    try {
        const img = await loadImageEl(rawUrl);
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height || 1));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";          // flatten transparency onto white
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        return canvas.toDataURL("image/jpeg", 0.9);
    } catch {
        return rawUrl; // couldn't canvas-convert (e.g. some TIFFs) — try raw
    }
};

// ── Load pdfjs once, with its web worker properly configured ──────────────────
// Without a configured worker, pdfjs throws ("fake worker" failure) and every
// PDF fails to parse. We point workerSrc at the version-matched CDN build.
let _pdfjsPromise = null;
const getPdfjs = () => {
    if (_pdfjsPromise) return _pdfjsPromise;
    _pdfjsPromise = (async () => {
        let mod = null;
        const attempts = [
            () => import("pdfjs-dist/legacy/build/pdf.mjs"),
            () => import("pdfjs-dist/legacy/build/pdf"),
            () => import("pdfjs-dist"),
        ];
        for (const load of attempts) {
            try { mod = await load(); if (mod) break; } catch (e) { /* try next entry */ }
        }
        let lib = mod;
        if (lib && !lib.getDocument && lib.default) lib = lib.default;
        if (!lib || !lib.getDocument) throw new Error("pdfjs-dist could not be loaded — is it installed?");
        try {
            if (!lib.GlobalWorkerOptions.workerSrc) {
                const v = lib.version || "4.0.379";
                const major = parseInt(v, 10) || 4;
                const ext = major >= 4 ? "mjs" : "js";
                lib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${v}/legacy/build/pdf.worker.min.${ext}`;
            }
        } catch (e) { console.warn("pdfjs worker config failed:", e); }
        return lib;
    })();
    return _pdfjsPromise;
};

// ── Extract embedded text from a (digital) PDF ────────────────────────────────
const extractPdfText = async (file) => {
    try {
        const pdfjsLib = await getPdfjs();
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let text = "";
        const maxPages = Math.min(pdf.numPages, 4);
        for (let p = 1; p <= maxPages; p++) {
            const page = await pdf.getPage(p);
            const content = await page.getTextContent();
            text += content.items.map(i => i.str).join(" ") + "\n";
            if (text.length > 2000) break;
        }
        return text.trim().slice(0, 2000);
    } catch (e) {
        console.warn("PDF text extraction failed:", e);
        return null;
    }
};

// ── Rasterise page 1 of a PDF to an image data URL (for scanned/image PDFs) ────
const rasterizePdfPage = async (file, pageNum = 1, maxWidth = 1400) => {
    try {
        const pdfjsLib = await getPdfjs();
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(pageNum);
        const base = page.getViewport({ scale: 1 });
        const scale = Math.min(2.2, maxWidth / base.width);
        const viewport = page.getViewport({ scale: scale > 0 ? scale : 1 });
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport }).promise;
        return canvas.toDataURL("image/jpeg", 0.9);
    } catch (e) {
        console.warn("PDF rasterization failed:", e);
        return null;
    }
};

// ── Extract text from a .docx ─────────────────────────────────────────────────
const extractDocxText = async (file) => {
    try {
        const mammoth = await import("mammoth");
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        return (result.value || "").trim().slice(0, 2000);
    } catch (e) {
        console.warn("DOCX text extraction failed:", e);
        return null;
    }
};

// ── Shared verifier persona (kept identical across text & vision branches) ─────
const VERIFIER_SYSTEM = `You are a document-screening assistant for a private company's e-procurement (tender) portal in India. Your ONLY job is to check two things: (1) whether an uploaded file matches the specific document type the bidder was asked to provide, and (2) whether it is readable and not blank, a placeholder, a screenshot of something else, or an unrelated file. You are NOT an authority on authenticity — you must never state or imply that a document is genuine, authentic, official, verified, or valid as a real document. Whether the document is truly authentic is decided later by a human reviewer.

You must reply with ONLY a JSON object of this exact shape (no markdown, no prose outside the JSON):
{"valid": true, "confidence": "high", "message": "<one specific sentence describing what the file appears to be and the key details you can see>", "suggestion": "<if invalid: exactly what the bidder should upload instead; else empty string>"}

Rules:
- "valid" true only if the file clearly matches the requested document TYPE and is readable.
- "confidence": "high" when you can read the content and are sure of the match; "medium" when readable but partly ambiguous; "low" when you could barely read it.
- Base your judgment on the CONTENT you can see, never on the file name alone.
- Helpful signals that a file matches the requested type: letterhead, issuing-authority name, registration/certificate/GSTIN/PAN numbers, dates, seals/stamps, signatures, company names, logos. Use these only to judge the TYPE match, never to declare the document authentic.
- Reject: personal photos/selfies, website or WhatsApp screenshots, blank/near-blank pages, draft/placeholder/watermarked samples, and files that are clearly a different document type than required.
- WORDING: describe what the file "appears to be" or is "consistent with", and name the details you can see. NEVER use the words "genuine", "authentic", "official", "verified", or call it a real/valid document. Do not vouch for authenticity in any form.
- STYLE: write the message as one plain, simple sentence, like a neutral system check. Do NOT use em-dashes or the "—"/"–" characters, and do not use "--"; join clauses with commas or a full stop instead. Do not sound conversational or like an assistant.
- Example good message: "This appears to be a PAN card showing a name, date of birth, and PAN number, consistent with the requested document." Example for a mismatch: "This appears to be a screenshot of a tender portal filter menu, not a GST certificate."`;

// Strip em/en dashes (and stray double hyphens) from user-facing verdict text so
// messages read like a plain system check, not model-written prose.
const sanitizeVerdict = (parsed) => {
    if (!parsed) return parsed;
    const clean = (s) => typeof s === "string"
        ? s.replace(/\s*[—–]\s*/g, ", ").replace(/\s*--\s*/g, ", ").replace(/\s{2,}/g, " ").replace(/\s+([.,;:])/g, "$1").trim()
        : s;
    if (parsed.message) parsed.message = clean(parsed.message);
    if (parsed.suggestion) parsed.suggestion = clean(parsed.suggestion);
    return parsed;
};

// ── Verify via the text model (digital PDF / DOCX / metadata fallback) ─────────
const verifyText = async (requiredDocName, contentBlock, { lowConfidenceCap = false } = {}) => {
    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const raw = await callDeepSeek({
                model: DS_TEXT_MODEL,
                maxTokens: 300,
                temperature: 0.1,
                messages: [
                    { role: "system", content: VERIFIER_SYSTEM },
                    { role: "user", content: `The bidder was required to upload: "${requiredDocName}".\n\n${contentBlock}\n\nReturn the JSON verdict.` },
                ],
            });
            const parsed = sanitizeVerdict(parseJsonLoose(raw));
            if (parsed) {
                if (lowConfidenceCap && parsed.confidence === "high") parsed.confidence = "medium";
                return parsed;
            }
        } catch (e) { console.warn("verifyText attempt failed:", e); }
    }
    return null;
};

// ── Verify via the vision model (photos & scanned PDFs) ───────────────────────
// The vision model is experimental, so retry the whole call if it errors OR
// returns something we can't parse — not just on network errors.
const verifyImage = async (requiredDocName, imageDataUrl) => {
    for (let attempt = 0; attempt < 3; attempt++) {
        try {
            const raw = await callDeepSeek({
                model: DS_VISION_MODEL,
                maxTokens: 300,
                temperature: 0.1,
                messages: [
                    { role: "system", content: VERIFIER_SYSTEM },
                    {
                        role: "user",
                        content: [
                            { type: "text", text: `The bidder was required to upload: "${requiredDocName}". The attached image is what they uploaded. Examine it and return the JSON verdict.` },
                            { type: "image_url", image_url: { url: imageDataUrl } },
                        ],
                    },
                ],
            });
            const parsed = sanitizeVerdict(parseJsonLoose(raw));
            if (parsed) return parsed;
        } catch (e) { console.warn("verifyImage attempt failed:", e); }
    }
    return null;
};

// ── OCR fallback: read text from an image when the vision model is unavailable ──
const ocrImage = async (imageDataUrl) => {
    try {
        const mod = await import("tesseract.js");
        const Tesseract = mod.default || mod;
        const { data } = await Tesseract.recognize(imageDataUrl, "eng");
        return (data?.text || "").trim().slice(0, 2000);
    } catch (e) {
        console.warn("OCR failed:", e);
        return null;
    }
};

// ── Main entry point ──────────────────────────────────────────────────────────
const validateDocumentWithAI = async (requiredDocName, file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();

    // 0) Hard-block dangerous / obviously-wrong file types
    if (BAD_EXTS.includes(ext) || !ext) {
        return {
            valid: false, confidence: "high",
            message: `File type ".${ext || "unknown"}" is not accepted for tender documents.`,
            suggestion: `Please upload a PDF, or a clear scan/photo (JPG or PNG) of your "${requiredDocName}".`,
        };
    }

    try {
        // 1) IMAGE → vision model, then OCR fallback if vision is unavailable
        if (IMAGE_EXTS.includes(ext)) {
            const dataUrl = await normalizeImageForVision(file);
            const result = await verifyImage(requiredDocName, dataUrl);
            if (result) return result;
            // Vision failed → OCR the image and verify the extracted text
            const ocrText = await ocrImage(dataUrl);
            if (ocrText && ocrText.length > 25) {
                const block = `The uploaded file is an image. Below is the TEXT read from it by OCR (may contain scan errors):\n───────────────\n${ocrText}\n───────────────`;
                const r2 = await verifyText(requiredDocName, block, { lowConfidenceCap: true });
                if (r2) return r2;
            }
            return await metadataFallback(requiredDocName, file, ext);
        }

        // 2) PDF → embedded text if present, otherwise rasterise & use vision
        if (PDF_EXTS.includes(ext)) {
            const text = await extractPdfText(file);
            if (text && text.length > 40) {
                const block = `Below is the TEXT extracted from the PDF they uploaded (first 2000 chars):\n───────────────\n${text}\n───────────────`;
                const result = await verifyText(requiredDocName, block);
                if (result) return result;
            }
            // Scanned/image PDF (no usable text) → render page 1 and let vision read it
            const pageImg = await rasterizePdfPage(file);
            if (pageImg) {
                const result = await verifyImage(requiredDocName, pageImg);
                if (result) return result;
                // Vision failed → OCR the rendered page
                const ocrText = await ocrImage(pageImg);
                if (ocrText && ocrText.length > 25) {
                    const block = `The PDF appears to be a scan. Below is the TEXT read from page 1 by OCR (may contain scan errors):\n───────────────\n${ocrText}\n───────────────`;
                    const r2 = await verifyText(requiredDocName, block, { lowConfidenceCap: true });
                    if (r2) return r2;
                }
            }
            return {
                valid: true, confidence: "low",
                message: "This PDF could not be read automatically (it may be an unusual scan). Please double-check it is the correct document before submitting.",
                suggestion: "If possible, upload a clearer scan or a digital (searchable) PDF.",
            };
        }

        // 3) DOCX → text model
        if (DOCX_EXTS.includes(ext)) {
            const text = await extractDocxText(file);
            if (text && text.length > 40) {
                const block = `Below is the TEXT extracted from the Word document they uploaded:\n───────────────\n${text}\n───────────────`;
                const result = await verifyText(requiredDocName, block);
                if (result) return result;
            }
            return await metadataFallback(requiredDocName, file, ext);
        }

        // 4) Legacy .doc (binary — mammoth can't read it) & everything else → metadata
        if (LEGACY_DOC_EXTS.includes(ext)) {
            const result = await metadataFallback(requiredDocName, file, ext);
            return {
                ...result,
                suggestion: result.suggestion || "For reliable verification, re-save and upload as PDF or .docx.",
            };
        }

        // 5) XLS/XLSX/CSV/etc → metadata fallback
        return await metadataFallback(requiredDocName, file, ext);
    } catch (e) {
        console.error("Document validation error:", e);
        // Never leave the UI stuck: return a safe, non-blocking low-confidence pass.
        return {
            valid: true, confidence: "low",
            message: "Automatic verification is temporarily unavailable. Please make sure this is the correct document before submitting.",
            suggestion: "",
        };
    }
};

// ── Filename/size-only assessment when content can't be read ──────────────────
async function metadataFallback(requiredDocName, file, ext) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    try {
        const raw = await callDeepSeek({
            model: DS_TEXT_MODEL,
            maxTokens: 200,
            temperature: 0.1,
            messages: [
                { role: "system", content: VERIFIER_SYSTEM },
                {
                    role: "user",
                    content: `The bidder was required to upload: "${requiredDocName}".\n\nThe file CONTENT could not be extracted, so judge ONLY from its metadata:\n- File name: "${file.name}"\n- Extension: .${ext}\n- Size: ${sizeMB} MB\n\nSince you cannot see the content, never use confidence "high". Return the JSON verdict.`,
                },
            ],
        });
        const parsed = parseJsonLoose(raw);
        if (parsed) {
            if (parsed.confidence === "high") parsed.confidence = "medium";
            if (!parsed.message) parsed.message = "Filename looks plausible, but the content could not be read, so please confirm it is the correct document before submitting.";
            return parsed;
        }
    } catch (e) {
        console.warn("Metadata fallback error:", e);
    }
    return {
        valid: true, confidence: "low",
        message: "Could not automatically verify this document. Please ensure it is the correct file before submitting.",
        suggestion: "",
    };
}

const catMeta = id => CATEGORIES.find(c => c.id === id) || CATEGORIES[CATEGORIES.length - 1];
const initials = s => (s || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

const hl = (text, q) => {
    if (!q || !text) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return text;
    return <>{text.slice(0, i)}<mark style={{ background: "rgba(253,224,71,0.5)", color: "#92400e", borderRadius: 2, padding: "0 2px" }}>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────

const T = {
    bg: "#f0f2f5", surface: "#ffffff", card: "#ffffff",
    border: "#e4e7ec", borderHi: "#c8cdd7",
    text: "#0d1117", muted: "#5c6776", faint: "#f7f8fa",
    accent: "#2563eb", accentLo: "rgba(37,99,235,0.07)", accentMid: "rgba(37,99,235,0.15)",
    dark: "#0f172a", darkHi: "#1e293b", darkLo: "rgba(15,23,42,0.06)",
    gold: "#854d0e", green: "#15803d", red: "#dc2626",
    shadow: "0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)",
    shadowMd: "0 4px 20px rgba(15,23,42,0.08), 0 2px 8px rgba(15,23,42,0.04)",
    shadowLg: "0 12px 48px rgba(15,23,42,0.12), 0 4px 16px rgba(15,23,42,0.06)",
};

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL STYLE
// ─────────────────────────────────────────────────────────────────────────────

const GlobalStyle = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=JetBrains+Mono:wght@400;500;600&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility; }
    body { background: ${T.bg}; color: ${T.text}; font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 14px; line-height: 1.5; }
    h1,h2,h3,h4,h5,h6 { font-family: 'DM Sans', sans-serif; font-weight: 700; letter-spacing: -0.3px; }
    @keyframes spin    { to { transform: rotate(360deg); } }
    @keyframes fadeUp  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
    @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:.4; } }
    @keyframes slideIn { from { transform:translateX(110%); opacity:0; } to { transform:translateX(0); opacity:1; } }
    @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
    .card-hover { transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease; }
    .card-hover:hover { border-color: ${T.accent}30 !important; box-shadow: 0 4px 20px rgba(37,99,235,0.10) !important; transform: translateY(-1px); }
    .row-hover { transition: background 0.12s; }
    .row-hover:hover { background: ${T.faint} !important; }
    .nav-item { transition: all 0.14s ease; }
    .nav-item:hover { background: rgba(255,255,255,0.10) !important; color: #ffffff !important; }
    .nav-item-light { transition: all 0.14s ease; }
    .nav-item-light:hover { background: ${T.accentLo} !important; color: ${T.accent} !important; }
    ::-webkit-scrollbar { width: 5px; height: 5px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #ced3dc; border-radius: 6px; }
    ::-webkit-scrollbar-thumb:hover { background: #b0b8c6; }
    input, select, textarea { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif !important; }
    button { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif !important; }
    ::selection { background: rgba(37,99,235,0.15); color: #1d4ed8; }
    .mono { font-family: 'JetBrains Mono', 'Fira Code', monospace !important; }
    *:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 2px; border-radius: 4px; }
    @media print {
      .no-print { display: none !important; }
      body { background: white; }
      .print-only { display: block !important; }
    }
  `}</style>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────

const Badge = ({ status }) => {
    const s = STATUS_CONFIG[status] || { dot: "#64748b", bg: "rgba(100,116,139,0.10)", color: "#64748b", label: status };
    return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.07em", border: `1px solid ${s.color}30`, fontFamily: "'DM Sans', sans-serif" }}>
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot, flexShrink: 0, boxShadow: `0 0 0 2px ${s.dot}30` }} />{s.label}
    </span>;
};

const CatBadge = ({ cat }) => {
    const m = catMeta(cat);
    return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, color: T.muted, background: T.faint, padding: "3px 10px", borderRadius: 6, fontWeight: 600, letterSpacing: "0.06em", border: `1px solid ${T.border}`, whiteSpace: "nowrap", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif" }}>
        <span style={{ display: "flex", alignItems: "center", opacity: 0.75 }}>{CAT_ICON_SVGS[m.id]}</span>
        {cat}
    </span>;
};

const Avatar = ({ name, size = 40, color }) => {
    const colors = ["#2563eb", "#7c3aed", "#0891b2", "#d97706", "#059669", "#e11d48", "#ea580c"];
    const bg = color || colors[(name || "").charCodeAt(0) % colors.length];
    return <div style={{ width: size, height: size, borderRadius: "50%", background: `linear-gradient(135deg, ${bg}30, ${bg}18)`, border: `2px solid ${bg}45`, display: "flex", alignItems: "center", justifyContent: "center", color: bg, fontSize: size * .35, fontWeight: 700, flexShrink: 0, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.5px" }}>{initials(name)}</div>;
};

const Spinner = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80 }}>
        <div style={{ width: 30, height: 30, border: `2.5px solid ${T.border}`, borderTop: `2.5px solid ${T.accent}`, borderRadius: "50%", animation: "spin .75s linear infinite" }} />
    </div>
);

const Empty = ({ icon, title, sub, cta }) => (
    <div style={{ textAlign: "center", padding: "72px 20px", background: T.card, borderRadius: 16, border: `1px dashed ${T.border}`, animation: "fadeUp .3s ease" }}>
        <div style={{ fontSize: 44, marginBottom: 16, filter: "grayscale(0.3)" }}>{icon}</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 6 }}>{title}</div>
        {sub && <div style={{ fontSize: 13, color: T.muted, marginBottom: cta ? 24 : 0, maxWidth: 300, margin: "0 auto" }}>{sub}</div>}
        {cta && <div style={{ marginTop: 24 }}>{cta}</div>}
    </div>
);

const Btn = ({ children, variant = "primary", loading, icon, size = "md", ...p }) => {
    const V = {
        primary: { background: T.accent, color: "#fff", border: `1px solid ${T.accent}`, boxShadow: `0 1px 4px rgba(37,99,235,0.30)` },
        secondary: { background: "#f1f4f9", color: T.text, border: `1px solid ${T.border}` },
        danger: { background: "#fff1f1", color: "#b91c1c", border: "1px solid #fecaca" },
        success: { background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" },
        ghost: { background: "transparent", color: T.muted, border: "1px solid transparent" },
        warning: { background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" },
        outline: { background: "transparent", color: T.accent, border: `1px solid ${T.accent}` },
        info: { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" },
        purple: { background: "#faf5ff", color: "#6d28d9", border: "1px solid #e9d5ff" },
    };
    const S = { sm: "5px 13px", md: "8px 18px", lg: "11px 24px" };
    const FS = { sm: 12, md: 13, lg: 14 };
    const v = V[variant] || V.primary;
    return <button {...p} disabled={loading || p.disabled} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: S[size], borderRadius: 8, fontSize: FS[size], fontWeight: 600, cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", opacity: (loading || p.disabled) ? 0.5 : 1, transition: "all 0.16s ease", letterSpacing: "0.01em", lineHeight: 1.4, ...v, ...p.style }}>
        {icon && <span style={{ fontSize: size === "sm" ? 12 : 14, lineHeight: 1 }}>{icon}</span>}
        {loading ? "Please wait…" : children}
    </button>;
};

const Input = ({ label, error, prefix, suffix, textarea, rows = 3, hint, required, ...p }) => (
    <div style={{ marginBottom: 16 }}>
        {label && <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6, letterSpacing: "0.02em", fontFamily: "'DM Sans', sans-serif" }}>
            {label}{required && <span style={{ color: T.red, marginLeft: 2 }}>*</span>}
        </label>}
        <div style={{ position: "relative" }}>
            {prefix && <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: T.muted, pointerEvents: "none" }}>{prefix}</span>}
            {textarea
                ? <textarea rows={rows} {...p} style={{ width: "100%", padding: "10px 13px", background: T.faint, border: `1px solid ${error ? "#dc2626" : T.border}`, borderRadius: 8, fontSize: 13, color: T.text, outline: "none", resize: "vertical", fontFamily: "'DM Sans', -apple-system, sans-serif", transition: "border-color .15s, box-shadow .15s", ...p.style }} onFocus={e => { e.target.style.borderColor = T.accent; e.target.style.boxShadow = `0 0 0 3px ${T.accentMid}`; }} onBlur={e => { e.target.style.borderColor = error ? "#dc2626" : T.border; e.target.style.boxShadow = "none"; }} />
                : <input {...p} style={{ width: "100%", padding: `10px ${suffix ? "38px" : "13px"} 10px ${prefix ? "36px" : "13px"}`, background: T.faint, border: `1px solid ${error ? "#dc2626" : T.border}`, borderRadius: 8, fontSize: 13, color: T.text, outline: "none", fontFamily: "'DM Sans', -apple-system, sans-serif", transition: "border-color .15s, box-shadow .15s", ...p.style }} onFocus={e => { e.target.style.borderColor = T.accent; e.target.style.boxShadow = `0 0 0 3px ${T.accentMid}`; }} onBlur={e => { e.target.style.borderColor = error ? "#dc2626" : T.border; e.target.style.boxShadow = "none"; }} />
            }
            {suffix && <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: T.muted, pointerEvents: "none" }}>{suffix}</span>}
        </div>
        {hint && !error && <div style={{ fontSize: 11, color: T.muted, marginTop: 5, lineHeight: 1.5 }}>{hint}</div>}
        {error && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 5, fontWeight: 500 }}>{error}</div>}
    </div>
);

const Select = ({ label, children, required, ...p }) => (
    <div style={{ marginBottom: 16 }}>
        {label && <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6, letterSpacing: "0.02em", fontFamily: "'DM Sans', sans-serif" }}>
            {label}{required && <span style={{ color: T.red, marginLeft: 2 }}>*</span>}
        </label>}
        <select {...p} style={{ width: "100%", padding: "10px 13px", background: T.faint, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, color: T.text, outline: "none", fontFamily: "'DM Sans', -apple-system, sans-serif", cursor: "pointer", transition: "border-color .15s, box-shadow .15s", ...p.style }} onFocus={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.boxShadow = `0 0 0 3px ${T.accentMid}`; }} onBlur={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>{children}</select>
    </div>
);

const Alert = ({ type = "error", children, onClose }) => {
    const m = {
        error: { bg: "rgba(220,38,38,0.06)", border: "rgba(220,38,38,0.20)", color: "#b91c1c", icon: "⚠" },
        success: { bg: "rgba(22,163,74,0.06)", border: "rgba(22,163,74,0.20)", color: "#15803d", icon: "✓" },
        info: { bg: "rgba(29,78,216,0.06)", border: "rgba(29,78,216,0.20)", color: "#1d4ed8", icon: "ℹ" },
        warning: { bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.25)", color: "#b45309", icon: "◆" },
    };
    const c = m[type] || m.info;
    return <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: c.color, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 10, fontWeight: 800 }}>{c.icon}</span><span>{children}</span></div>
        {onClose && <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: c.color, fontSize: 16, lineHeight: 1, padding: 0, opacity: .6 }}>×</button>}
    </div>;
};

// AI Rating Badge — shown on every bid card in admin view
const AIRatingBadge = ({ bid, compact = false }) => {
    if (!bid.ai_rating) return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, color: T.muted, background: T.faint, padding: "3px 9px", borderRadius: 20, border: `1px solid ${T.border}`, fontWeight: 600 }}>
            <span style={{ animation: "pulse 2s infinite", fontSize: 8 }}>●</span> AI Rating Pending
        </div>
    );

    const rating = bid.ai_rating;
    const analysis = (() => { try { return bid.ai_analysis ? JSON.parse(bid.ai_analysis) : {}; } catch { return {}; } })();
    const color = T.text;
    const bg = T.faint;
    const border = T.border;
    const label = rating >= 8 ? "Excellent" : rating >= 6 ? "Good" : rating >= 4 ? "Average" : "Poor";

    if (compact) return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: bg, border: `1px solid ${border}`, borderRadius: 20, padding: "3px 10px" }}>
            <span style={{ fontSize: 10, fontWeight: 800, color, fontFamily: "'DM Sans', sans-serif" }}>{rating}/10</span>
            <span style={{ fontSize: 9, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
        </div>
    );

    return (
        <div style={{ marginTop: 12, background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: analysis.summary ? 12 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 42, fontWeight: 800, color, fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>{rating}</div>
                        <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>/10</div>
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: "0.04em" }}>AI Score — {label}</div>
                        {analysis.recommendation && <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 600 }}>{analysis.recommendation} · Risk: {analysis.risk || "—"}</div>}
                    </div>
                </div>
                {analysis.price_score != null && (
                    <div style={{ display: "flex", gap: 14 }}>
                        {[["Price", analysis.price_score, 3], ["Experience", analysis.experience_score, 3], ["Proposal", analysis.proposal_score, 2], ["Complete", analysis.completeness_score, 2]].map(([l, s, max]) => s != null && (
                            <div key={l} style={{ textAlign: "center" }}>
                                <div style={{ fontSize: 15, fontWeight: 800, color, fontFamily: "'DM Sans', sans-serif" }}>{s}<span style={{ fontSize: 10, color: T.muted }}>/{max}</span></div>
                                <div style={{ fontSize: 9, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 1 }}>{l}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {analysis.summary && <div style={{ fontSize: 13, color: T.text, lineHeight: 1.65, borderTop: `1px solid ${border}`, paddingTop: 10, marginTop: 4 }}>{analysis.summary}</div>}
            {analysis.flags && analysis.flags !== "None" && <div style={{ fontSize: 12, color: T.text, marginTop: 8, fontWeight: 700 }}>Flags: {analysis.flags}</div>}
        </div>
    );
};

const Divider = ({ label }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
        <div style={{ flex: 1, height: 1, background: T.border }} />
        {label && <><span style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap" }}>{label}</span><div style={{ flex: 1, height: 1, background: T.border }} /></>}
    </div>
);

// SVG icon helpers for stat cards
const StatIcon = ({ type, color }) => {
    const s = { width: 20, height: 20, stroke: color, strokeWidth: 1.7, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
    if (type === "tenders") return <svg viewBox="0 0 24 24" {...s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" /></svg>;
    if (type === "clock") return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>;
    if (type === "bids") return <svg viewBox="0 0 24 24" {...s}><rect x="2" y="7" width="20" height="15" rx="2" /><path d="M16 7V5a2 2 0 0 0-4 0v2M8 11h8M8 15h5" /></svg>;
    if (type === "save") return <svg viewBox="0 0 24 24" {...s}><path d="m19 21-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>;
    if (type === "value") return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9" /><path d="M9.5 15a2.5 2.5 0 0 0 2.5 1.5c1.4 0 2.5-.8 2.5-2s-1.1-1.7-2.5-2-2.5-.8-2.5-2 1.1-2 2.5-2a2.5 2.5 0 0 1 2.5 1.5M12 6.5v11" /></svg>;
    if (type === "bidders") return <svg viewBox="0 0 24 24" {...s}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    return null;
};

const Stat = ({ label, value, sub, color = T.accent, icon, iconType, onClick }) => (
    <div
        className="card-hover"
        onClick={onClick}
        style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "18px 20px", position: "relative", overflow: "hidden", boxShadow: T.shadow, cursor: onClick ? "pointer" : "default", transition: "box-shadow .18s, border-color .18s" }}
        onMouseEnter={onClick ? e => { e.currentTarget.style.borderColor = color + "60"; e.currentTarget.style.boxShadow = `0 4px 20px ${color}15`; } : undefined}
        onMouseLeave={onClick ? e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = T.shadow; } : undefined}
    >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            {(iconType || icon) && <div style={{ width: 38, height: 38, borderRadius: "50%", background: T.faint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {iconType ? <StatIcon type={iconType} color="#4b5563" /> : <span style={{ fontSize: 16 }}>{icon}</span>}
            </div>}
            <div style={{ fontSize: 13, fontWeight: 500, color: T.text, fontFamily: "'DM Sans', sans-serif" }}>{label}</div>
        </div>
        <div style={{ fontSize: 30, fontWeight: 700, color: T.text, letterSpacing: "-1px", fontFamily: "'DM Sans', sans-serif", lineHeight: 1, marginBottom: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 11.5, color: T.muted, fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>{sub}</div>}
    </div>
);

const Modal = ({ title, onClose, children, maxWidth = 680, subtitle }) => (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,17,35,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backdropFilter: "blur(6px)" }} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={{ background: "#ffffff", border: `1px solid ${T.borderHi}`, borderRadius: 20, width: "100%", maxWidth, maxHeight: "93vh", overflow: "auto", boxShadow: "0 32px 96px rgba(10,17,35,0.28)", animation: "fadeUp .2s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 28px", borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, background: "#ffffff", zIndex: 1 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.2px" }}>{title}</h2>
                    {subtitle && <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>{subtitle}</div>}
                </div>
                <button onClick={onClose} style={{ background: T.faint, border: `1px solid ${T.border}`, fontSize: 18, cursor: "pointer", color: T.muted, lineHeight: 1, width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s", flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.background = "#f0f2f5"; e.currentTarget.style.color = T.text; }} onMouseLeave={e => { e.currentTarget.style.background = T.faint; e.currentTarget.style.color = T.muted; }}>×</button>
            </div>
            <div style={{ padding: 28 }}>{children}</div>
        </div>
    </div>
);

// Full-page bid form — same layout style as Browse Tenders (no modal/card shell)
const BidPage = ({ title, onClose, children, subtitle }) => (
    <div style={{ animation: "fadeUp .3s ease", width: "100%" }}>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.accent, fontWeight: 700, fontSize: 13, padding: 0, marginBottom: 14, fontFamily: "'DM Sans', sans-serif" }}>← Back to Tenders</button>
        <div style={{ marginBottom: 22 }}>
            <h1 style={{ margin: "0 0 4px", fontSize: 26, fontWeight: 700, color: T.text, letterSpacing: "-0.5px", fontFamily: "'DM Sans', sans-serif" }}>{title}</h1>
            {subtitle && <div style={{ fontSize: 13, color: T.muted }}>{subtitle}</div>}
        </div>
        {children}
    </div>
);

// Collapsible section used inside forms
const FormSection = ({ title, icon, children, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={{ marginBottom: 20, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
            <button onClick={() => setOpen(!open)} style={{ width: "100%", padding: "12px 16px", background: T.accentLo, border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.accent, textTransform: "uppercase", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 8 }}>
                    {icon && <span>{icon}</span>}{title}
                </div>
                <span style={{ fontSize: 11, color: T.accent, transition: "transform .2s", display: "inline-block", transform: open ? "rotate(180deg)" : "none" }}>▼</span>
            </button>
            {open && <div style={{ padding: "16px 16px 4px", background: T.card }}>{children}</div>}
        </div>
    );
};

// Info grid cell used in tender detail view
const InfoCell = ({ label, value, mono, color, span }) => (
    <div style={{ gridColumn: span ? "1 / -1" : "auto" }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: color || T.text, fontFamily: mono ? "'DM Sans', sans-serif" : "inherit", lineHeight: 1.5 }}>{value || "—"}</div>
    </div>
);

// ─── TENDER DETAIL VIEW (reused by Admin + Bidder) ───────────────────────────

const TenderDetailView = ({ t, bidCount = 0 }) => {
    const dd = daysDiff(t.deadline);
    const docs = (t.documents_required || "").split("\n").map(s => s.trim()).filter(Boolean);
    const criteria = (t.eligibility_criteria || "").split("\n").map(s => s.trim()).filter(Boolean);

    return (
        <div style={{ animation: "fadeUp .25s ease" }}>
            {/* Status & badges */}
            <div style={{ display: "flex", gap: 7, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                <Badge status={t.status} />
                <CatBadge cat={t.category} />
                {t.tender_type && <span style={{ fontSize: 10, color: T.muted, background: T.faint, padding: "3px 9px", borderRadius: 20, border: `1px solid ${T.border}`, fontWeight: 600 }}>{t.tender_type}</span>}
                {t.reference_no && <span style={{ fontSize: 10, color: T.accent, background: T.accentLo, padding: "3px 9px", borderRadius: 20, border: `1px solid ${T.border}`, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>Ref: {t.reference_no}</span>}
            </div>

            {/* Description */}
            {t.description && (
                <div style={{ marginBottom: 18, padding: "14px 16px", background: T.faint, borderRadius: 10, border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Tender Description / Scope of Work</div>
                    <p style={{ margin: 0, fontSize: 13, color: T.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{t.description}</p>
                </div>
            )}

            {/* Primary Details Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, background: T.faint, borderRadius: 12, padding: 18, marginBottom: 16, border: `1px solid ${T.border}` }}>
                <InfoCell label="Department / Organization" value={t.dept} />
                <InfoCell label="Location / Site" value={t.location} />
                <InfoCell label="Tender Type" value={t.tender_type} />
                <InfoCell label="Estimated Value" value={fmtFull(t.value)} mono color={T.accent} />
                <InfoCell label="EMD Amount" value={t.emd_amount ? fmtFull(t.emd_amount) : "—"} mono />
                <InfoCell label="Tender Document Cost" value={t.bid_document_cost ? fmtFull(t.bid_document_cost) : "—"} mono />
                <InfoCell label="Contract Type" value={t.contract_type} />
                <InfoCell label="Contract Duration" value={t.contract_duration} />
                <InfoCell label="Bids Received" value={String(bidCount)} mono />
            </div>

            {/* Key Dates */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Key Dates</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                    {[
                        ["Published", fmtDate(t.created_at), T.muted],
                        ["Pre-Bid Meeting", t.pre_bid_date ? fmtDate(t.pre_bid_date) : "N/A", T.muted],
                        ["Bid Submission Deadline", fmtDate(t.deadline), dd < 0 ? T.muted : dd <= 7 ? T.red : T.green],
                        ["Bid Opening Date", t.bid_opening_date ? fmtDate(t.bid_opening_date) : "—", T.muted],
                    ].map(([l, v, c]) => (
                        <div key={l} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px" }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{l}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: c }}>{v}</div>
                            {l.includes("Deadline") && dd >= 0 && <div style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>{dd} day{dd !== 1 ? "s" : ""} remaining</div>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Bid Constraints */}
            {(t.min_bid_value || t.max_bid_value || t.emd_amount || t.performance_guarantee) && (
                <div style={{ marginBottom: 16 }}>
                    <Alert type="info">
                        <strong>Financial Constraints:</strong>{" "}
                        {t.min_bid_value && `Min Bid: ${fmtFull(t.min_bid_value)}`}
                        {t.min_bid_value && t.max_bid_value && " · "}
                        {t.max_bid_value && `Max Bid: ${fmtFull(t.max_bid_value)}`}
                        {t.emd_amount && ` · EMD: ${fmtFull(t.emd_amount)}`}
                        {t.performance_guarantee && ` · Performance Guarantee: ${t.performance_guarantee}%`}
                    </Alert>
                </div>
            )}

            {/* Eligibility Criteria */}
            {criteria.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Eligibility Criteria</div>
                    <div style={{ background: "rgba(217,119,6,0.05)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 10, padding: "14px 16px" }}>
                        {criteria.map((c, i) => (
                            <div key={i} style={{ display: "flex", gap: 10, marginBottom: i < criteria.length - 1 ? 10 : 0, fontSize: 13, color: T.text, lineHeight: 1.6 }}>
                                <span style={{ color: T.gold, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>◆</span>
                                <span>{c}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Technical Specifications */}
            {t.technical_specs && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Technical Specifications</div>
                    <div style={{ background: T.faint, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px", fontSize: 13, color: T.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{t.technical_specs}</div>
                </div>
            )}

            {/* Required Documents */}
            {docs.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Required Documents</div>
                    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        {docs.map((doc, i) => (
                            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: T.text }}>
                                <span style={{ color: T.green, fontWeight: 800, flexShrink: 0, marginTop: 1, fontSize: 10 }}>✓</span>
                                {doc}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Payment Terms */}
            {t.payment_terms && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Payment Terms</div>
                    <div style={{ background: T.faint, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px", fontSize: 13, color: T.text, lineHeight: 1.7 }}>{t.payment_terms}</div>
                </div>
            )}

            {/* Contact Info */}
            {(t.contact_person || t.contact_email || t.contact_phone) && (
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Contact Information</div>
                    <div style={{ background: "rgba(29,78,216,0.05)", border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px", display: "flex", gap: 24, flexWrap: "wrap" }}>
                        {t.contact_person && (
                            <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                                <span style={{ fontSize: 18 }}>👤</span>
                                <div><div style={{ fontSize: 9, color: T.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em" }}>Contact Officer</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{t.contact_person}</div>{t.contact_designation && <div style={{ fontSize: 11, color: T.muted }}>{t.contact_designation}</div>}</div>
                            </div>
                        )}
                        {t.contact_email && (
                            <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                                <span style={{ fontSize: 18 }}>✉️</span>
                                <div><div style={{ fontSize: 9, color: T.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em" }}>Email</div><a href={`mailto:${t.contact_email}`} style={{ fontSize: 13, fontWeight: 700, color: T.accent, textDecoration: "none" }}>{t.contact_email}</a></div>
                            </div>
                        )}
                        {t.contact_phone && (
                            <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                                <span style={{ fontSize: 18 }}>📞</span>
                                <div><div style={{ fontSize: 9, color: T.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em" }}>Phone</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{t.contact_phone}</div></div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────────────────────────────────────

const ToastContext = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const add = useCallback((msg, type = "success") => {
        const id = Date.now();
        setToasts(t => [...t, { id, msg, type }]);
        setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
    }, []);
    const colors = { success: T.green, error: T.red, info: T.accent, warning: T.gold };
    return (
        <>
            {typeof children === "function" ? children(add) : children}
            <div style={{ position: "fixed", bottom: 28, right: 28, display: "flex", flexDirection: "column", gap: 10, zIndex: 9999 }}>
                {toasts.map(t => (
                    <div key={t.id} style={{ background: "#ffffff", border: `1px solid ${T.border}`, color: T.text, borderRadius: 12, padding: "12px 18px", fontSize: 12, fontWeight: 600, boxShadow: "0 8px 32px rgba(15,23,42,0.15)", animation: "slideIn .25s ease", display: "flex", alignItems: "center", gap: 10, minWidth: 260, maxWidth: 360 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: colors[t.type] || T.muted, flexShrink: 0 }} />
                        {t.msg}
                    </div>
                ))}
            </div>
        </>
    );
};

const useConfirm = () => {
    const [state, setState] = useState(null);
    const confirm = (msg, title = "Are you sure?") => new Promise(resolve => setState({ msg, title, resolve }));
    const Dialog = state ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
            <div style={{ background: "#ffffff", border: `1px solid ${T.borderHi}`, borderRadius: 16, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 24px 80px rgba(15,23,42,0.2)", animation: "fadeUp .2s" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 8 }}>{state.title}</div>
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 28, lineHeight: 1.6 }}>{state.msg}</div>
                <div style={{ display: "flex", gap: 10 }}>
                    <Btn variant="secondary" style={{ flex: 1 }} onClick={() => { state.resolve(false); setState(null); }}>Cancel</Btn>
                    <Btn variant="danger" style={{ flex: 1 }} onClick={() => { state.resolve(true); setState(null); }}>Confirm</Btn>
                </div>
            </div>
        </div>
    ) : null;
    return { confirm, Dialog };
};

// ─────────────────────────────────────────────────────────────────────────────
// SEARCH / FILTER
// ─────────────────────────────────────────────────────────────────────────────

// Monochrome line icons for the four standard bidder nav items (falls back to emoji for anything else)
const NAV_ICON_SVGS = {
    browse: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.4-3.4" /></svg>,
    saved: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m19 21-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>,
    mybids: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
    profile: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    dashboard: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></svg>,
    tenders: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" /></svg>,
    bids: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="15" rx="2" /><path d="M16 7V5a2 2 0 0 0-4 0v2M8 11h8M8 15h5" /></svg>,
    bidders: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
};

const SearchBar = ({ value, onChange, placeholder }) => (
    <div style={{ position: "relative", flex: 1 }}>
        <svg style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || "Search…"} style={{ width: "100%", padding: "9px 36px 9px 38px", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: 10, fontSize: 13, color: T.text, outline: "none", fontFamily: "'DM Sans', -apple-system, sans-serif", transition: "border-color .15s", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = "#cbd5e1"} />
        {value && <button onClick={() => onChange("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "#e2e8f0", border: "none", borderRadius: "50%", width: 18, height: 18, cursor: "pointer", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}>×</button>}
    </div>
);

// Monochrome line icons for category pills (visually consistent with the rest of the icon set)
const CAT_ICON_SVGS = {
    "Infrastructure": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h20" /><path d="M4 20V10l6-4 6 4v10" /><path d="M14 20v-6h4v6" /><path d="M10 20v-4" /></svg>,
    "Construction": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 22h20" /><path d="M6 22V11l6-6 6 6v11" /><path d="M10 22v-6h4v6" /></svg>,
    "Technology": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>,
    "Energy": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>,
    "Healthcare": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Z" /><path d="M12 8v8M8 12h8" /></svg>,
    "Education": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>,
    "Defence": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5z" /></svg>,
    "Agriculture": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 20A7 7 0 0 1 4 13c0-4 3-7 8-11 5 4 8 7 8 11a7 7 0 0 1-7 7" /><path d="M11 20v-9" /></svg>,
    "Transport": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="11" rx="2" /><path d="M3 12h18M7 17v2M17 17v2" /><circle cx="7.5" cy="17" r="0.6" fill="currentColor" /><circle cx="16.5" cy="17" r="0.6" fill="currentColor" /></svg>,
    "Water & Sanitation": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2s6 7 6 12a6 6 0 0 1-12 0c0-5 6-12 6-12Z" /></svg>,
    "Smart City": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V10l5-3v14" /><path d="M13 21V6l4-3v18" /><path d="M8 21h13" /><path d="M6 14h1M6 17h1M15 10h1M15 13h1M15 16h1" /></svg>,
    "Other": <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
};

const CatPills = ({ selected, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);
    const current = CATEGORIES.find(c => c.id === selected);
    return (
        <div ref={ref} style={{ position: "relative", display: "inline-block", marginBottom: 14 }}>
            <button onClick={() => setOpen(o => !o)}
                style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 220, padding: "9px 14px", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 9, fontSize: 13, fontWeight: 500, color: T.text, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <span style={{ display: "flex", alignItems: "center", opacity: current ? 1 : 0.55, flexShrink: 0 }}>{current ? CAT_ICON_SVGS[current.id] : CAT_ICON_SVGS["Other"]}</span>
                <span style={{ flex: 1, textAlign: "left" }}>{current ? current.id : "All Categories"}</span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flexShrink: 0 }}><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {open && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: 240, maxHeight: 320, overflowY: "auto", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowMd, zIndex: 30, padding: 6 }}>
                    <button onClick={() => { onChange("all"); setOpen(false); }}
                        style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "8px 10px", borderRadius: 7, border: "none", background: selected === "all" ? T.faint : "transparent", color: T.text, fontSize: 13, fontWeight: selected === "all" ? 700 : 500, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left" }}
                        onMouseEnter={e => { if (selected !== "all") e.currentTarget.style.background = T.faint; }}
                        onMouseLeave={e => { if (selected !== "all") e.currentTarget.style.background = "transparent"; }}>
                        All Categories
                    </button>
                    {CATEGORIES.map(c => {
                        const sel = selected === c.id;
                        return (
                            <button key={c.id} onClick={() => { onChange(c.id); setOpen(false); }}
                                style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "8px 10px", borderRadius: 7, border: "none", background: sel ? T.faint : "transparent", color: T.text, fontSize: 13, fontWeight: sel ? 700 : 500, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", textAlign: "left" }}
                                onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.faint; }}
                                onMouseLeave={e => { if (!sel) e.currentTarget.style.background = "transparent"; }}>
                                <span style={{ display: "flex", alignItems: "center", color: T.text, opacity: 0.75, flexShrink: 0 }}>{CAT_ICON_SVGS[c.id]}</span>
                                {c.id}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const DEFAULT_FILTERS = { valueRange: 0, deadline: "all", status: "all", department: "all" };

const FilterPanel = ({ filters, setFilters, count }) => {
    const [open, setOpen] = useState(false);
    const active = [filters.valueRange !== 0, filters.deadline !== "all", filters.status !== "all", filters.department !== "all"].filter(Boolean).length;
    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 14px", border: `1px solid ${open ? T.accent : T.border}`, borderRadius: 8, background: open ? T.accentLo : "transparent", cursor: "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", fontSize: 11, fontWeight: 700, color: open ? T.accent : T.muted, textTransform: "uppercase", letterSpacing: "0.08em", transition: "all .15s" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                    Filters {active > 0 && <span style={{ background: T.accent, color: "#fff", borderRadius: 10, fontSize: 9, padding: "1px 6px", fontWeight: 800 }}>{active}</span>}
                </button>
                <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>{count} result{count !== 1 ? "s" : ""}</span>
                {active > 0 && <button onClick={() => setFilters(DEFAULT_FILTERS)} style={{ fontSize: 10, color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>✕ Clear</button>}
            </div>
            {open && (
                <div style={{ marginTop: 10, background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 20, animation: "fadeUp .2s ease" }}>
                    {[
                        ["Value Range", VALUE_RANGES.map((r, i) => ({ v: i, l: r.label })), "valueRange"],
                        ["Deadline", [["all", "Any Time"], ["7", "Next 7 Days"], ["30", "Next 30 Days"], ["90", "Next 3 Months"], ["expired", "Expired"]].map(([v, l]) => ({ v, l })), "deadline"],
                        ["Status", [["all", "All Status"], ["active", "Active"], ["draft", "Draft"], ["closed", "Closed"]].map(([v, l]) => ({ v, l })), "status"],
                    ].map(([title, opts, key]) => (
                        <div key={key}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: T.muted, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.12em" }}>{title}</div>
                            {opts.map(({ v, l }) => (
                                <label key={v} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, cursor: "pointer", fontSize: 12, color: filters[key] === v ? T.text : T.muted, fontWeight: filters[key] === v ? 600 : 400, transition: "color .15s" }}>
                                    <span style={{ width: 14, height: 14, borderRadius: "50%", border: `1.5px solid ${filters[key] === v ? T.accent : T.border}`, background: filters[key] === v ? T.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .15s" }}>
                                        {filters[key] === v && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />}
                                    </span>
                                    <input type="radio" checked={filters[key] === v} onChange={() => setFilters(f => ({ ...f, [key]: v }))} style={{ display: "none" }} />
                                    {l}
                                </label>
                            ))}
                        </div>
                    ))}
                    <div>
                        <div style={{ fontSize: 9, fontWeight: 800, color: T.muted, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.12em" }}>Department</div>
                        <select value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))} style={{ width: "100%", padding: "7px 10px", background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 12, fontFamily: "'DM Sans', -apple-system, sans-serif", color: T.text, outline: "none" }}>
                            <option value="all">All Departments</option>
                            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// AUTH PAGES
// ─────────────────────────────────────────────────────────────────────────────

const AuthField = ({ label, type = "text", placeholder, value, onChange, onKeyDown }) => {
    const [show, setShow] = useState(false);
    const isPw = type === "password";
    return (
        <div style={{ marginBottom: 16 }}>
            {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.muted, marginBottom: 7, letterSpacing: "0.02em", fontFamily: "'DM Sans', sans-serif" }}>{label}</label>}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                    type={isPw && show ? "text" : type}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    style={{ width: "100%", padding: isPw ? "11px 42px 11px 14px" : "11px 14px", background: T.faint, border: `1px solid ${T.border}`, borderRadius: 9, fontSize: 14, color: T.text, outline: "none", fontFamily: "'DM Sans', -apple-system, sans-serif", transition: "border-color .15s, box-shadow .15s" }}
                    onFocus={e => { e.target.style.borderColor = T.accent; e.target.style.boxShadow = `0 0 0 3px ${T.accentMid}`; e.target.style.background = "#fff"; }}
                    onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = "none"; e.target.style.background = T.faint; }}
                />
                {isPw && <span onClick={() => setShow(s => !s)} style={{ position: "absolute", right: 13, fontSize: 12, color: T.accent, fontWeight: 600, cursor: "pointer", userSelect: "none", letterSpacing: "0.02em" }}>{show ? "Hide" : "Show"}</span>}
            </div>
        </div>
    );
};

const AuthShell = ({ accent, heading, blurb, points, children }) => (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "'DM Sans', -apple-system, sans-serif", background: T.bg }}>
        <GlobalStyle />
        <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "22px 36px" }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.dark, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 3px 10px rgba(15,23,42,0.35)" }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px", fontFamily: "'DM Sans', sans-serif" }}>N</span>
            </div>
            <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: "-0.2px", fontFamily: "'DM Sans', sans-serif" }}>NeuroGig</div>
                <div style={{ fontSize: 11, fontWeight: 500, color: T.muted, letterSpacing: "0.02em" }}>Procurement Platform</div>
            </div>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 80, padding: "20px 36px 60px", flexWrap: "wrap" }}>
            <div style={{ maxWidth: 440 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.faint, border: `1px solid ${T.border}`, borderRadius: 20, padding: "5px 14px", marginBottom: 20 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.dark, display: "inline-block", boxShadow: "0 0 0 3px rgba(15,23,42,0.12)" }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.text, letterSpacing: "0.04em" }}>NeuroGig · Secure Portal</span>
                </div>
                <h1 style={{ fontSize: 34, fontWeight: 800, color: T.text, lineHeight: 1.2, letterSpacing: "-0.8px", marginBottom: 16, fontFamily: "'DM Sans', sans-serif" }}>{heading}</h1>
                <p style={{ fontSize: 15.5, color: T.muted, lineHeight: 1.65, marginBottom: 28 }}>{blurb}</p>
                {points && points.map(pt => (
                    <div key={pt} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: T.text, marginBottom: 12 }}>
                        <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: T.darkLo, flexShrink: 0, marginTop: 1 }}>
                            <span style={{ fontSize: 10, color: T.dark, fontWeight: 800 }}>✓</span>
                        </span>
                        <span style={{ lineHeight: 1.5 }}>{pt}</span>
                    </div>
                ))}
            </div>
            <div style={{ width: 400, flexShrink: 0, background: "#ffffff", borderRadius: 16, border: `1px solid ${T.border}`, padding: "32px 30px", boxShadow: T.shadowMd }}>
                {children}
            </div>
        </div>
        <div style={{ textAlign: "center", padding: "18px", fontSize: 12, color: T.muted, borderTop: `1px solid ${T.border}` }}>🔒 256-bit encrypted connection · NeuroGig Procurement Platform</div>
    </div>
);

export function AdminLogin({ onLogin, goToBidder }) {
    const [tab, setTab] = useState("login");
    const [f, setF] = useState({ email: "", password: "", full_name: "" });
    const [remember, setRemember] = useState(false);
    const [err, setErr] = useState(""); const [msg, setMsg] = useState(""); const [loading, setLoading] = useState(false);
    // ── Forgot-password state ──
    const [forgotMode, setForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotNewPw, setForgotNewPw] = useState("");
    const [forgotFoundId, setForgotFoundId] = useState(null);
    const [forgotLoading, setForgotLoading] = useState(false);

    const login = async () => {
        if (!f.email || !f.password) { setErr("Email and password required."); return; }
        if (!isValidEmail(f.email)) { setErr("Enter a valid email address (e.g. name@company.com)."); return; }
        setLoading(true); setErr("");
        const { data, error } = await supabase.from("profiles").select("id,email,full_name,role").eq("email", f.email).eq("role", "admin").eq("password_hash", f.password).maybeSingle();
        setLoading(false);
        if (error || !data) { setErr("Invalid admin credentials."); return; }
        onLogin({ id: data.id, email: data.email, name: data.full_name || data.email, role: "admin", dept: "Procurement Cell", avatar: data.full_name || data.email }, remember);
    };
    const register = async () => {
        if (!f.email || !f.password || !f.full_name) { setErr("All fields required."); return; }
        if (!isValidEmail(f.email)) { setErr("Enter a valid email address (e.g. name@company.com)."); return; }
        setLoading(true); setErr("");
        const { error } = await supabase.from("profiles").insert({ email: f.email, password_hash: f.password, full_name: f.full_name, role: "admin" });
        setLoading(false);
        if (error) { setErr(error.message.includes("unique") ? "Email already registered." : error.message); return; }
        setMsg("Account created. Sign in now."); setTab("login"); setF(p => ({ ...p, password: "" }));
    };
    const lookupAccount = async () => {
        if (!forgotEmail) { setErr("Enter your registered email."); return; }
        if (!isValidEmail(forgotEmail)) { setErr("Enter a valid email address (e.g. name@company.com)."); return; }
        setForgotLoading(true); setErr("");
        const { data, error } = await supabase.from("profiles").select("id").eq("email", forgotEmail).eq("role", "admin").maybeSingle();
        setForgotLoading(false);
        if (error || !data) { setErr("No admin account found with that email."); return; }
        setForgotFoundId(data.id);
    };
    const resetPassword = async () => {
        if (!forgotNewPw || forgotNewPw.length < 6) { setErr("Password must be at least 6 characters."); return; }
        setForgotLoading(true); setErr("");
        const { error } = await supabase.from("profiles").update({ password_hash: forgotNewPw }).eq("id", forgotFoundId);
        setForgotLoading(false);
        if (error) { setErr(error.message); return; }
        setMsg("Password reset successfully! You can now sign in.");
        setForgotMode(false); setForgotEmail(""); setForgotNewPw(""); setForgotFoundId(null);
    };
    const openForgot = () => { setForgotMode(true); setErr(""); setForgotEmail(""); setForgotFoundId(null); setForgotNewPw(""); };
    const closeForgot = () => { setForgotMode(false); setErr(""); setForgotEmail(""); setForgotFoundId(null); setForgotNewPw(""); };

    return (
        <AuthShell
            accent="#0f172a"
            heading="Manage tenders and bids from one console."
            blurb="Sign in with your administrator account to publish tenders, review submissions and track procurement activity."
            points={["End-to-end tender lifecycle management", "Real-time bid tracking and comparison", "Role-based, audited access"]}
        >
            {forgotMode ? (
                <div style={{ animation: "fadeUp .2s ease" }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 6 }}>Reset Password</div>
                    <div style={{ fontSize: 13, color: T.muted, marginBottom: 22, lineHeight: 1.65 }}>Enter your registered admin email address. We’ll verify the account and let you set a new password.</div>
                    <AuthField label="Registered email" type="email" placeholder="admin@company.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} onKeyDown={e => !forgotFoundId && e.key === "Enter" && lookupAccount()} />
                    {forgotFoundId && (
                        <>
                            <Alert type="success">Account verified ✔ Set your new password below.</Alert>
                            <AuthField label="New password" type="password" placeholder="Min 6 characters" value={forgotNewPw} onChange={e => setForgotNewPw(e.target.value)} onKeyDown={e => e.key === "Enter" && resetPassword()} />
                        </>
                    )}
                    {err && <Alert type="error" onClose={() => setErr("")}>{err}</Alert>}
                    {!forgotFoundId
                        ? <button onClick={lookupAccount} disabled={forgotLoading} style={{ width: "100%", padding: "12px", fontSize: 15, fontWeight: 700, borderRadius: 9, background: "#0f172a", color: "#fff", border: "none", cursor: forgotLoading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", opacity: forgotLoading ? 0.7 : 1, marginBottom: 10 }}>{forgotLoading ? "Looking up…" : "Look Up Account"}</button>
                        : <button onClick={resetPassword} disabled={forgotLoading} style={{ width: "100%", padding: "12px", fontSize: 15, fontWeight: 700, borderRadius: 9, background: "#0f172a", color: "#fff", border: "none", cursor: forgotLoading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", opacity: forgotLoading ? 0.7 : 1, marginBottom: 10 }}>{forgotLoading ? "Resetting…" : "Set New Password"}</button>
                    }
                    <button onClick={closeForgot} style={{ width: "100%", padding: "11px", fontSize: 14, fontWeight: 600, borderRadius: 9, background: "#fff", color: T.muted, border: `1px solid ${T.borderHi}`, cursor: "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>← Back to Sign In</button>
                </div>
            ) : (
                <>
                    <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, marginBottom: 22 }}>
                        {[["login", "Sign In"], ["register", "Register"]].map(([k, l]) => (
                            <button key={k} onClick={() => { setTab(k); setErr(""); setMsg(""); }} style={{ flex: 1, padding: "10px 0", background: "none", border: "none", borderBottom: tab === k ? `2px solid #0f172a` : "2px solid transparent", cursor: "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", fontSize: 14, fontWeight: 600, color: tab === k ? "#0f172a" : T.muted, marginBottom: -1 }}>{l}</button>
                        ))}
                    </div>
                    {tab === "login" ? <>
                        <AuthField label="Official email" type="email" placeholder="admin@company.com" value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} onKeyDown={e => e.key === "Enter" && login()} />
                        <AuthField label="Password" type="password" placeholder="Enter your password" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} onKeyDown={e => e.key === "Enter" && login()} />
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: T.muted, cursor: "pointer" }}>
                                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ width: 15, height: 15, accentColor: "#0f172a" }} /> Remember me
                            </label>
                            <span onClick={openForgot} style={{ fontSize: 13, color: T.accent, fontWeight: 600, cursor: "pointer" }}>Forgot password?</span>
                        </div>
                        {err && <Alert type="error">{err}</Alert>}
                        {msg && <Alert type="success">{msg}</Alert>}
                        <button onClick={login} disabled={loading} style={{ width: "100%", padding: "12px", fontSize: 15, fontWeight: 700, borderRadius: 9, background: "#0f172a", color: "#fff", border: "none", cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", transition: "opacity .15s", opacity: loading ? 0.7 : 1 }}>{loading ? "Signing in…" : "Sign in"}</button>
                    </> : <>
                        <AuthField label="Full name" placeholder="John Doe" value={f.full_name} onChange={e => setF(p => ({ ...p, full_name: e.target.value }))} />
                        <AuthField label="Email" type="email" placeholder="admin@company.com" value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} />
                        <AuthField label="Password" type="password" placeholder="Min 8 characters" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} />
                        {err && <Alert type="error">{err}</Alert>}
                        <button onClick={register} disabled={loading} style={{ width: "100%", padding: "12px", fontSize: 15, fontWeight: 700, borderRadius: 9, background: "#0f172a", color: "#fff", border: "none", cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", opacity: loading ? 0.7 : 1 }}>{loading ? "Creating…" : "Create admin account"}</button>
                    </>}
                    <div style={{ textAlign: "center", margin: "18px 0", fontSize: 13, color: T.borderHi, display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ flex: 1, height: 1, background: T.border }} /> or <span style={{ flex: 1, height: 1, background: T.border }} />
                    </div>
                    <button onClick={goToBidder} style={{ width: "100%", padding: "11px", fontSize: 14, fontWeight: 600, borderRadius: 9, background: "#fff", color: T.text, border: `1px solid ${T.borderHi}`, cursor: "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>Continue as bidder</button>
                </>
            )}
        </AuthShell>
    );
}

export function BidderLogin({ onLogin, goToAdmin }) {
    const [tab, setTab] = useState("login");
    const [f, setF] = useState({ email: "", password: "", full_name: "", company_name: "", reg_no: "" });
    const [remember, setRemember] = useState(false);
    const [err, setErr] = useState(""); const [msg, setMsg] = useState(""); const [loading, setLoading] = useState(false);
    // ── Forgot-password state ──
    const [forgotMode, setForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotNewPw, setForgotNewPw] = useState("");
    const [forgotFoundId, setForgotFoundId] = useState(null);
    const [forgotLoading, setForgotLoading] = useState(false);

    const login = async () => {
        if (!f.email || !f.password) { setErr("Email and password required."); return; }
        if (!isValidEmail(f.email)) { setErr("Enter a valid email address (e.g. name@company.com)."); return; }
        setLoading(true); setErr("");
        const { data, error } = await supabase.from("profiles").select("id,email,full_name,company_name,reg_no,role").eq("email", f.email).eq("role", "bidder").eq("password_hash", f.password).maybeSingle();
        setLoading(false);
        if (error || !data) { setErr("Invalid credentials or account not found."); return; }
        onLogin({ id: data.id, email: data.email, name: data.full_name || data.email, role: "bidder", company: data.company_name || "—", regNo: data.reg_no || "—", avatar: data.full_name || data.email }, remember);
    };
    const register = async () => {
        if (!f.email || !f.password || !f.full_name || !f.company_name) { setErr("Name, email, password and company required."); return; }
        if (!isValidEmail(f.email)) { setErr("Enter a valid email address (e.g. name@company.com)."); return; }
        setLoading(true); setErr("");
        const { error } = await supabase.from("profiles").insert({ email: f.email, password_hash: f.password, full_name: f.full_name, company_name: f.company_name, reg_no: f.reg_no, role: "bidder" });
        setLoading(false);
        if (error) { setErr(error.message.includes("unique") ? "Email already registered." : error.message); return; }
        setMsg("Account created. Sign in now."); setTab("login"); setF(p => ({ ...p, password: "" }));
    };
    const lookupAccount = async () => {
        if (!forgotEmail) { setErr("Enter your registered email."); return; }
        if (!isValidEmail(forgotEmail)) { setErr("Enter a valid email address (e.g. name@company.com)."); return; }
        setForgotLoading(true); setErr("");
        const { data, error } = await supabase.from("profiles").select("id").eq("email", forgotEmail).eq("role", "bidder").maybeSingle();
        setForgotLoading(false);
        if (error || !data) { setErr("No bidder account found with that email."); return; }
        setForgotFoundId(data.id);
    };
    const resetPassword = async () => {
        if (!forgotNewPw || forgotNewPw.length < 6) { setErr("Password must be at least 6 characters."); return; }
        setForgotLoading(true); setErr("");
        const { error } = await supabase.from("profiles").update({ password_hash: forgotNewPw }).eq("id", forgotFoundId);
        setForgotLoading(false);
        if (error) { setErr(error.message); return; }
        setMsg("Password reset successfully! You can now sign in.");
        setForgotMode(false); setForgotEmail(""); setForgotNewPw(""); setForgotFoundId(null);
    };
    const openForgot = () => { setForgotMode(true); setErr(""); setForgotEmail(""); setForgotFoundId(null); setForgotNewPw(""); };
    const closeForgot = () => { setForgotMode(false); setErr(""); setForgotEmail(""); setForgotFoundId(null); setForgotNewPw(""); };

    return (
        <AuthShell
            accent="#0f172a"
            heading="Empowering procurement with transparency."
            blurb="A secure, efficient platform to discover tenders, submit competitive bids and grow your business."
            points={["Access thousands of live tender opportunities", "Real-time notifications on bid status", "Fair, transparent and fully auditable"]}
        >
            {forgotMode ? (
                <div style={{ animation: "fadeUp .2s ease" }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 6 }}>Reset Password</div>
                    <div style={{ fontSize: 13, color: T.muted, marginBottom: 22, lineHeight: 1.65 }}>Enter your registered email address. We’ll verify the account and let you set a new password.</div>
                    <AuthField label="Registered email" type="email" placeholder="company@email.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} onKeyDown={e => !forgotFoundId && e.key === "Enter" && lookupAccount()} />
                    {forgotFoundId && (
                        <>
                            <Alert type="success">Account verified ✔ Set your new password below.</Alert>
                            <AuthField label="New password" type="password" placeholder="Min 6 characters" value={forgotNewPw} onChange={e => setForgotNewPw(e.target.value)} onKeyDown={e => e.key === "Enter" && resetPassword()} />
                        </>
                    )}
                    {err && <Alert type="error" onClose={() => setErr("")}>{err}</Alert>}
                    {!forgotFoundId
                        ? <button onClick={lookupAccount} disabled={forgotLoading} style={{ width: "100%", padding: "12px", fontSize: 15, fontWeight: 700, borderRadius: 9, background: "#0f172a", color: "#fff", border: "none", cursor: forgotLoading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", opacity: forgotLoading ? 0.7 : 1, marginBottom: 10 }}>{forgotLoading ? "Looking up…" : "Look Up Account"}</button>
                        : <button onClick={resetPassword} disabled={forgotLoading} style={{ width: "100%", padding: "12px", fontSize: 15, fontWeight: 700, borderRadius: 9, background: "#0f172a", color: "#fff", border: "none", cursor: forgotLoading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", opacity: forgotLoading ? 0.7 : 1, marginBottom: 10 }}>{forgotLoading ? "Resetting…" : "Set New Password"}</button>
                    }
                    <button onClick={closeForgot} style={{ width: "100%", padding: "11px", fontSize: 14, fontWeight: 600, borderRadius: 9, background: "#fff", color: T.muted, border: `1px solid ${T.borderHi}`, cursor: "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>← Back to Sign In</button>
                </div>
            ) : (
                <>
                    <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, marginBottom: 22 }}>
                        {[["login", "Sign In"], ["register", "Register"]].map(([k, l]) => (
                            <button key={k} onClick={() => { setTab(k); setErr(""); setMsg(""); }} style={{ flex: 1, padding: "10px 0", background: "none", border: "none", borderBottom: tab === k ? `2px solid #0f172a` : "2px solid transparent", cursor: "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", fontSize: 14, fontWeight: 600, color: tab === k ? "#0f172a" : T.muted, marginBottom: -1 }}>{l}</button>
                        ))}
                    </div>
                    {tab === "login" ? <>
                        <AuthField label="Email address" type="email" placeholder="you@company.com" value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} onKeyDown={e => e.key === "Enter" && login()} />
                        <AuthField label="Password" type="password" placeholder="Enter your password" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} onKeyDown={e => e.key === "Enter" && login()} />
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: T.muted, cursor: "pointer" }}>
                                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ width: 15, height: 15, accentColor: "#0f172a" }} /> Remember me
                            </label>
                            <span onClick={openForgot} style={{ fontSize: 13, color: T.accent, fontWeight: 600, cursor: "pointer" }}>Forgot password?</span>
                        </div>
                        {err && <Alert type="error">{err}</Alert>}
                        {msg && <Alert type="success">{msg}</Alert>}
                        <button onClick={login} disabled={loading} style={{ width: "100%", padding: "12px", fontSize: 15, fontWeight: 700, borderRadius: 9, background: "#0f172a", color: "#fff", border: "none", cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", opacity: loading ? 0.7 : 1 }}>{loading ? "Signing in…" : "Sign in"}</button>
                    </> : <>
                        <AuthField label="Full name" placeholder="Rajesh Sharma" value={f.full_name} onChange={e => setF(p => ({ ...p, full_name: e.target.value }))} />
                        <AuthField label="Email" type="email" placeholder="company@email.com" value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} />
                        <AuthField label="Password" type="password" placeholder="Min 8 characters" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} />
                        <AuthField label="Company name" placeholder="Infra Build Ltd." value={f.company_name} onChange={e => setF(p => ({ ...p, company_name: e.target.value }))} />
                        <AuthField label="Registration no. (optional)" placeholder="CIN / GSTIN" value={f.reg_no} onChange={e => setF(p => ({ ...p, reg_no: e.target.value }))} />
                        {err && <Alert type="error">{err}</Alert>}
                        <button onClick={register} disabled={loading} style={{ width: "100%", padding: "12px", fontSize: 15, fontWeight: 700, borderRadius: 9, background: "#0f172a", color: "#fff", border: "none", cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif", opacity: loading ? 0.7 : 1 }}>{loading ? "Creating…" : "Create bidder account"}</button>
                    </>}
                    <div style={{ textAlign: "center", margin: "18px 0", fontSize: 13, color: T.borderHi, display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ flex: 1, height: 1, background: T.border }} /> or <span style={{ flex: 1, height: 1, background: T.border }} />
                    </div>
                    <button onClick={goToAdmin} style={{ width: "100%", padding: "11px", fontSize: 14, fontWeight: 600, borderRadius: 9, background: "#fff", color: T.text, border: `1px solid ${T.borderHi}`, cursor: "pointer", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>Continue as admin</button>
                </>
            )}
        </AuthShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// SIDENAV
// ─────────────────────────────────────────────────────────────────────────────

const SideNav = ({ items, active, setActive, user, onLogout, brand, badge = {} }) => (
    <div style={{ width: 244, background: "#ffffff", borderRadius: 18, minHeight: "calc(100vh - 40px)", display: "flex", flexDirection: "column", flexShrink: 0, border: `1px solid ${T.border}`, boxShadow: T.shadowMd, position: "sticky", top: 20, alignSelf: "flex-start" }}>
        {/* Logo */}
        <div style={{ padding: "22px 20px 16px", display: "flex", alignItems: "center", gap: 11 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.dark, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 3px 10px rgba(15,23,42,0.35)` }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px", fontFamily: "'DM Sans', sans-serif" }}>N</span>
            </div>
            <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: "-0.2px", fontFamily: "'DM Sans', sans-serif" }}>NeuroGig</div>
                <div style={{ fontSize: 11, fontWeight: 500, color: T.muted, letterSpacing: "0.02em" }}>{brand}</div>
            </div>
        </div>
        <div style={{ height: 1, background: T.border, margin: "0 16px 8px" }} />
        {/* Nav items */}
        <div style={{ flex: 1, padding: "4px 10px", overflowY: "auto" }}>
            {items.map(item => {
                const isActive = active === item.id;
                const svgIcon = NAV_ICON_SVGS[item.id];
                return (
                    <button key={item.id} onClick={() => setActive(item.id)} className={isActive ? "" : "nav-item-light"} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "10px 12px", borderRadius: 10, border: "none", cursor: "pointer", marginBottom: 2, background: isActive ? T.dark : "transparent", color: isActive ? "#fff" : T.muted, fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, fontWeight: isActive ? 700 : 500, textAlign: "left", transition: "all .14s" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ display: "flex", alignItems: "center", opacity: isActive ? 1 : 0.6 }}>{svgIcon || <span style={{ fontSize: 15 }}>{item.icon}</span>}</span>
                            {item.label}
                        </span>
                        {badge[item.id] > 0 && <span style={{ background: isActive ? "#fff" : "#e11d48", color: isActive ? T.dark : "#fff", borderRadius: 12, fontSize: 10, padding: "1px 6px", fontWeight: 700, minWidth: 18, textAlign: "center", letterSpacing: "0.02em" }}>{badge[item.id]}</span>}
                    </button>
                );
            })}
        </div>
        {/* User profile */}
        <div style={{ padding: "12px 12px 18px", borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, padding: "9px 11px", borderRadius: 11, background: T.faint, border: `1px solid ${T.border}` }}>
                <Avatar name={user.name} size={34} />
                <div style={{ overflow: "hidden", flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'DM Sans', sans-serif" }}>{user.name}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{user.role === "admin" ? "Administrator" : user.company}</div>
                </div>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="6 9 12 15 18 9" /></svg>
            </div>
            <button onClick={onLogout} style={{ width: "100%", padding: "8px 12px", border: "none", borderRadius: 9, background: "transparent", color: T.muted, fontSize: 12.5, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, transition: "all .14s" }} onMouseEnter={e => { e.currentTarget.style.color = T.text; }} onMouseLeave={e => { e.currentTarget.style.color = T.muted; }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                Sign Out
            </button>
        </div>
    </div>
);

const PageHeader = ({ title, sub, actions }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 30 }}>
        <div>
            <h1 style={{ margin: "0 0 5px", fontSize: 26, fontWeight: 800, color: T.text, letterSpacing: "-0.2px", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>{title}</h1>
            {sub && <div style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.5 }}>{sub}</div>}
        </div>
        {actions && <div style={{ display: "flex", gap: 9 }}>{actions}</div>}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────

const AdminDashboard = ({ tenders, bids, bidders, setPage }) => {
    const [viewT, setViewT] = useState(null);
    const active = tenders.filter(t => t.status === "active");
    const closing = active.filter(t => daysDiff(t.deadline) <= 7 && daysDiff(t.deadline) >= 0);
    const awarded = bids.filter(b => b.status === "awarded");
    const totalVal = active.reduce((a, t) => a + t.value, 0);
    const recent = [...tenders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
    const catMap = CATEGORIES.map(c => ({ ...c, count: tenders.filter(t => t.category === c.id).length })).filter(c => c.count > 0);
    const maxCat = Math.max(1, ...catMap.map(c => c.count));
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
        return { label: d.toLocaleString("default", { month: "short" }), count: bids.filter(b => { const bd = new Date(b.submitted_at); return bd.getFullYear() === d.getFullYear() && bd.getMonth() === d.getMonth(); }).length };
    });
    const maxMonth = Math.max(1, ...months.map(m => m.count));

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                    {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </div>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: T.text, letterSpacing: "-0.5px" }}>Dashboard Overview</h1>
            </div>

            {closing.length > 0 && (
                <Alert type="warning">
                    <strong>{closing.length} tender{closing.length > 1 ? "s" : ""}</strong> closing within 7 days.{" "}
                    <button onClick={() => setPage("tenders")} style={{ background: "none", border: "none", color: "#b45309", fontWeight: 800, cursor: "pointer", textDecoration: "underline", fontSize: "inherit", fontFamily: "inherit" }}>Review →</button>
                </Alert>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
                <Stat label="Active Tenders" value={active.length} sub={`${tenders.length} total`} color={T.accent} iconType="tenders"
                    onClick={() => setPage("tenders")} />
                <Stat label="Total Bids" value={bids.length} sub={`${awarded.length} awarded`} color={T.green} iconType="bids"
                    onClick={() => setPage("bids")} />
                <Stat label="Pipeline Value" value={fmt(totalVal)} sub="active tenders" color={T.gold} iconType="value"
                    onClick={() => setPage("tenders")} />
                <Stat label="Registered Cos." value={bidders.length} sub="verified bidders" color="#a78bfa" iconType="bidders"
                    onClick={() => setPage("bidders")} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16, marginBottom: 16 }}>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
                    <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Recent Tenders</div>
                        <button onClick={() => setPage("tenders")} style={{ fontSize: 11, color: T.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 700, fontFamily: "'DM Sans', -apple-system, sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>View all →</button>
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ borderBottom: `1px solid ${T.border}` }}>
                            {["Title", "Category", "Value", "Bids", "Status"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 9, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {recent.map((t, i) => (
                                <tr key={t.id} className="row-hover" onClick={() => setViewT(t)} style={{ borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}>
                                    <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 600, color: T.text, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.title}>{t.title}</td>
                                    <td style={{ padding: "12px 16px" }}><CatBadge cat={t.category} /></td>
                                    <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(t.value)}</td>
                                    <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif" }}>{bids.filter(b => b.tender_id === t.id).length}</td>
                                    <td style={{ padding: "12px 16px" }}><Badge status={t.status} /></td>
                                </tr>
                            ))}
                            {!recent.length && <tr><td colSpan={5} style={{ textAlign: "center", padding: 40, color: T.muted, fontSize: 12 }}>No tenders yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 20 }}>By Category</div>
                    {catMap.length === 0 ? <div style={{ color: T.muted, fontSize: 12 }}>No data.</div> : catMap.map(c => (
                        <div key={c.id} style={{ marginBottom: 12 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, marginBottom: 5, color: T.muted }}>
                                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ display: "flex", alignItems: "center", color: T.text, opacity: 0.7 }}>{CAT_ICON_SVGS[c.id]}</span>
                                    {c.id}
                                </span>
                                <span style={{ fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif" }}>{c.count}</span>
                            </div>
                            <div style={{ height: 4, background: T.faint, borderRadius: 4 }}>
                                <div style={{ height: 4, background: T.dark, borderRadius: 4, width: `${(c.count / maxCat) * 100}%`, transition: "width .6s ease" }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 20 }}>Bid Activity — Last 6 Months</div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 90 }}>
                    {months.map(m => (
                        <div key={m.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{m.count || ""}</div>
                            <div style={{ width: "100%", background: T.accentLo, borderRadius: "4px 4px 0 0", height: `${m.count ? Math.max(6, (m.count / maxMonth) * 64) : 4}px`, transition: "height .6s ease", border: `1px solid rgba(59,130,246,0.2)`, borderBottom: "none" }} />
                            <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>{m.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Tender detail modal — opened by clicking a Recent Tenders row */}
            {viewT && (() => {
                const tBids = [...bids.filter(b => b.tender_id === viewT.id)].sort((a, b) => a.amount - b.amount);
                const lowest = tBids.find(b => b.status === "submitted");
                return (
                    <Modal
                        title={viewT.title}
                        subtitle={`${viewT.dept || ""}${viewT.reference_no ? " · Ref: " + viewT.reference_no : ""} · ${fmtDate(viewT.deadline)}`}
                        onClose={() => setViewT(null)}
                        maxWidth={820}
                    >
                        <TenderDetailView t={viewT} bidCount={tBids.filter(b => b.status !== "withdrawn").length} />

                        <Divider label={`Received Bids (${tBids.length})`} />
                        {!tBids.length ? (
                            <Empty icon="📭" title="No bids yet" sub="Bids appear here once bidders submit." />
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {tBids.map(bid => {
                                    const bidder = bidders.find(p => p.id === bid.bidder_id);
                                    const pct = viewT.value ? ((bid.amount / viewT.value) * 100).toFixed(1) : null;
                                    const isLowest = bid === lowest;
                                    return (
                                        <div key={bid.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: `1px solid ${isLowest ? T.green + "55" : T.border}`, borderRadius: 10, background: isLowest ? "rgba(21,128,61,0.05)" : T.card }}>
                                            <Avatar name={bidder?.full_name || "?"} size={32} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                                                    {bidder?.full_name || "—"}
                                                    {isLowest && <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 800, color: T.green, textTransform: "uppercase", letterSpacing: "0.05em" }}>Lowest</span>}
                                                </div>
                                                <div style={{ fontSize: 10, color: T.muted }}>{bidder?.company_name || "—"}</div>
                                            </div>
                                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{fmt(bid.amount)}</div>
                                                {pct && <div style={{ fontSize: 10, color: T.muted }}>{pct}% of estimate</div>}
                                            </div>
                                            <Badge status={bid.status} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Modal>
                );
            })()}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: TENDERS — EXPANDED FORM
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
    // Core
    title: "", dept: "", category: "Infrastructure", value: "", deadline: "", description: "",
    // Classification
    tender_type: "Open Tender (OT)", contract_type: "Item Rate Contract", reference_no: "", location: "",
    // Financial
    min_bid_value: "", max_bid_value: "", emd_amount: "", bid_document_cost: "", performance_guarantee: "",
    // Dates
    pre_bid_date: "", bid_opening_date: "",
    // Contract
    contract_duration: "", payment_terms: "",
    // Eligibility & Docs
    eligibility_criteria: "", documents_required: "", technical_specs: "",
    // Contact
    contact_person: "", contact_designation: "", contact_email: "", contact_phone: "",
};

const AdminTenders = ({ tenders, setTenders, bids, setBids, bidders, toast }) => {
    const { confirm, Dialog } = useConfirm();
    const [showForm, setShowForm] = useState(false);
    const [viewT, setViewT] = useState(null);
    const [editT, setEditT] = useState(null);
    const [search, setSearch] = useState("");
    const [cat, setCat] = useState("all");
    const [sort, setSort] = useState("newest");
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formErr, setFormErr] = useState("");
    const [viewMode, setViewMode] = useState("list");
    const [dupId, setDupId] = useState(null);

    const sf = (key, val) => setForm(p => ({ ...p, [key]: val }));

    const applyFilters = useCallback((list) => {
        if (search) list = list.filter(t => [t.title, t.dept, t.description, t.reference_no, t.location].join(" ").toLowerCase().includes(search.toLowerCase()));
        if (cat !== "all") list = list.filter(t => t.category === cat);
        if (filters.status !== "all") list = list.filter(t => t.status === filters.status);
        if (filters.department !== "all") list = list.filter(t => t.dept === filters.department);
        const vr = VALUE_RANGES[filters.valueRange];
        if (vr.min > 0 || vr.max !== Infinity) list = list.filter(t => t.value >= vr.min && t.value < vr.max);
        if (filters.deadline !== "all") {
            if (filters.deadline === "expired") list = list.filter(t => daysDiff(t.deadline) < 0);
            else list = list.filter(t => { const d = daysDiff(t.deadline); return d >= 0 && d <= parseInt(filters.deadline); });
        }
        return [...list].sort((a, b) => {
            if (sort === "newest") return new Date(b.created_at) - new Date(a.created_at);
            if (sort === "deadline") return new Date(a.deadline) - new Date(b.deadline);
            if (sort === "value_desc") return b.value - a.value;
            if (sort === "value_asc") return a.value - b.value;
            if (sort === "bids_desc") return bids.filter(x => x.tender_id === b.id).length - bids.filter(x => x.tender_id === a.id).length;
            if (sort === "title") return a.title.localeCompare(b.title);
            return 0;
        });
    }, [search, cat, filters, sort, bids]);

    const filtered = useMemo(() => applyFilters([...tenders]), [tenders, applyFilters]);
    const tb = id => bids.filter(b => b.tender_id === id);
    const getBidder = id => bidders.find(x => x.id === id);

    const openCreate = () => { setEditT(null); setForm(EMPTY_FORM); setFormErr(""); setShowForm(true); };
    const openEdit = t => {
        setEditT(t);
        setForm({
            title: t.title || "", dept: t.dept || "", category: t.category || "Infrastructure", value: String(t.value || ""), deadline: t.deadline || "", description: t.description || "",
            tender_type: t.tender_type || "Open Tender (OT)", contract_type: t.contract_type || "Item Rate Contract",
            reference_no: t.reference_no || "", location: t.location || "",
            min_bid_value: t.min_bid_value ? String(t.min_bid_value) : "", max_bid_value: t.max_bid_value ? String(t.max_bid_value) : "",
            emd_amount: t.emd_amount ? String(t.emd_amount) : "", bid_document_cost: t.bid_document_cost ? String(t.bid_document_cost) : "",
            performance_guarantee: t.performance_guarantee ? String(t.performance_guarantee) : "",
            pre_bid_date: t.pre_bid_date || "", bid_opening_date: t.bid_opening_date || "",
            contract_duration: t.contract_duration || "", payment_terms: t.payment_terms || "",
            eligibility_criteria: t.eligibility_criteria || "", documents_required: t.documents_required || "",
            technical_specs: t.technical_specs || "",
            contact_person: t.contact_person || "", contact_designation: t.contact_designation || "",
            contact_email: t.contact_email || "", contact_phone: t.contact_phone || "",
        });
        setFormErr(""); setShowForm(true);
    };

    const duplicate = async t => {
        setDupId(t.id);
        const { id, created_at, ...rest } = t;
        const { data, error } = await supabase.from("tenders").insert({ ...rest, title: `${t.title} (Copy)`, status: "draft" }).select().single();
        setDupId(null);
        if (error) { toast(error.message, "error"); return; }
        setTenders(prev => [data, ...prev]); toast("Tender duplicated as Draft.");
    };

    const buildPayload = () => ({
        title: form.title, dept: form.dept, category: form.category, value: parseInt(form.value) || 0,
        deadline: form.deadline, description: form.description,
        tender_type: form.tender_type || null, contract_type: form.contract_type || null,
        reference_no: form.reference_no || null, location: form.location || null,
        min_bid_value: form.min_bid_value ? parseInt(form.min_bid_value) : null,
        max_bid_value: form.max_bid_value ? parseInt(form.max_bid_value) : null,
        emd_amount: form.emd_amount ? parseInt(form.emd_amount) : null,
        bid_document_cost: form.bid_document_cost ? parseInt(form.bid_document_cost) : null,
        performance_guarantee: form.performance_guarantee ? parseInt(form.performance_guarantee) : null,
        pre_bid_date: form.pre_bid_date || null, bid_opening_date: form.bid_opening_date || null,
        contract_duration: form.contract_duration || null, payment_terms: form.payment_terms || null,
        eligibility_criteria: form.eligibility_criteria || null, documents_required: form.documents_required || null,
        technical_specs: form.technical_specs || null,
        contact_person: form.contact_person || null, contact_designation: form.contact_designation || null,
        contact_email: form.contact_email || null, contact_phone: form.contact_phone || null,
        status: editT ? editT.status : "draft",
    });

    const save = async () => {
        if (!form.title || !form.value || !form.deadline) { setFormErr("Title, estimated value and deadline are required."); return; }
        setSaving(true); setFormErr("");
        const payload = buildPayload();
        const res = editT
            ? await supabase.from("tenders").update(payload).eq("id", editT.id).select().single()
            : await supabase.from("tenders").insert(payload).select().single();
        setSaving(false);
        if (res.error) { setFormErr(res.error.message); return; }
        editT ? setTenders(prev => prev.map(t => t.id === editT.id ? res.data : t)) : setTenders(prev => [res.data, ...prev]);
        toast(editT ? "Tender updated." : "Tender created as Draft.");
        setShowForm(false); setEditT(null); setForm(EMPTY_FORM);
    };

    const updateStatus = async (id, status) => {
        const { error } = await supabase.from("tenders").update({ status }).eq("id", id);
        if (!error) { setTenders(prev => prev.map(t => t.id === id ? { ...t, status } : t)); toast(`Tender ${status}.`); }
    };

    const deleteTender = async id => {
        const ok = await confirm("This will permanently delete the tender and all its bids.", "Delete Tender?");
        if (!ok) return;
        const { error } = await supabase.from("tenders").delete().eq("id", id);
        if (!error) { setTenders(prev => prev.filter(t => t.id !== id)); setBids(prev => prev.filter(b => b.tender_id !== id)); toast("Tender deleted."); }
    };

    const awardBid = async (tender, bidId) => {
        const ok = await confirm("This will award the bid, reject all others, and close the tender.", "Award This Bid?");
        if (!ok) return;
        const others = bids.filter(b => b.tender_id === tender.id && b.id !== bidId).map(b => b.id);
        await supabase.from("bids").update({ status: "awarded" }).eq("id", bidId);
        if (others.length) await supabase.from("bids").update({ status: "rejected" }).in("id", others);
        await supabase.from("tenders").update({ status: "closed" }).eq("id", tender.id);
        setTenders(prev => prev.map(t => t.id === tender.id ? { ...t, status: "closed" } : t));
        setBids(prev => prev.map(b => b.tender_id === tender.id ? { ...b, status: b.id === bidId ? "awarded" : "rejected" } : b));
        setViewT(null); toast("🎉 Bid awarded and tender closed!");
    };

    const rejectBid = async bidId => {
        const ok = await confirm("Reject this specific bid?", "Reject Bid?");
        if (!ok) return;
        await supabase.from("bids").update({ status: "rejected" }).eq("id", bidId);
        setBids(prev => prev.map(b => b.id === bidId ? { ...b, status: "rejected" } : b));
        toast("Bid rejected.");
    };

    const exportCSV = () => {
        const headers = ["Title", "Reference", "Dept", "Category", "Value", "EMD", "Deadline", "Status", "Bids"];
        const rows = tenders.map(t => [t.title, t.reference_no || "", t.dept, t.category, t.value, t.emd_amount || "", t.deadline, t.status, bids.filter(b => b.tender_id === t.id).length]);
        const csv = [headers, ...rows].map(r => r.map(v => `"${v || ""}"`).join(",")).join("\n");
        const a = document.createElement("a"); a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv); a.download = "tenders.csv"; a.click();
        toast("Tenders exported as CSV.");
    };

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            {Dialog}
            <PageHeader
                title="Tender Management"
                sub={`${tenders.length} total · ${tenders.filter(t => t.status === "active").length} active · ${tenders.filter(t => t.status === "draft").length} draft`}
                actions={<>
                    <Btn size="sm" variant="secondary" icon="⬇" onClick={exportCSV}>CSV</Btn>
                    <Btn icon="+" onClick={openCreate}>Create Tender</Btn>
                </>}
            />

            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search by title, reference no, department, location, description…" />
                <select value={sort} onChange={e => setSort(e.target.value)} style={{ padding: "9px 12px", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: 10, fontSize: 12, outline: "none", color: T.text, fontFamily: "'DM Sans', -apple-system, sans-serif", cursor: "pointer" }}>
                    {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div style={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                    {[["list", "☰"], ["grid", "⊞"]].map(([m, ic]) => <button key={m} onClick={() => setViewMode(m)} style={{ padding: "9px 14px", border: "none", background: viewMode === m ? T.accentLo : T.card, color: viewMode === m ? T.accent : T.muted, cursor: "pointer", fontSize: 14, transition: "all .15s" }}>{ic}</button>)}
                </div>
            </div>

            <CatPills selected={cat} onChange={setCat} />
            <FilterPanel filters={filters} setFilters={setFilters} count={filtered.length} />

            {viewMode === "list" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {filtered.map(t => {
                        const tBids = tb(t.id); const dd = daysDiff(t.deadline);
                        return (
                            <div key={t.id} className="card-hover" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
                                <div style={{ padding: "16px 20px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                                                <Badge status={t.status} />
                                                <CatBadge cat={t.category || "Other"} />
                                                {t.tender_type && <span style={{ fontSize: 10, color: T.muted, background: T.faint, padding: "2px 8px", borderRadius: 20, border: `1px solid ${T.border}`, fontWeight: 600 }}>{t.tender_type}</span>}
                                                {t.status === "active" && dd <= 7 && dd >= 0 && <span style={{ fontSize: 10, fontWeight: 800, background: "rgba(239,68,68,0.12)", color: "#f87171", padding: "3px 9px", borderRadius: 20, animation: "pulse 2s infinite", border: "1px solid rgba(239,68,68,0.2)", textTransform: "uppercase" }}>⚠ {dd}d left</span>}
                                                {t.emd_amount && <span style={{ fontSize: 10, color: T.muted, background: T.faint, padding: "2px 8px", borderRadius: 20, border: `1px solid ${T.border}` }}>EMD: {fmt(t.emd_amount)}</span>}
                                            </div>
                                            <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: T.text }}>{hl(t.title, search)}</h3>
                                            <div style={{ fontSize: 11, color: T.muted, marginBottom: 2 }}>
                                                {t.reference_no && <span style={{ fontFamily: "'DM Sans', sans-serif", marginRight: 8, color: T.accent }}>#{t.reference_no}</span>}
                                                {hl(t.dept || "—", search)} {t.location && <span>· 📍{hl(t.location, search)}</span>} · Deadline: {fmtDate(t.deadline)}
                                            </div>
                                            {t.description && <div style={{ fontSize: 11, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "80%", marginTop: 2, opacity: .7 }}>{t.description}</div>}
                                        </div>
                                        <div style={{ textAlign: "right", marginLeft: 20, flexShrink: 0 }}>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(t.value)}</div>
                                            <div style={{ fontSize: 11, color: T.muted, marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>{tBids.length} bid{tBids.length !== 1 ? "s" : ""}</div>
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", gap: 6, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}`, flexWrap: "wrap" }}>
                                        <Btn size="sm" variant="secondary" onClick={() => setViewT(t)}>📋 View & Bids</Btn>
                                        <Btn size="sm" variant="ghost" style={{ color: T.accent }} onClick={() => openEdit(t)}>✏ Edit</Btn>
                                        <Btn size="sm" variant="ghost" style={{ color: "#a78bfa" }} onClick={() => duplicate(t)} loading={dupId === t.id}>⧉ Dup</Btn>
                                        {t.status === "draft" && <Btn size="sm" variant="success" onClick={() => updateStatus(t.id, "active")}>▶ Publish</Btn>}
                                        {t.status === "active" && <Btn size="sm" variant="warning" onClick={() => updateStatus(t.id, "closed")}>⏹ Close</Btn>}
                                        {t.status === "closed" && <Btn size="sm" variant="outline" onClick={() => updateStatus(t.id, "active")}>↺ Re-open</Btn>}
                                        <Btn size="sm" variant="ghost" style={{ color: T.red }} onClick={() => deleteTender(t.id)}>🗑</Btn>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                    {filtered.map(t => {
                        const tBids = tb(t.id);
                        return (
                            <div key={t.id} className="card-hover" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
                                <div style={{ padding: "16px 18px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}><CatBadge cat={t.category} /><Badge status={t.status} /></div>
                                    {t.reference_no && <div style={{ fontSize: 10, color: T.accent, fontFamily: "'DM Sans', sans-serif", marginBottom: 4 }}>#{t.reference_no}</div>}
                                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 5, lineHeight: 1.4 }}>{t.title}</div>
                                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 2 }}>{t.dept || "—"}</div>
                                    {t.location && <div style={{ fontSize: 10, color: T.muted }}>📍 {t.location}</div>}
                                    <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, marginTop: 12, borderTop: `1px solid ${T.border}` }}>
                                        <div><div style={{ fontSize: 16, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(t.value)}</div><div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>Est. Value</div></div>
                                        <div style={{ textAlign: "right" }}><div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: "'DM Sans', sans-serif" }}>{tBids.length}</div><div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>Bids</div></div>
                                    </div>
                                    <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                                        <Btn size="sm" variant="secondary" style={{ flex: 1 }} onClick={() => setViewT(t)}>View</Btn>
                                        <Btn size="sm" variant="ghost" style={{ color: T.accent }} onClick={() => openEdit(t)}>Edit</Btn>
                                        {t.status === "draft" && <Btn size="sm" variant="success" onClick={() => updateStatus(t.id, "active")}>Publish</Btn>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {!filtered.length && <Empty icon="🔍" title="No tenders found" sub="Try adjusting your search or filters." cta={<Btn variant="secondary" onClick={() => { setSearch(""); setCat("all"); setFilters(DEFAULT_FILTERS); }}>Reset Filters</Btn>} />}

            {/* ── CREATE / EDIT MODAL ───────────────────────────────────────────── */}
            {showForm && (
                <Modal title={editT ? "Edit Tender" : "Create New Tender"} subtitle={editT ? `Editing: ${editT.title}` : "Complete the form below. Fields marked * are mandatory."} onClose={() => { setShowForm(false); setEditT(null); }} maxWidth={800}>
                    <FormSection title="Basic Information" icon="📋">
                        <div style={{ gridColumn: "1/-1" }}>
                            <Input label="Tender Title" required placeholder="e.g. Construction of NH-48 Bridge Over Krishna River" value={form.title} onChange={e => sf("title", e.target.value)} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                            <Select label="Department / Organization" required value={form.dept} onChange={e => sf("dept", e.target.value)}>
                                <option value="">Select department…</option>
                                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                            </Select>
                            <Select label="Category" required value={form.category} onChange={e => sf("category", e.target.value)}>
                                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.id}</option>)}
                            </Select>
                            <Select label="Tender Type" value={form.tender_type} onChange={e => sf("tender_type", e.target.value)}>
                                {TENDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </Select>
                            <Select label="Contract Type" value={form.contract_type} onChange={e => sf("contract_type", e.target.value)}>
                                {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </Select>
                            <Input label="Reference / NIT Number" placeholder="e.g. NHAI/KAR/2026/0451" value={form.reference_no} onChange={e => sf("reference_no", e.target.value)} hint="Unique tender reference number" />
                            <Input label="Location / Site" placeholder="e.g. Chitradurga District, Karnataka" value={form.location} onChange={e => sf("location", e.target.value)} />
                        </div>
                        <Input label="Scope of Work / Tender Description" required textarea rows={4} placeholder="Describe the complete scope of work, project background, and objectives…" value={form.description} onChange={e => sf("description", e.target.value)} />
                    </FormSection>

                    <FormSection title="Financial Details" icon="💰">
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
                            <Input label="Estimated Cost (₹)" required type="number" placeholder="e.g. 50000000" value={form.value} onChange={e => sf("value", e.target.value)} hint={form.value ? `= ${fmtFull(parseInt(form.value) || 0)}` : ""} />
                            <Input label="EMD Amount (₹)" type="number" placeholder="Earnest Money Deposit" value={form.emd_amount} onChange={e => sf("emd_amount", e.target.value)} hint="Usually 2-3% of estimated cost" />
                            <Input label="Tender Document Cost (₹)" type="number" placeholder="e.g. 5000" value={form.bid_document_cost} onChange={e => sf("bid_document_cost", e.target.value)} />
                            <Input label="Minimum Bid Amount (₹)" type="number" placeholder="Optional floor" value={form.min_bid_value} onChange={e => sf("min_bid_value", e.target.value)} />
                            <Input label="Maximum Bid Amount (₹)" type="number" placeholder="Optional ceiling" value={form.max_bid_value} onChange={e => sf("max_bid_value", e.target.value)} />
                            <Input label="Performance Guarantee (%)" type="number" placeholder="e.g. 5" value={form.performance_guarantee} onChange={e => sf("performance_guarantee", e.target.value)} suffix="%" hint="% of contract value" />
                        </div>
                        <Input label="Payment Terms" textarea rows={2} placeholder="e.g. 30% on mobilization, 60% on RA bills, 10% on completion…" value={form.payment_terms} onChange={e => sf("payment_terms", e.target.value)} />
                    </FormSection>

                    <FormSection title="Important Dates" icon="📅">
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                            <Input label="Pre-Bid Meeting Date" type="date" value={form.pre_bid_date} onChange={e => sf("pre_bid_date", e.target.value)} hint="Leave blank if no pre-bid meeting" />
                            <Input label="Bid Submission Deadline" required type="date" value={form.deadline} onChange={e => sf("deadline", e.target.value)} />
                            <Input label="Bid Opening Date" type="date" value={form.bid_opening_date} onChange={e => sf("bid_opening_date", e.target.value)} hint="When bids will be publicly opened" />
                            <Input label="Contract Duration" placeholder="e.g. 18 months from award date" value={form.contract_duration} onChange={e => sf("contract_duration", e.target.value)} />
                        </div>
                    </FormSection>

                    <FormSection title="Eligibility Criteria" icon="✅" defaultOpen={false}>
                        <Input label="Eligibility Criteria (one per line)" textarea rows={5}
                            placeholder={"Minimum annual turnover of ₹10 Cr in last 3 years\nClass A contractor registration with PWD\nMinimum 5 years experience in similar work\nISO 9001 certification mandatory\nNo blacklisting in last 5 years"}
                            value={form.eligibility_criteria} onChange={e => sf("eligibility_criteria", e.target.value)}
                            hint="Enter each criterion on a separate line"
                        />
                        <Input label="Technical Specifications" textarea rows={4}
                            placeholder="Detailed technical specifications, material standards, quality requirements, applicable codes/standards…"
                            value={form.technical_specs} onChange={e => sf("technical_specs", e.target.value)}
                        />
                    </FormSection>

                    <FormSection title="Required Documents" icon="📁" defaultOpen={false}>
                        <Input label="Documents Required (one per line)" textarea rows={6}
                            placeholder={"Valid Class A PWD Contractor Registration Certificate\nGST Registration Certificate\nPAN Card (Company)\nIncome Tax Returns for last 3 years\nBankers Solvency Certificate\nEMD Demand Draft / Bank Guarantee\nWork Experience Certificates for similar works\nList of Plant & Machinery\nKey Personnel Qualification Documents"}
                            value={form.documents_required} onChange={e => sf("documents_required", e.target.value)}
                            hint="Enter each required document on a separate line"
                        />
                    </FormSection>

                    <FormSection title="Contact Information" icon="📞" defaultOpen={false}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                            <Input label="Contact Person Name" placeholder="e.g. Mr. Ramesh Kumar" value={form.contact_person} onChange={e => sf("contact_person", e.target.value)} />
                            <Input label="Designation" placeholder="e.g. Executive Engineer" value={form.contact_designation} onChange={e => sf("contact_designation", e.target.value)} />
                            <Input label="Email Address" type="email" placeholder="procurement@company.com" value={form.contact_email} onChange={e => sf("contact_email", e.target.value)} />
                            <Input label="Phone Number" type="tel" placeholder="+91 80 2234 5678" value={form.contact_phone} onChange={e => sf("contact_phone", e.target.value)} />
                        </div>
                    </FormSection>

                    {formErr && <Alert type="error">{formErr}</Alert>}
                    <div style={{ display: "flex", gap: 10, paddingTop: 8 }}>
                        <Btn variant="secondary" onClick={() => { setShowForm(false); setEditT(null); }} style={{ flex: 1 }}>Cancel</Btn>
                        <Btn variant="primary" onClick={save} loading={saving} style={{ flex: 1 }}>{editT ? "Save Changes" : "Create Tender"}</Btn>
                    </div>
                </Modal>
            )}

            {/* ── VIEW & BIDS MODAL ─────────────────────────────────────────────── */}
            {viewT && (() => {
                const tBids = [...bids.filter(b => b.tender_id === viewT.id)].sort((a, b) => a.amount - b.amount);
                const lowestBid = tBids.find(b => b.status === "submitted");
                const printTender = () => window.print();
                return (
                    <Modal title={viewT.title} subtitle={`${viewT.dept || ""} · ${viewT.reference_no ? "Ref: " + viewT.reference_no + " ·" : ""} ${fmtDate(viewT.deadline)}`} onClose={() => setViewT(null)} maxWidth={860}>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }} className="no-print">
                            <Btn size="sm" variant="secondary" icon="🖨" onClick={printTender}>Print</Btn>
                        </div>
                        <TenderDetailView t={viewT} bidCount={tBids.filter(b => b.status !== "withdrawn").length} />

                        <Divider label={`Received Bids (${tBids.length})`} />
                        {!tBids.length ? <Empty icon="📭" title="No bids yet" sub="Bids will appear once bidders submit." /> : (
                            <>
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    {tBids.map((bid, i) => {
                                        const bidder = getBidder(bid.bidder_id);
                                        const pct = ((bid.amount / viewT.value) * 100).toFixed(1);
                                        const isLowest = bid === lowestBid;
                                        return (
                                            <div key={bid.id} style={{ border: `1px solid ${isLowest ? "rgba(34,197,94,0.3)" : T.border}`, borderRadius: 10, padding: "14px 16px", background: isLowest ? "rgba(34,197,94,0.05)" : T.faint }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                                                            {isLowest && <span style={{ fontSize: 9, fontWeight: 800, background: "rgba(34,197,94,0.2)", color: "#4ade80", padding: "2px 8px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.08em" }}>LOWEST BID</span>}
                                                            <Badge status={bid.status} />
                                                        </div>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{bidder?.full_name || "Unknown"}</div>
                                                        <div style={{ fontSize: 11, color: T.muted }}>{bidder?.company_name || "—"}{bidder?.reg_no ? ` · ${bidder.reg_no}` : ""}</div>
                                                        <div style={{ fontSize: 10, color: T.muted, marginTop: 3, fontFamily: "'DM Sans', sans-serif" }}>Submitted: {fmtTime(bid.submitted_at)}</div>
                                                        {bid.message && (() => {
                                                            const { experience, projects, proposal, emdRef } = parseBidMessage(bid.message);
                                                            return (
                                                                <div style={{ marginTop: 10 }}>
                                                                    {(experience || projects) && (
                                                                        <div style={{ marginBottom: 6, padding: "8px 10px", background: T.faint, border: `1px solid ${T.border}`, borderRadius: 9, fontSize: 11 }}>
                                                                            {experience && <div style={{ color: T.text }}><span style={{ color: T.muted, fontWeight: 600 }}>Experience: </span><strong>{experience} yrs</strong></div>}
                                                                            {projects && <div style={{ color: T.text, marginTop: experience ? 4 : 0, whiteSpace: "pre-wrap", lineHeight: 1.5 }}><span style={{ color: T.muted, fontWeight: 600 }}>Past projects: </span>{projects}</div>}
                                                                        </div>
                                                                    )}
                                                                    {emdRef && <div style={{ marginBottom: 6, padding: "6px 10px", background: "rgba(29,78,216,0.05)", border: "1px solid rgba(29,78,216,0.15)", borderRadius: 9, fontSize: 11 }}><span style={{ color: T.muted, fontWeight: 600 }}>EMD Ref: </span><span style={{ color: T.accent, fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}>{emdRef}</span></div>}
                                                                    {proposal && <div style={{ fontSize: 11, color: T.muted, padding: "6px 10px", background: T.card, borderRadius: 9, fontStyle: "italic", borderLeft: `2px solid ${T.border}` }}>"{proposal}"</div>}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                    <div style={{ textAlign: "right", marginLeft: 16 }}>
                                                        <div style={{ fontSize: 22, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(bid.amount)}</div>
                                                        <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>{fmtFull(bid.amount)}</div>
                                                        <div style={{ fontSize: 11, fontWeight: 700, color: bid.amount <= viewT.value ? T.green : T.red, marginTop: 3 }}>{pct}% of estimate</div>
                                                    </div>
                                                </div>
                                                <AIRatingBadge bid={bid} />
                                                {/* Documents submitted by this bidder */}
                                                {(() => {
                                                    const docs = (viewT.documents_required || "").split("\n").map(s => s.trim()).filter(Boolean); return docs.length > 0 ? (
                                                        <details style={{ marginTop: 10, cursor: "pointer" }}>
                                                            <summary style={{ fontSize: 11, fontWeight: 600, color: T.muted, listStyle: "none", display: "flex", alignItems: "center", gap: 6, padding: "6px 0", userSelect: "none" }}>
                                                                <span style={{ fontSize: 14 }}>📁</span> Required Documents ({docs.length}) <span style={{ fontSize: 10, color: T.muted }}>▸ click to expand</span>
                                                            </summary>
                                                            <div style={{ marginTop: 8, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                                                                {docs.map((doc, di) => (
                                                                    <div key={di} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: di < docs.length - 1 ? `1px solid ${T.border}` : "none", background: di % 2 === 0 ? "#ffffff" : T.faint }}>
                                                                        <div style={{ width: 22, height: 22, borderRadius: 9, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                            <span style={{ color: T.green, fontSize: 11, fontWeight: 800 }}>✓</span>
                                                                        </div>
                                                                        <span style={{ fontSize: 12, color: T.text, flex: 1 }}>{doc}</span>
                                                                        <span style={{ fontSize: 14 }}>📄</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </details>
                                                    ) : null;
                                                })()}
                                                {viewT.status === "active" && bid.status === "submitted" && (
                                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 8 }}>
                                                        <Btn size="sm" variant="success" onClick={() => awardBid(viewT, bid.id)}>🏆 Award</Btn>
                                                        <Btn size="sm" variant="danger" onClick={() => rejectBid(bid.id)}>✕ Reject</Btn>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {tBids.length > 1 && (() => {
                                    const amounts = tBids.filter(b => b.status !== "withdrawn").map(b => b.amount);
                                    if (amounts.length < 2) return null; // not enough non-withdrawn bids for meaningful stats
                                    const avg = amounts.reduce((a, v) => a + v, 0) / amounts.length;
                                    return (
                                        <div style={{ marginTop: 20, background: T.faint, borderRadius: 10, padding: 16, border: `1px solid ${T.border}` }}>
                                            <div style={{ fontSize: 9, fontWeight: 800, color: T.muted, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.1em" }}>Bid Statistics</div>
                                            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
                                                {[["Lowest", fmt(Math.min(...amounts))], ["Highest", fmt(Math.max(...amounts))], ["Average", fmt(avg)], ["Spread", fmt(Math.max(...amounts) - Math.min(...amounts))]].map(([l, v]) => (
                                                    <div key={l}><div style={{ fontSize: 9, color: T.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{l}</div><div style={{ fontSize: 14, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{v}</div></div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </>
                        )}
                    </Modal>
                );
            })()}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: ALL BIDS
// ─────────────────────────────────────────────────────────────────────────────

const AdminBids = ({ bids, tenders, bidders }) => {
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [cat, setCat] = useState("all");
    const [sort, setSort] = useState("newest");
    const [viewBid, setViewBid] = useState(null);

    const enriched = useMemo(() => {
        let list = bids.map(b => ({ ...b, tender: tenders.find(t => t.id === b.tender_id), bidder: bidders.find(x => x.id === b.bidder_id) })).filter(b => b.tender);
        if (search) list = list.filter(b => [b.tender?.title, b.bidder?.full_name, b.bidder?.company_name].join(" ").toLowerCase().includes(search.toLowerCase()));
        if (status !== "all") list = list.filter(b => b.status === status);
        if (cat !== "all") list = list.filter(b => b.tender?.category === cat);
        return [...list].sort((a, b2) => sort === "newest" ? new Date(b2.submitted_at) - new Date(a.submitted_at) : sort === "value_desc" ? b2.amount - a.amount : a.amount - b2.amount);
    }, [bids, tenders, bidders, search, status, cat, sort]);

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            <PageHeader title="All Bids" sub={`${bids.length} total · ${bids.filter(b => b.status === "awarded").length} awarded · ${bids.filter(b => b.status === "submitted").length} pending`} />
            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search by tender, bidder or company…" />
                {[{ val: status, set: setStatus, opts: [["all", "All Status"], ["submitted", "Submitted"], ["awarded", "Awarded"], ["rejected", "Rejected"], ["withdrawn", "Withdrawn"]] }, { val: sort, set: setSort, opts: [["newest", "Newest"], ["value_desc", "Highest"], ["value_asc", "Lowest"]] }].map((sel, i) => (
                    <select key={i} value={sel.val} onChange={e => sel.set(e.target.value)} style={{ padding: "9px 12px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 12, outline: "none", color: T.text, fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
                        {sel.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                ))}
            </div>
            <CatPills selected={cat} onChange={setCat} />
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ borderBottom: `1px solid ${T.border}` }}>
                        {["Tender", "Category", "Bidder / Company", "Bid Amount", "vs Estimate", "AI Rating", "Date", "Status"].map(h => <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 9, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap" }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {enriched.map((b, i) => {
                            const pct = b.tender?.value ? ((b.amount / b.tender.value) * 100).toFixed(1) : null;
                            return (
                                <tr key={b.id} className="row-hover" onClick={() => setViewBid(b)} style={{ borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}>
                                    <td style={{ padding: "12px 16px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{b.tender?.title}</div>
                                        {b.tender?.reference_no && <div style={{ fontSize: 10, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>#{b.tender.reference_no}</div>}
                                    </td>
                                    <td style={{ padding: "12px 16px" }}><CatBadge cat={b.tender?.category || "Other"} /></td>
                                    <td style={{ padding: "12px 16px" }}><div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{b.bidder?.full_name || "—"}</div><div style={{ fontSize: 10, color: T.muted }}>{b.bidder?.company_name || "—"}</div></td>
                                    <td style={{ padding: "12px 16px" }}><div style={{ fontSize: 13, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(b.amount)}</div><div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>{fmtFull(b.amount)}</div></td>
                                    <td style={{ padding: "12px 16px" }}>{pct && <span style={{ fontSize: 11, fontWeight: 800, color: b.amount <= b.tender?.value ? T.green : T.red, fontFamily: "'DM Sans', sans-serif" }}>{pct}%</span>}</td>
                                    <td style={{ padding: "12px 16px" }}><AIRatingBadge bid={b} compact /></td>
                                    <td style={{ padding: "12px 16px", fontSize: 11, color: T.muted, whiteSpace: "nowrap", fontFamily: "'DM Sans', sans-serif" }}>{fmtDate(b.submitted_at)}</td>
                                    <td style={{ padding: "12px 16px" }}><Badge status={b.status} /></td>
                                </tr>
                            );
                        })}
                        {!enriched.length && <tr><td colSpan={8} style={{ textAlign: "center", padding: 50, color: T.muted, fontSize: 12 }}>No bids match your filters.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Bid detail modal — opened by clicking a row */}
            {viewBid && (
                <Modal
                    title={viewBid.tender?.title}
                    subtitle={`${viewBid.tender?.dept || ""}${viewBid.tender?.reference_no ? " · Ref: " + viewBid.tender.reference_no : ""}${viewBid.tender?.deadline ? " · " + fmtDate(viewBid.tender.deadline) : ""}`}
                    onClose={() => setViewBid(null)}
                    maxWidth={800}
                >
                    <TenderDetailView t={viewBid.tender} bidCount={bids.filter(x => x.tender_id === viewBid.tender_id && x.status !== "withdrawn").length} />

                    <Divider label="Bid Details" />
                    <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 18px", background: T.faint }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                            <Avatar name={viewBid.bidder?.full_name || "?"} size={40} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{viewBid.bidder?.full_name || "—"}</div>
                                <div style={{ fontSize: 11, color: T.muted }}>{viewBid.bidder?.company_name || "—"}</div>
                                <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>Submitted {fmtDate(viewBid.submitted_at)}</div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                                <div style={{ fontSize: 18, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(viewBid.amount)}</div>
                                <div style={{ fontSize: 11, color: T.muted }}>{fmtFull(viewBid.amount)}</div>
                                {viewBid.tender?.value ? <div style={{ fontSize: 11, fontWeight: 700, color: viewBid.amount <= viewBid.tender.value ? T.green : T.red, marginTop: 2 }}>{((viewBid.amount / viewBid.tender.value) * 100).toFixed(1)}% of estimate</div> : null}
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                            <Badge status={viewBid.status} />
                        </div>
                        {viewBid.message && (() => {
                            const { experience, projects, proposal, emdRef } = parseBidMessage(viewBid.message);
                            return (
                                <div style={{ marginBottom: 4 }}>
                                    {(experience || projects) && (
                                        <div style={{ marginBottom: 8, padding: "10px 12px", background: T.faint, border: `1px solid ${T.border}`, borderRadius: 9, fontSize: 12 }}>
                                            {experience && <div style={{ color: T.text }}><span style={{ color: T.muted, fontWeight: 600 }}>Experience: </span><strong>{experience} yrs</strong></div>}
                                            {projects && <div style={{ color: T.text, marginTop: experience ? 5 : 0, whiteSpace: "pre-wrap", lineHeight: 1.6 }}><span style={{ color: T.muted, fontWeight: 600 }}>Past projects: </span>{projects}</div>}
                                        </div>
                                    )}
                                    {emdRef && <div style={{ marginBottom: 8, padding: "8px 12px", background: T.accentLo, border: `1px solid ${T.accentMid}`, borderRadius: 9, fontSize: 12 }}><span style={{ color: T.muted, fontWeight: 600 }}>EMD Ref: </span><span style={{ color: T.accent, fontWeight: 700 }}>{emdRef}</span></div>}
                                    {proposal && <div style={{ fontSize: 12, color: T.text, padding: "10px 12px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 9, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{proposal}</div>}
                                </div>
                            );
                        })()}
                        <AIRatingBadge bid={viewBid} />
                    </div>
                </Modal>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: BIDDERS
// ─────────────────────────────────────────────────────────────────────────────

const AdminBidders = ({ bidders, setBidders, bids, setBids, tenders, toast }) => {
    const { confirm, Dialog } = useConfirm();
    const [search, setSearch] = useState("");
    const [view, setView] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const filtered = bidders.filter(b => [b.full_name, b.company_name, b.email].join(" ").toLowerCase().includes(search.toLowerCase()));

    const deleteBidder = async id => {
        const ok = await confirm("This will permanently delete the bidder account and all their bids.", "Delete Bidder?");
        if (!ok) return;
        setDeleting(id);
        const { error: bidErr } = await supabase.from("bids").delete().eq("bidder_id", id);
        const { error: profErr } = await supabase.from("profiles").delete().eq("id", id);
        setDeleting(null);
        if (bidErr || profErr) { toast("Failed to delete bidder.", "error"); return; }
        // Update parent state in-place — no reload needed
        if (setBidders) setBidders(prev => prev.filter(x => x.id !== id));
        if (setBids) setBids(prev => prev.filter(b => b.bidder_id !== id));
        if (view?.id === id) setView(null); // close open modal if it was this bidder
        toast("Bidder account deleted.");
    };

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            {Dialog}
            <PageHeader title="Registered Bidders" sub={`${bidders.length} compan${bidders.length !== 1 ? "ies" : "y"} registered`} />
            <div style={{ marginBottom: 16 }}><SearchBar value={search} onChange={setSearch} placeholder="Search by name, company or email…" /></div>
            {!filtered.length ? <Empty icon="👥" title="No bidders found" sub="Bidders appear here after registering." /> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {filtered.map(b => {
                        const bidCount = bids.filter(bd => bd.bidder_id === b.id).length;
                        const awarded = bids.filter(bd => bd.bidder_id === b.id && bd.status === "awarded").length;
                        const totalVal = bids.filter(bd => bd.bidder_id === b.id).reduce((a, bd) => a + bd.amount, 0);
                        return (
                            <div key={b.id} className="card-hover" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "18px 22px", display: "flex", alignItems: "center", gap: 16 }}>
                                <Avatar name={b.full_name || b.email} size={44} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{hl(b.full_name || "—", search)}</div>
                                    <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{hl(b.company_name || "—", search)} · {hl(b.email, search)}</div>
                                    {b.reg_no && <div style={{ fontSize: 10, color: T.muted, marginTop: 1, fontFamily: "'DM Sans', sans-serif" }}>Reg: {b.reg_no}</div>}
                                </div>
                                <div style={{ display: "flex", gap: 20, textAlign: "center" }}>
                                    {[[`Bids`, bidCount, T.accent], [`Won`, awarded, T.green], totalVal > 0 && [`Value`, fmt(totalVal), T.text]].filter(Boolean).map(([l, v, c]) => (
                                        <div key={l}><div style={{ fontSize: 18, fontWeight: 800, color: c, fontFamily: "'DM Sans', sans-serif" }}>{v}</div><div style={{ fontSize: 10, color: T.muted, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>{l}</div></div>
                                    ))}
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <Btn size="sm" variant="secondary" onClick={() => setView(b)}>History</Btn>
                                    <Btn size="sm" variant="ghost" style={{ color: T.red }} onClick={() => deleteBidder(b.id)} loading={deleting === b.id}>🗑</Btn>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {view && (() => {
                const bdBids = bids.map(b => ({ ...b, tender: tenders.find(t => t.id === b.tender_id) })).filter(b => b.bidder_id === view.id && b.tender);
                return (
                    <Modal title={`${view.full_name || view.email} — Bid History`} onClose={() => setView(null)}>
                        <div style={{ display: "flex", gap: 14, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${T.border}` }}>
                            <Avatar name={view.full_name || view.email} size={48} />
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{view.full_name}</div>
                                <div style={{ fontSize: 12, color: T.muted }}>{view.company_name} · {view.email}</div>
                                {view.reg_no && <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>Reg: {view.reg_no}</div>}
                            </div>
                        </div>
                        {!bdBids.length ? <Empty icon="📋" title="No bids yet" /> : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {bdBids.map(b => (
                                    <div key={b.id} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", background: T.faint }}>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6 }}>{b.tender?.title}</div>
                                            <div style={{ display: "flex", gap: 6 }}><CatBadge cat={b.tender?.category || "Other"} /><Badge status={b.status} /></div>
                                            <div style={{ fontSize: 10, color: T.muted, marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>{fmtDate(b.submitted_at)}</div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ fontSize: 16, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(b.amount)}</div>
                                            {b.tender?.value && <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>{((b.amount / b.tender.value) * 100).toFixed(1)}% of est.</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Modal>
                );
            })()}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: REPORTS
// ─────────────────────────────────────────────────────────────────────────────

const AdminReports = ({ tenders, bids, bidders, toast }) => {
    const catStats = CATEGORIES.map(c => ({ ...c, count: tenders.filter(t => t.category === c.id).length, val: tenders.filter(t => t.category === c.id).reduce((a, t) => a + t.value, 0) })).filter(c => c.count > 0);
    const maxCat = Math.max(1, ...catStats.map(c => c.count));
    const topBidders = [...bidders].map(b => ({ ...b, count: bids.filter(bd => bd.bidder_id === b.id).length, won: bids.filter(bd => bd.bidder_id === b.id && bd.status === "awarded").length })).sort((a, b) => b.count - a.count).slice(0, 5);

    const exportCSV = (type) => {
        let rows, headers, name;
        if (type === "tenders") {
            headers = ["Title", "Reference", "Department", "Category", "Tender Type", "Value", "EMD", "Deadline", "Status", "Bids Received", "Location"];
            rows = tenders.map(t => [t.title, t.reference_no, t.dept, t.category, t.tender_type, t.value, t.emd_amount || "", t.deadline, t.status, bids.filter(b => b.tender_id === t.id).length, t.location]);
            name = "tenders_export.csv";
        } else {
            headers = ["Tender Title", "Reference", "Bidder Name", "Company", "Reg No", "Amount", "vs Estimate %", "Status", "Submitted Date", "Cover Note"];
            rows = bids.map(b => { const t = tenders.find(x => x.id === b.tender_id); const p = bidders.find(x => x.id === b.bidder_id); return [t?.title, t?.reference_no, p?.full_name, p?.company_name, p?.reg_no, b.amount, t?.value ? ((b.amount / t.value) * 100).toFixed(1) : "", b.status, b.submitted_at, b.message]; });
            name = "bids_export.csv";
        }
        const csv = [headers, ...rows].map(r => r.map(v => `"${v || ""}"`).join(",")).join("\n");
        const a = document.createElement("a"); a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv); a.download = name; a.click();
        toast(`${type === "tenders" ? "Tenders" : "Bids"} exported as CSV.`);
    };

    const summaryRows = [
        ["Published", tenders.filter(t => t.status !== "draft").length, T.accent],
        ["Active", tenders.filter(t => t.status === "active").length, T.green],
        ["Closed", tenders.filter(t => t.status === "closed").length, T.muted],
        ["Total Bids", bids.length, T.accent],
        ["Avg Bids", tenders.length ? (bids.length / tenders.length).toFixed(1) : 0, T.text],
        ["Awards Made", bids.filter(b => b.status === "awarded").length, T.gold],
        ["Bidders", bidders.length, "#a78bfa"],
        ["Total Value", fmtFull(tenders.filter(t => t.status !== "draft").reduce((a, t) => a + t.value, 0)), T.accent],
        ["Active Value", fmtFull(tenders.filter(t => t.status === "active").reduce((a, t) => a + t.value, 0)), T.green],
    ];

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            <PageHeader title="Reports & Analytics" actions={<>
                <Btn size="sm" variant="secondary" icon="⬇" onClick={() => exportCSV("tenders")}>Tenders CSV</Btn>
                <Btn size="sm" variant="secondary" icon="⬇" onClick={() => exportCSV("bids")}>Bids CSV</Btn>
            </>} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 20 }}>Tenders by Category</div>
                    {!catStats.length ? <div style={{ color: T.muted, fontSize: 12 }}>No data.</div> : catStats.map(c => (
                        <div key={c.id} style={{ marginBottom: 14 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 6, color: T.muted }}>
                                <span>{c.icon} {c.id}</span>
                                <span style={{ fontWeight: 700, color: c.color, fontFamily: "'DM Sans', sans-serif" }}>{c.count} · {fmt(c.val)}</span>
                            </div>
                            <div style={{ height: 5, background: T.faint, borderRadius: 4 }}>
                                <div style={{ height: 5, background: c.color, borderRadius: 4, width: `${(c.count / maxCat) * 100}%`, transition: "width .7s ease", boxShadow: `0 0 8px ${c.color}50` }} />
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 20 }}>Top Bidders</div>
                    {!topBidders.length ? <div style={{ color: T.muted, fontSize: 12 }}>No bids yet.</div> : topBidders.map((b, i) => {
                        const rankColors = ["#fbbf24", "#94a3b8", "#b45309", "#475569", "#374151"];
                        return (
                            <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, padding: "10px 12px", background: T.faint, borderRadius: 9 }}>
                                <div style={{ width: 26, height: 26, borderRadius: "50%", background: `${rankColors[i]}20`, border: `1.5px solid ${rankColors[i]}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: rankColors[i], fontFamily: "'DM Sans', sans-serif", flexShrink: 0 }}>{i + 1}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.full_name || b.email}</div>
                                    <div style={{ fontSize: 10, color: T.muted }}>{b.company_name}</div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{b.count} bids</div>
                                    {b.won > 0 && <div style={{ fontSize: 10, color: T.green, fontWeight: 700 }}>{b.won} won</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 20 }}>Procurement Summary</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                    {summaryRows.map(([l, v, c]) => (
                        <div key={l} style={{ background: T.faint, borderRadius: 10, padding: "14px 18px", border: `1px solid ${T.border}` }}>
                            <div style={{ fontSize: 9, color: T.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{l}</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: c || T.accent, fontFamily: "'DM Sans', sans-serif" }}>{v}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: NOTIFICATIONS
// ─────────────────────────────────────────────────────────────────────────────

const AdminNotifications = ({ tenders, bids, bidders }) => {
    const closing = tenders.filter(t => t.status === "active" && daysDiff(t.deadline) <= 7 && daysDiff(t.deadline) >= 0);
    const recent = bids.filter(b => new Date(b.submitted_at) > new Date(Date.now() - 86400000 * 3));
    const awarded = bids.filter(b => b.status === "awarded");

    const items = [
        ...closing.map(t => ({ type: "warning", icon: "⚠", title: "Tender closing soon", body: `"${t.title}"${t.reference_no ? " (" + t.reference_no + ")" : ""} closes in ${daysDiff(t.deadline)} day${daysDiff(t.deadline) !== 1 ? "s" : ""}`, time: t.deadline })),
        ...recent.map(b => { const p = bidders.find(x => x.id === b.bidder_id); const t = tenders.find(x => x.id === b.tender_id); return { type: "info", icon: "💼", title: "New bid received", body: `${p?.company_name || "A company"} bid ${fmt(b.amount)} on "${t?.title || "a tender"}"`, time: b.submitted_at }; }),
        ...awarded.slice(-5).map(b => { const t = tenders.find(x => x.id === b.tender_id); const p = bidders.find(x => x.id === b.bidder_id); return { type: "success", icon: "🏆", title: "Bid awarded", body: `${p?.company_name || "A bidder"} awarded contract for "${t?.title || ""}"`, time: b.submitted_at }; }),
    ].sort((a, b) => new Date(b.time) - new Date(a.time));

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            <PageHeader title="Notifications" sub={`${items.length} notification${items.length !== 1 ? "s" : ""}`} />
            {!items.length ? <Empty icon="🔔" title="All clear!" sub="No urgent notifications at this time." /> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {items.map((item, i) => {
                        const borderColors = { warning: "rgba(245,158,11,0.3)", info: "rgba(59,130,246,0.3)", success: "rgba(34,197,94,0.3)" };
                        return (
                            <div key={i} style={{ background: T.card, border: `1px solid ${borderColors[item.type] || T.border}`, borderRadius: 12, padding: "16px 20px", display: "flex", gap: 14, alignItems: "flex-start", animation: "fadeUp .3s ease" }}>
                                <div style={{ fontSize: 20, marginTop: 1 }}>{item.icon}</div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{item.title}</div>
                                    <div style={{ fontSize: 12, color: T.muted, marginTop: 3, lineHeight: 1.5 }}>{item.body}</div>
                                </div>
                                <div style={{ fontSize: 10, color: T.muted, whiteSpace: "nowrap", fontFamily: "'DM Sans', sans-serif" }}>{fmtDate(item.time)}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: SETTINGS
// ─────────────────────────────────────────────────────────────────────────────

const AdminSettings = ({ user, toast }) => {
    const [f, setF] = useState({ full_name: user.name, password: "", confirm: "" });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (f.password && f.password !== f.confirm) { toast("Passwords do not match.", "error"); return; }
        setSaving(true);
        const u = { full_name: f.full_name }; if (f.password) u.password_hash = f.password;
        const { error } = await supabase.from("profiles").update(u).eq("id", user.id);
        setSaving(false); toast(error ? error.message : "Profile updated.", error ? "error" : "success");
    };
    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            <PageHeader title="Settings" />
            <div style={{ maxWidth: 520 }}>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 26, marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22, paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
                        <Avatar name={user.name} size={50} />
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{user.name}</div>
                            <div style={{ fontSize: 12, color: T.muted }}>{user.email}</div>
                            <div style={{ fontSize: 10, color: T.accent, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.08em" }}>Administrator</div>
                        </div>
                    </div>
                    <Input label="Full Name" value={f.full_name} onChange={e => setF(p => ({ ...p, full_name: e.target.value }))} />
                    <Input label="Email" value={user.email} disabled />
                    <Input label="New Password" type="password" placeholder="Leave blank to keep current" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} />
                    <Input label="Confirm Password" type="password" placeholder="••••••••" value={f.confirm} onChange={e => setF(p => ({ ...p, confirm: e.target.value }))} />
                    <Btn variant="primary" onClick={save} loading={saving}>Save Changes</Btn>
                </div>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>System Status</div>
                    <div style={{ padding: "10px 14px", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 8, fontSize: 12, color: T.green, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.green, boxShadow: `0 0 8px ${T.green}` }} />
                        Supabase live connection active
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: DOCUMENTS REVIEW
// ─────────────────────────────────────────────────────────────────────────────

const AdminDocuments = ({ bidDocuments, setBidDocuments, bids, tenders, bidders, toast }) => {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [reviewModal, setReviewModal] = useState(null);
    const [noteText, setNoteText] = useState("");
    const [saving, setSaving] = useState(false);
    const [openBidders, setOpenBidders] = useState(() => new Set());

    const fmtSz = s => !s ? "" : s < 1024 ? s + " B" : s < 1048576 ? (s / 1024).toFixed(1) + " KB" : (s / 1048576).toFixed(1) + " MB";

    const enrichedDocs = useMemo(() => {
        let list = bidDocuments.map(d => ({
            ...d,
            bid: bids.find(b => b.id === d.bid_id),
            tender: tenders.find(t => t.id === d.tender_id),
            bidder: bidders.find(p => p.id === d.bidder_id),
        }));
        if (statusFilter !== "all") list = list.filter(d => d.status === statusFilter);
        if (search) list = list.filter(d => [
            d.doc_name, d.file_name, d.bidder?.full_name, d.bidder?.company_name, d.tender?.title
        ].join(" ").toLowerCase().includes(search.toLowerCase()));
        return list.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
    }, [bidDocuments, bids, tenders, bidders, search, statusFilter]);

    // Group the filtered docs by bidder, then by tender within each bidder
    const bidderGroups = useMemo(() => {
        const map = new Map();
        for (const d of enrichedDocs) {
            const key = d.bidder_id || d.bidder?.full_name || "unknown";
            if (!map.has(key)) map.set(key, { key, bidder: d.bidder, docs: [] });
            map.get(key).docs.push(d);
        }
        return [...map.values()].map(g => {
            const counts = { pending: 0, approved: 0, rejected: 0 };
            g.docs.forEach(d => { counts[d.status] = (counts[d.status] || 0) + 1; });
            const tmap = new Map();
            for (const d of g.docs) {
                const tkey = d.tender_id || d.tender?.title || "general";
                if (!tmap.has(tkey)) tmap.set(tkey, { key: tkey, tender: d.tender, docs: [] });
                tmap.get(tkey).docs.push(d);
            }
            return { ...g, counts, total: g.docs.length, tenders: [...tmap.values()] };
        }).sort((a, b) => (b.counts.pending - a.counts.pending) || (b.total - a.total));
    }, [enrichedDocs]);

    const searching = search.trim().length > 0;
    const isOpen = key => searching || openBidders.has(key);
    const toggleBidder = key => setOpenBidders(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const expandAll = () => setOpenBidders(new Set(bidderGroups.map(g => g.key)));
    const collapseAll = () => setOpenBidders(new Set());
    const allOpen = bidderGroups.length > 0 && bidderGroups.every(g => openBidders.has(g.key));

    const openReview = (doc, action) => { setReviewModal({ doc, action }); setNoteText(""); };

    const submitReview = async () => {
        if (!reviewModal) return;
        const { doc, action } = reviewModal;
        setSaving(true);
        const newStatus = action === "approve" ? "approved" : "rejected";
        const { error } = await supabase.from("bid_documents").update({
            status: newStatus,
            admin_note: noteText.trim() || null,
            reviewed_at: new Date().toISOString(),
        }).eq("id", doc.id);
        setSaving(false);
        if (error) { toast(error.message, "error"); return; }
        setBidDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, status: newStatus, admin_note: noteText.trim() || null } : d));
        setReviewModal(null);
        toast(action === "approve" ? "Document approved." : "Document rejected.", action === "approve" ? "success" : "info");
    };

    const S = {
        pending: { bg: "rgba(217,119,6,0.08)", color: "#b45309", label: "Pending", dot: "#d97706" },
        approved: { bg: "rgba(34,197,94,0.08)", color: T.green, label: "Approved", dot: T.green },
        rejected: { bg: "rgba(239,68,68,0.06)", color: T.red, label: "Rejected", dot: T.red },
    };

    const pending = bidDocuments.filter(d => d.status === "pending").length;
    const approved = bidDocuments.filter(d => d.status === "approved").length;
    const rejected = bidDocuments.filter(d => d.status === "rejected").length;

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            {/* Review Modal */}
            {reviewModal && (
                <Modal
                    title={reviewModal.action === "approve" ? "Approve Document" : "Reject Document"}
                    subtitle={reviewModal.doc.doc_name}
                    onClose={() => setReviewModal(null)}
                    maxWidth={500}
                >
                    <div style={{ marginBottom: 16 }}>
                        {/* File info card */}
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", background: T.faint, borderRadius: 10, border: `1px solid ${T.border}`, marginBottom: 14 }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{reviewModal.doc.doc_name}</div>
                                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{reviewModal.doc.file_name}{reviewModal.doc.file_size ? ` · ${fmtSz(reviewModal.doc.file_size)}` : ""}</div>
                                {(() => { const d = enrichedDocs.find(x => x.id === reviewModal.doc.id); return d?.bidder ? <div style={{ fontSize: 11, color: T.muted }}>{d.bidder.full_name} · {d.bidder.company_name}</div> : null; })()}
                            </div>
                        </div>
                        <a href={reviewModal.doc.file_url} target="_blank" rel="noreferrer"
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", background: T.accentLo, color: T.accent, border: `1px solid ${T.accentMid}`, borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: "none", marginBottom: 16 }}>
                            Open Document in New Tab
                        </a>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 6 }}>
                            {reviewModal.action === "approve" ? "Approval Note (optional)" : "Rejection Reason (optional)"}
                        </div>
                        <textarea
                            value={noteText} onChange={e => setNoteText(e.target.value)}
                            placeholder={reviewModal.action === "approve" ? "e.g. Document verified and accepted." : "e.g. GST certificate is expired. Please re-submit a valid certificate dated within 1 year."}
                            style={{ width: "100%", padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12, fontFamily: "'DM Sans', sans-serif", resize: "vertical", minHeight: 80, outline: "none", boxSizing: "border-box" }}
                        />
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                        <Btn variant="secondary" onClick={() => setReviewModal(null)} style={{ flex: 1 }}>Cancel</Btn>
                        <Btn
                            variant={reviewModal.action === "approve" ? "success" : "danger"}
                            onClick={submitReview}
                            loading={saving}
                            style={{ flex: 2 }}
                        >
                            {reviewModal.action === "approve" ? "Confirm Approval" : "Confirm Rejection"}
                        </Btn>
                    </div>
                </Modal>
            )}

            <PageHeader title="Document Review" sub={`${pending} pending · ${approved} approved · ${rejected} rejected`} />

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 22 }}>
                <Stat label="Total Uploaded" value={bidDocuments.length} color={T.accent} />
                <Stat label="Pending Review" value={pending} color="#d97706" />
                <Stat label="Approved" value={approved} color={T.green} />
                <Stat label="Rejected" value={rejected} color={T.red} />
            </div>

            {/* Filters */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search by document, bidder, company or tender…" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    style={{ padding: "9px 12px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 12, outline: "none", color: T.text, fontFamily: "'DM Sans', sans-serif" }}>
                    <option value="all">All Status</option>
                    <option value="pending">Pending Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                </select>
            </div>

            {!bidderGroups.length ? (
                <Empty icon="📁" title={bidDocuments.length === 0 ? "No documents uploaded yet" : "No documents match filters"}
                    sub={bidDocuments.length === 0 ? "Documents appear here once bidders upload files during bid submission." : "Try adjusting your filters."} />
            ) : (
                <>
                    {/* Expand / collapse control */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                        <div style={{ fontSize: 12, color: T.muted }}>{bidderGroups.length} bidder{bidderGroups.length > 1 ? "s" : ""} · {enrichedDocs.length} document{enrichedDocs.length > 1 ? "s" : ""}</div>
                        <button onClick={() => allOpen ? collapseAll() : expandAll()} disabled={searching}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 13px", border: `1px solid ${T.border}`, borderRadius: 8, background: T.card, color: searching ? T.muted : T.accent, fontSize: 11, fontWeight: 700, cursor: searching ? "default" : "pointer", fontFamily: "'DM Sans', sans-serif", opacity: searching ? 0.6 : 1 }}>
                            {allOpen ? "Collapse all" : "Expand all"}
                        </button>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {bidderGroups.map(g => {
                            const open = isOpen(g.key);
                            const chips = [
                                g.counts.pending > 0 && ["#d97706", `${g.counts.pending} pending`],
                                g.counts.approved > 0 && [T.green, `${g.counts.approved} approved`],
                                g.counts.rejected > 0 && [T.red, `${g.counts.rejected} rejected`],
                            ].filter(Boolean);
                            return (
                                <div key={g.key} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden", boxShadow: T.shadow }}>
                                    {/* Bidder header (dropdown toggle) */}
                                    <div onClick={() => toggleBidder(g.key)} className="row-hover"
                                        style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", cursor: searching ? "default" : "pointer", background: open ? T.faint : T.card, borderBottom: open ? `1px solid ${T.border}` : "none", transition: "background .15s" }}>
                                        <Avatar name={g.bidder?.full_name || "?"} size={38} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{g.bidder?.full_name || "Unknown bidder"}</div>
                                            <div style={{ fontSize: 11, color: T.muted }}>{g.bidder?.company_name || "—"}</div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                            {chips.map(([c, label]) => (
                                                <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: c, background: `${c}15`, padding: "3px 9px", borderRadius: 20, border: `1px solid ${c}35`, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
                                                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: c }} />{label}
                                                </span>
                                            ))}
                                            <span style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginLeft: 2, whiteSpace: "nowrap" }}>{g.total} doc{g.total > 1 ? "s" : ""}</span>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", marginLeft: 4, flexShrink: 0 }}><polyline points="6 9 12 15 18 9" /></svg>
                                        </div>
                                    </div>

                                    {/* Tenders + document rows */}
                                    {open && g.tenders.map((tg, ti) => (
                                        <div key={tg.key}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 18px", background: T.faint, borderTop: `1px solid ${T.border}` }}>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{tg.tender?.title || "General documents"}</span>
                                                {tg.tender?.reference_no && <span style={{ fontSize: 10, color: T.accent, fontWeight: 600 }}>#{tg.tender.reference_no}</span>}
                                                <span style={{ fontSize: 10, color: T.muted, marginLeft: "auto" }}>{tg.docs.length} document{tg.docs.length > 1 ? "s" : ""}</span>
                                            </div>
                                            {tg.docs.map(doc => {
                                                const s = S[doc.status] || S.pending;
                                                return (
                                                    <div key={doc.id} className="row-hover" style={{ display: "flex", alignItems: "center", gap: 14, padding: "11px 18px", borderTop: `1px solid ${T.border}`, background: s.bg }}>
                                                        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                                                            <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{doc.doc_name}</div>
                                                            <a href={doc.file_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: T.accent, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                                {doc.file_name}{doc.file_size && <span style={{ color: T.muted, fontWeight: 400 }}>({fmtSz(doc.file_size)})</span>}
                                                            </a>
                                                            {doc.admin_note && <div style={{ fontSize: 10, color: T.muted, marginTop: 2, fontStyle: "italic" }}>Note: "{doc.admin_note}"</div>}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", flexShrink: 0 }}>{fmtDate(doc.uploaded_at)}</div>
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: s.color, background: `${s.dot}18`, padding: "3px 10px", borderRadius: 20, border: `1px solid ${s.dot}40`, textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap", flexShrink: 0 }}>
                                                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot }} />{s.label}
                                                        </span>
                                                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                                            {doc.status !== "approved" && (
                                                                <button onClick={() => openReview(doc, "approve")}
                                                                    style={{ padding: "5px 12px", border: `1px solid ${T.green}55`, borderRadius: 7, background: "rgba(21,128,61,0.08)", color: T.green, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                                                                    Approve
                                                                </button>
                                                            )}
                                                            {doc.status !== "rejected" && (
                                                                <button onClick={() => openReview(doc, "reject")}
                                                                    style={{ padding: "5px 12px", border: `1px solid ${T.red}55`, borderRadius: 7, background: "rgba(220,38,38,0.07)", color: T.red, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                                                                    Reject
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: PAYMENTS — review bidder-submitted document-fee payments
// ─────────────────────────────────────────────────────────────────────────────

const AdminPayments = ({ bids, setBids, tenders, bidders, toast }) => {
    const [filter, setFilter] = useState("pending");
    const [saving, setSaving] = useState(null);

    const rows = useMemo(() => bids
        .filter(b => b.payment_status)
        .map(b => ({ ...b, tender: tenders.find(t => t.id === b.tender_id), bidder: bidders.find(p => p.id === b.bidder_id) }))
        .filter(b => filter === "all" ? true : b.payment_status === filter)
        .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)),
        [bids, tenders, bidders, filter]);

    const counts = useMemo(() => {
        const c = { pending: 0, approved: 0, rejected: 0 };
        bids.forEach(b => { if (b.payment_status && c[b.payment_status] != null) c[b.payment_status]++; });
        return c;
    }, [bids]);

    const act = async (bid, status) => {
        setSaving(bid.id + status);
        const { error } = await supabase.from("bids").update({ payment_status: status }).eq("id", bid.id);
        setSaving(null);
        if (error) { toast(error.message, "error"); return; }
        setBids(prev => prev.map(x => x.id === bid.id ? { ...x, payment_status: status } : x));
        toast(status === "approved" ? "Payment approved." : "Payment rejected.", status === "approved" ? "success" : "info");
    };

    const S = { pending: ["#d97706", "Pending"], approved: [T.green, "Approved"], rejected: [T.red, "Rejected"] };

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            <PageHeader title="Payment Reviews" sub={`${counts.pending} awaiting review · ${counts.approved} approved · ${counts.rejected} rejected`} />

            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                {[["pending", `Pending (${counts.pending})`], ["approved", `Approved (${counts.approved})`], ["rejected", `Rejected (${counts.rejected})`], ["all", "All"]].map(([k, label]) => (
                    <button key={k} onClick={() => setFilter(k)}
                        style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${filter === k ? T.accent : T.border}`, background: filter === k ? T.accent : T.card, color: filter === k ? "#fff" : T.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                        {label}
                    </button>
                ))}
            </div>

            {!rows.length ? (
                <Empty icon="💳" title="No payments to show" sub={filter === "pending" ? "Document-fee payments awaiting review will appear here." : "Nothing in this view."} />
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {rows.map(b => {
                        const [color, label] = S[b.payment_status] || S.pending;
                        return (
                            <div key={b.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", boxShadow: T.shadow }}>
                                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                                    <Avatar name={b.bidder?.full_name || "?"} size={40} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{b.bidder?.full_name || "—"}</div>
                                        <div style={{ fontSize: 11, color: T.muted }}>{b.bidder?.company_name || "—"}</div>
                                        <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{b.tender?.title || "—"}{b.tender?.reference_no ? ` · ${b.tender.reference_no}` : ""}</div>
                                    </div>
                                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                                        <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{fmtFull(b.tender?.bid_document_cost || 0)}</div>
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color, background: `${color}18`, padding: "3px 10px", borderRadius: 20, border: `1px solid ${color}40`, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>
                                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: color }} />{label}
                                        </span>
                                    </div>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, background: T.faint, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px" }}>
                                    {[["Transaction Type", b.payment_type], ["Transaction ID", b.payment_txn_id], ["Paid At", b.payment_time], ["Payer Name", b.payer_name]].map(([k, v]) => (
                                        <div key={k}>
                                            <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>{k}</div>
                                            <div style={{ fontSize: 12, color: T.text, fontWeight: 600, marginTop: 2, wordBreak: "break-word" }}>{v || "—"}</div>
                                        </div>
                                    ))}
                                </div>

                                {b.payment_status === "pending" && (
                                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                                        <Btn variant="primary" onClick={() => act(b, "approved")} loading={saving === b.id + "approved"} style={{ flex: 1 }}>Approve Payment</Btn>
                                        <Btn variant="danger" onClick={() => act(b, "rejected")} loading={saving === b.id + "rejected"} style={{ flex: 1 }}>Reject</Btn>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN APP SHELL
// ─────────────────────────────────────────────────────────────────────────────

export function AdminApp({ user, onLogout }) {
    const [page, setPage] = useState("dashboard");
    const [tenders, setTenders] = useState([]);
    const [bids, setBids] = useState([]);
    const [bidders, setBidders] = useState([]);
    const [bidDocuments, setBidDocuments] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        const [{ data: t, error: te }, { data: b, error: be }, { data: bd, error: bde }, { data: docs, error: de }] = await Promise.all([
            supabase.from("tenders").select("*").order("created_at", { ascending: false }),
            supabase.from("bids").select("*").order("submitted_at", { ascending: false }),
            supabase.from("profiles").select("*").eq("role", "bidder"),
            supabase.from("bid_documents").select("*").order("uploaded_at", { ascending: false }),
        ]);
        if (te) console.error("Tenders fetch error:", te);
        if (be) console.error("Bids fetch error:", be);
        if (bde) console.error("Profiles fetch error:", bde);
        if (de) console.error("bid_documents fetch error:", de);
        console.log("bid_documents fetched:", docs?.length ?? 0, docs);
        setTenders(t || []); setBids(b || []); setBidders(bd || []); setBidDocuments(docs || []);
        setLoading(false);
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const urgentCount = tenders.filter(t => t.status === "active" && daysDiff(t.deadline) <= 7 && daysDiff(t.deadline) >= 0).length;
    const newBids = bids.filter(b => new Date(b.submitted_at) > new Date(Date.now() - 86400000 * 2)).length;
    const pendingDocs = bidDocuments.filter(d => d.status === "pending").length;
    const pendingPayments = bids.filter(b => b.payment_status === "pending").length;

    const navItems = [
        { id: "dashboard", icon: "📊", label: "Dashboard" },
        { id: "tenders", icon: "📋", label: "Tenders" },
        { id: "bids", icon: "💼", label: "All Bids" },
        { id: "payments", icon: "💳", label: "Payments" },
        { id: "documents", icon: "📁", label: "Documents" },
        { id: "bidders", icon: "👥", label: "Bidders" },
        { id: "reports", icon: "📈", label: "Reports" },
        { id: "notifications", icon: "🔔", label: "Notifications" },
        { id: "settings", icon: "⚙️", label: "Settings" },
    ];

    return (
        <ToastContext>
            {toast => (
                <div style={{ display: "flex", minHeight: "100vh", fontFamily: "'DM Sans', -apple-system, sans-serif", background: "#f4f5f7", padding: 20, gap: 18 }}>
                    <GlobalStyle />
                    <SideNav items={navItems} active={page} setActive={setPage} user={user} onLogout={onLogout} brand="Admin Console" badge={{ notifications: urgentCount + newBids, documents: pendingDocs, payments: pendingPayments }} accent="#1d4ed8" accentSoft="#3b82f6" />
                    <div style={{ flex: 1, padding: "6px 2px 30px 2px", overflowY: "auto", minWidth: 0 }}>
                        {loading ? <Spinner /> : (() => {
                            const props = { tenders, setTenders, bids, setBids, bidders, setBidders, toast, setPage };
                            const pages = {
                                dashboard: <AdminDashboard {...props} />,
                                tenders: <AdminTenders {...props} />,
                                bids: <AdminBids {...props} />,
                                payments: <AdminPayments bids={bids} setBids={setBids} tenders={tenders} bidders={bidders} toast={toast} />,
                                documents: <AdminDocuments bidDocuments={bidDocuments} setBidDocuments={setBidDocuments} bids={bids} tenders={tenders} bidders={bidders} toast={toast} />,
                                bidders: <AdminBidders {...props} />,
                                reports: <AdminReports {...props} />,
                                notifications: <AdminNotifications {...props} />,
                                settings: <AdminSettings user={user} toast={toast} />,
                            };
                            return pages[page];
                        })()}
                    </div>
                </div>
            )}
        </ToastContext>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// BIDDER: BROWSE — rich tender cards + full detail modal
// ─────────────────────────────────────────────────────────────────────────────

const BidderBrowse = ({ tenders, bids, user, onBidAdded, toast, savedIds, setSavedIds, setPage }) => {
    const [search, setSearch] = useState("");
    const [cat, setCat] = useState("all");
    const [sort, setSort] = useState("deadline");
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [bidModal, setBidModal] = useState(null);
    const [detailT, setDetailT] = useState(null);
    const [bidAmt, setBidAmt] = useState("");
    const [bidMsg, setBidMsg] = useState("");
    const [expYears, setExpYears] = useState("");
    const [pastProjects, setPastProjects] = useState("");
    const [bidStep, setBidStep] = useState(1);   // 1=Technical, 2=Financial, 3=Declaration
    const [emdRef, setEmdRef] = useState("");
    const [checkedDocs, setCheckedDocs] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState({});   // { docName: File }
    // eslint-disable-next-line no-unused-vars
    const [uploadProgress, setUploadProgress] = useState({});  // { docName: 'uploading'|'done'|'error' }
    const [declared, setDeclared] = useState(false);
    const [submitting, setSubmit] = useState(false);
    const [payState, setPayState] = useState("idle");   // idle | processing | paid
    const [showPayForm, setShowPayForm] = useState(false);
    const [payForm, setPayForm] = useState({ type: "", txnId: "", time: "", payerName: "" });
    const [payUnderReview, setPayUnderReview] = useState(false);
    const [bidSent, setBidSent] = useState(false);
    // docValidation: { [docName]: { status: 'validating'|'valid'|'warning'|'invalid', message: '', suggestion: '' } }
    const [docValidation, setDocValidation] = useState({});

    const openBidModal = t => {
        setBidModal(t); setBidAmt(""); setBidMsg(""); setExpYears(""); setPastProjects(""); setBidStep(1);
        setEmdRef(""); setCheckedDocs({}); setUploadedFiles({}); setUploadProgress({}); setDeclared(false); setDocValidation({}); setPayState("idle"); setShowPayForm(false); setPayForm({ type: "", txnId: "", time: "", payerName: "" });
    };
    // Make the bid page behave like a real page: scroll to top, own URL hash, browser Back closes it
    useEffect(() => {
        if (!bidModal) return;
        window.scrollTo(0, 0);
        window.history.pushState({ bidPage: true }, "", `#/bid/${bidModal.id}`);
        const onPop = () => setBidModal(null);
        window.addEventListener("popstate", onPop);
        return () => {
            window.removeEventListener("popstate", onPop);
            if (window.location.hash.startsWith("#/bid/")) window.history.back();
        };
    }, [bidModal]);
    // savedIds / setSavedIds are props from BidderApp so the nav badge stays in sync

    const toggleSave = id => {
        const next = savedIds.includes(id) ? savedIds.filter(x => x !== id) : [...savedIds, id];
        setSavedIds(next); localStorage.setItem("saved_tenders", JSON.stringify(next));
        toast(savedIds.includes(id) ? "Removed from saved." : "Tender saved.");
    };

    // Exclude withdrawn bids — a bidder who withdrew can re-submit
    const alreadyBid = id => bids.some(b => b.tender_id === id && b.bidder_id === user.id && b.status !== "withdrawn");
    const bidCount = useCallback(id => bids.filter(b => b.tender_id === id && b.status !== "withdrawn").length, [bids]);

    const filtered = useMemo(() => {
        let list = tenders.filter(t => t.status === "active");
        if (search) list = list.filter(t => [t.title, t.dept, t.description, t.reference_no, t.location, t.tender_type].join(" ").toLowerCase().includes(search.toLowerCase()));
        if (cat !== "all") list = list.filter(t => t.category === cat);
        if (filters.department !== "all") list = list.filter(t => t.dept === filters.department);
        const vr = VALUE_RANGES[filters.valueRange];
        if (vr.min > 0 || vr.max !== Infinity) list = list.filter(t => t.value >= vr.min && t.value < vr.max);
        if (filters.deadline !== "all" && filters.deadline !== "expired") list = list.filter(t => { const d = daysDiff(t.deadline); return d >= 0 && d <= parseInt(filters.deadline); });
        return [...list].sort((a, b) => {
            if (sort === "deadline") return new Date(a.deadline) - new Date(b.deadline);
            if (sort === "newest") return new Date(b.created_at) - new Date(a.created_at);
            if (sort === "value_desc") return b.value - a.value;
            if (sort === "value_asc") return a.value - b.value;
            if (sort === "bids_desc") return bidCount(b.id) - bidCount(a.id);
            if (sort === "title") return a.title.localeCompare(b.title);
            return 0;
        });
    }, [tenders, search, cat, filters, sort, bidCount]);

    const preflight = () => {
        const amt = parseFloat(bidAmt);
        if (!amt || amt <= 0) { toast("Enter a valid bid amount.", "error"); return false; }
        if (bidModal.min_bid_value && amt < bidModal.min_bid_value) { toast(`Bid must be at least ${fmtFull(bidModal.min_bid_value)}.`, "error"); return false; }
        if (bidModal.max_bid_value && amt > bidModal.max_bid_value) { toast(`Bid cannot exceed ${fmtFull(bidModal.max_bid_value)}.`, "error"); return false; }
        if (bidModal.emd_amount && !emdRef.trim()) { toast("EMD Payment Reference Number is required.", "error"); return false; }
        if (!declared) { toast("Please accept the declaration before submitting.", "error"); return false; }
        const invalidDocs = Object.entries(docValidation).filter(([, v]) => v.status === "invalid").map(([k]) => k);
        if (invalidDocs.length > 0) { toast(`Please upload proper documents before submitting. Flagged: ${invalidDocs.join(", ")}`, "error"); return false; }
        const validatingDocs = Object.entries(docValidation).filter(([, v]) => v.status === "validating").map(([k]) => k);
        if (validatingDocs.length > 0) { toast("Please wait, your documents are still being checked.", "info"); return false; }
        return true;
    };

    // Step 3 → go to the payment page (or submit directly if there is no fee)
    const proceedFromDeclaration = () => {
        if (!preflight()) return;
        if (bidModal.bid_document_cost > 0) setBidStep(4);
        else submitBid();
    };

    // Step 4 Pay → simulate the transaction, then ask for its details
    const startPayment = async () => {
        if (!preflight()) return;
        setPayState("processing");
        await new Promise(r => setTimeout(r, 2000));
        setPayState("paid");
        setShowPayForm(true);
    };

    const submitBid = async () => {
        if (!preflight()) return;
        const docFee = bidModal.bid_document_cost || 0;
        if (docFee > 0) {
            if (!payForm.type || !payForm.txnId.trim() || !payForm.time.trim() || !payForm.payerName.trim()) {
                toast("Please fill in all payment details.", "error"); return;
            }
        }

        setSubmit(true);
        const amt = parseFloat(bidAmt);
        const fullMessage = [
            expYears && `Years of Experience: ${expYears}`,
            pastProjects.trim() && `Company Experience & Past Projects:\n${pastProjects.trim()}`,
            bidMsg && `Technical Proposal:\n${bidMsg}`,
            emdRef && `EMD Reference: ${emdRef}`,
        ].filter(Boolean).join("\n\n");
        const insertPayload = { tender_id: bidModal.id, bidder_id: user.id, amount: amt, message: fullMessage || null, status: "submitted" };
        if (docFee > 0) {
            insertPayload.payment_type = payForm.type;
            insertPayload.payment_txn_id = payForm.txnId.trim();
            insertPayload.payment_time = payForm.time.trim();
            insertPayload.payer_name = payForm.payerName.trim();
            insertPayload.payment_status = "pending";
        }
        const { data, error } = await supabase.from("bids").insert(insertPayload).select().single();
        if (error) { setSubmit(false); setPayState("idle"); setShowPayForm(false); toast(error.message, "error"); return; }

        // Upload each document file to Supabase Storage and record in bid_documents
        const docsToUpload = Object.entries(uploadedFiles);
        if (docsToUpload.length > 0) {
            let uploadErrors = 0;
            for (const [docName, file] of docsToUpload) {
                try {
                    const safeName = `${data.id}/${docName.replace(/[^a-zA-Z0-9._-]/g, "_")}_${Date.now()}_${file.name}`;
                    const { data: storageData, error: storageErr } = await supabase.storage
                        .from("bid-documents")
                        .upload(safeName, file, { cacheControl: "3600", upsert: false });
                    if (storageErr) {
                        console.error("Storage upload error:", storageErr);
                        uploadErrors++;
                        continue;
                    }
                    const { data: { publicUrl } } = supabase.storage.from("bid-documents").getPublicUrl(storageData.path);
                    const { error: dbErr } = await supabase.from("bid_documents").insert({
                        bid_id: data.id,
                        tender_id: bidModal.id,
                        bidder_id: user.id,
                        doc_name: docName,
                        file_name: file.name,
                        file_url: publicUrl,
                        file_size: file.size,
                        status: "pending",
                    });
                    if (dbErr) { console.error("bid_documents insert error:", dbErr); uploadErrors++; }
                } catch (err) { console.error("Doc upload err:", err); uploadErrors++; }
            }
            if (uploadErrors > 0) {
                toast(`⚠️ ${uploadErrors} document(s) failed to upload. Check storage permissions in Supabase.`, "error");
            }
        }


        setSubmit(false);
        onBidAdded(data); setBidModal(null); setPayState("idle"); setShowPayForm(false);
        setPayUnderReview((bidModal.bid_document_cost || 0) > 0);
        setBidSent(true);
        (async () => {
            const { data: bidderProfile } = await supabase.from("profiles").select("company_name,reg_no").eq("id", user.id).maybeSingle();
            const analysis = await rateBidWithAI(bidModal, data, bidderProfile);
            if (analysis) {
                await supabase.from("bids").update({
                    ai_rating: analysis.rating,
                    ai_analysis: JSON.stringify(analysis),
                }).eq("id", data.id);
            }
        })();
    };

    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
    const [pageSize, setPageSize] = useState(10);
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    // Reset to page 1 whenever filters change
    const prevFilterKey = search + cat + JSON.stringify(filters) + sort;
    const [lastFilterKey, setLastFilterKey] = useState(prevFilterKey);
    if (prevFilterKey !== lastFilterKey) { setLastFilterKey(prevFilterKey); setCurrentPage(1); }

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            {/* Tender list — hidden while the bid page is open */}
            <div style={{ display: bidModal ? "none" : "block" }}>
            {/* Page header */}
            <div style={{ marginBottom: 22 }}>
                <h1 style={{ margin: "0 0 4px", fontSize: 26, fontWeight: 700, color: T.text, letterSpacing: "-0.5px" }}>Browse Tenders</h1>
                <div style={{ fontSize: 13, color: T.muted }}>Discover government procurement opportunities across India</div>
            </div>

            {/* Stat cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 22 }}>
                <Stat label="Active Tenders" value={tenders.filter(t => t.status === "active").length} sub="Live opportunities"
                    iconType="tenders"
                    onClick={() => { setSearch(""); setCat("all"); setFilters(DEFAULT_FILTERS); setSort("deadline"); }} />
                <Stat label="Closing Soon" value={tenders.filter(t => t.status === "active" && daysDiff(t.deadline) <= 7 && daysDiff(t.deadline) >= 0).length} sub="Deadline approaching"
                    iconType="clock"
                    onClick={() => { setSearch(""); setCat("all"); setFilters(f => ({ ...DEFAULT_FILTERS, deadline: "7" })); setSort("deadline"); }} />
                <Stat label="My Bids" value={bids.filter(b => b.status !== "withdrawn").length} sub="Bids placed"
                    iconType="bids"
                    onClick={() => setPage && setPage("mybids")} />
                <Stat label="Saved" value={savedIds.length} sub="Saved tenders"
                    iconType="save"
                    onClick={() => setPage && setPage("saved")} />
            </div>

            {/* Search + Sort */}
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search by title, reference no, department, location…" />
                <select value={sort} onChange={e => setSort(e.target.value)} style={{ padding: "9px 14px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 12, outline: "none", color: T.text, fontFamily: "'DM Sans', sans-serif", minWidth: 170 }}>
                    {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>

            <CatPills selected={cat} onChange={setCat} />
            <FilterPanel filters={filters} setFilters={setFilters} count={filtered.length} />

            {/* Tender list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {paginated.map(t => {
                    const dd = daysDiff(t.deadline); const bidded = alreadyBid(t.id); const bc = bidCount(t.id); const saved = savedIds.includes(t.id);
                    const tags = [t.category, t.dept, t.contract_type].filter(Boolean);
                    return (
                        <div key={t.id} className="card-hover" style={{ background: "#ffffff", border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", boxShadow: T.shadow }}>
                            <div style={{ padding: "18px 22px 16px" }}>
                                {/* Row 1: posted/location left, quick-action icons right */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 12 }}>
                                    <div style={{ fontSize: 12, color: T.muted, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                        <span>{timeAgo(t.created_at)}</span>
                                        {t.location && <><span>·</span><span>{hl(t.location, search)}</span></>}
                                    </div>
                                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                        <button onClick={() => !bidded && openBidModal(t)} title={bidded ? "Bid already placed" : "Submit bid"}
                                            style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${bidded ? "#15803d" : T.border}`, background: bidded ? "#15803d" : "#fff", cursor: bidded ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s" }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={bidded ? "#fff" : "#6b7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" /></svg>
                                        </button>
                                        <button onClick={() => toggleSave(t.id)} title={saved ? "Unsave" : "Save"}
                                            style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${saved ? "#dc2626" : T.border}`, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s" }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill={saved ? "#dc2626" : "none"} stroke={saved ? "#dc2626" : "#6b7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.5-1.5 3-3.5 3-6a5 5 0 0 0-9-3 5 5 0 0 0-9 3c0 5 5 8 9 11 1.5-1 3.2-2.4 4.7-3.9" /></svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Title */}
                                <h3 onClick={() => setDetailT(t)}
                                    style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 700, color: T.text, lineHeight: 1.35, letterSpacing: "-0.2px", cursor: "pointer", transition: "color .15s" }}
                                    onMouseEnter={e => { e.currentTarget.style.color = T.accent; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = T.text; }}>
                                    {hl(t.title, search)}
                                </h3>

                                {/* Verification-style meta row */}
                                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 12.5, color: "#374151", marginBottom: 12 }}>
                                    <span style={{ fontWeight: 700, color: T.text, fontSize: 15 }}>{fmt(t.value)}</span>
                                    <span style={{ color: T.border }}>·</span>
                                    {t.tender_type && <>
                                        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                            <span style={{ width: 16, height: 16, borderRadius: "50%", background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                            </span>
                                            {t.tender_type}
                                        </span>
                                        <span style={{ color: T.border }}>·</span>
                                    </>}
                                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                                        {bc} bid{bc !== 1 ? "s" : ""}
                                    </span>
                                    {t.emd_amount && <>
                                        <span style={{ color: T.border }}>·</span>
                                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
                                            EMD: {fmt(t.emd_amount)}
                                        </span>
                                    </>}
                                    {t.dept && <>
                                        <span style={{ color: T.border }}>·</span>
                                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M12 21c-4-4-7-8-7-12a7 7 0 0 1 14 0c0 4-3 8-7 12z" /><circle cx="12" cy="9" r="2" /></svg>
                                            {hl(t.dept.split(" ").slice(0, 4).join(" "), search)}
                                        </span>
                                    </>}
                                </div>

                                {/* Description */}
                                {t.description && <p style={{ margin: "0 0 14px", fontSize: 13, color: "#374151", lineHeight: 1.7 }}>
                                    {t.contract_type && <strong>{t.contract_type} - </strong>}
                                    {t.description}
                                </p>}

                                {/* Tags + deadline + actions */}
                                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                                    {tags.map((tag, i) => (
                                        <span key={i} style={{ fontSize: 12, fontWeight: 500, color: "#374151", background: T.faint, padding: "6px 13px", borderRadius: 20 }}>{tag}</span>
                                    ))}
                                    {dd >= 0 && dd <= 7 && <span style={{ fontSize: 12, fontWeight: 600, color: dd <= 3 ? "#dc2626" : "#374151", background: T.faint, padding: "6px 13px", borderRadius: 20 }}>{dd === 0 ? "Closes today" : `${dd}d left`}</span>}
                                    {bidded && <span style={{ fontSize: 12, fontWeight: 700, color: "#15803d", background: "rgba(21,128,61,0.08)", padding: "6px 13px", borderRadius: 20, border: "1px solid rgba(21,128,61,0.22)" }}>✓ Bid Placed</span>}
                                    <div style={{ flex: 1 }} />
                                    <button onClick={() => setDetailT(t)}
                                        style={{ padding: "7px 15px", border: `1px solid ${T.border}`, borderRadius: 20, background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "'DM Sans', sans-serif", transition: "all .15s" }}
                                        onMouseEnter={e => { e.currentTarget.style.background = T.faint; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                                        View Details
                                    </button>
                                    {!bidded
                                        ? <button onClick={() => openBidModal(t)}
                                            style={{ padding: "7px 17px", background: "#111827", border: "1px solid #111827", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#fff", fontFamily: "'DM Sans', sans-serif", transition: "all .15s" }}
                                            onMouseEnter={e => { e.currentTarget.style.background = "#1f2937"; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = "#111827"; }}>
                                            Submit Bid
                                        </button>
                                        : <button style={{ padding: "7px 17px", background: "#15803d", border: "1px solid #15803d", borderRadius: 20, cursor: "default", fontSize: 12, fontWeight: 600, color: "#fff", fontFamily: "'DM Sans', sans-serif", opacity: 0.92, display: "flex", alignItems: "center", gap: 5 }}>
                                            Bid placed
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                                        </button>
                                    }
                                </div>
                            </div>
                        </div>
                    );
                })}
                {!filtered.length && <Empty icon="🔍" title="No tenders found" sub="Try adjusting your search or filters." cta={<Btn variant="secondary" onClick={() => { setSearch(""); setCat("all"); setFilters(DEFAULT_FILTERS); }}>Reset</Btn>} />}
            </div>

            {/* Pagination */}
            {filtered.length > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, flexWrap: "wrap", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                            style={{ width: 32, height: 32, border: `1px solid ${T.border}`, borderRadius: 7, background: T.card, cursor: currentPage === 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: currentPage === 1 ? 0.4 : 1 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1).reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                            acc.push(p); return acc;
                        }, []).map((p, i) => p === "..." ? (
                            <span key={`e${i}`} style={{ padding: "0 6px", color: T.muted, fontSize: 13 }}>…</span>
                        ) : (
                            <button key={p} onClick={() => setCurrentPage(p)}
                                style={{ width: 32, height: 32, border: `1px solid ${p === currentPage ? T.dark : T.border}`, borderRadius: 7, background: p === currentPage ? T.dark : T.card, color: p === currentPage ? "#fff" : T.text, cursor: "pointer", fontSize: 13, fontWeight: p === currentPage ? 700 : 400, transition: "all .15s" }}>
                                {p}
                            </button>
                        ))}
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                            style={{ width: 32, height: 32, border: `1px solid ${T.border}`, borderRadius: 7, background: T.card, cursor: currentPage === totalPages ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: currentPage === totalPages ? 0.4 : 1 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, color: T.muted }}>{filtered.length} results</span>
                        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                            style={{ padding: "5px 10px", border: `1px solid ${T.border}`, borderRadius: 7, fontSize: 12, color: T.text, background: T.card, outline: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} per page</option>)}
                        </select>
                    </div>
                </div>
            )}

            {/* ── FULL DETAIL MODAL ────────────────────────────────────────────── */}
            {detailT && (
                <Modal title={detailT.title} subtitle={detailT.dept || ""} onClose={() => setDetailT(null)} maxWidth={860}>
                    <TenderDetailView t={detailT} bidCount={bidCount(detailT.id)} />
                    <div style={{ marginTop: 24, paddingTop: 18, borderTop: `1px solid ${T.border}`, display: "flex", gap: 10 }}>
                        {!alreadyBid(detailT.id)
                            ? <Btn variant="primary" style={{ flex: 1, padding: "13px", fontSize: 14, borderRadius: 10 }} onClick={() => { openBidModal(detailT); setDetailT(null); }}>Submit Your Bid →</Btn>
                            : <Alert type="success" style={{ flex: 1, margin: 0 }}>You have already submitted a bid for this tender.</Alert>
                        }
                    </div>
                </Modal>
            )}

            </div>

            {/* ── BID SUBMISSION PAGE — Two-Bid System ─────────────────────── */}
            {bidModal && (() => {
                const docs = (bidModal.documents_required || "").split("\n").map(s => s.trim()).filter(Boolean);
                const allDocsChecked = docs.length === 0 || docs.every(d => checkedDocs[d]);
                const hasFee = bidModal.bid_document_cost > 0;
                const steps = [
                    { n: 1, label: "Technical Bid" },
                    { n: 2, label: "Financial Bid" },
                    { n: 3, label: "Declaration" },
                    ...(hasFee ? [{ n: 4, label: "Payment" }] : []),
                ];
                return (
                    <BidPage title="Bid Submission — Two-Bid System" subtitle={bidModal.title} onClose={() => setBidModal(null)}>

                        {/* Step indicator */}
                        <div style={{ display: "flex", gap: 0, marginBottom: 28, borderRadius: 10, overflow: "hidden", border: `1px solid ${T.border}` }}>
                            {steps.map((s, i) => (
                                <div key={s.n} onClick={() => bidStep > s.n && setBidStep(s.n)} style={{ flex: 1, padding: "12px 10px", textAlign: "center", background: bidStep === s.n ? T.accent : bidStep > s.n ? T.accentLo : "#f8fafc", cursor: bidStep > s.n ? "pointer" : "default", borderRight: i < steps.length - 1 ? `1px solid ${T.border}` : "none", transition: "all .2s" }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: bidStep === s.n ? "#fff" : bidStep > s.n ? T.accent : T.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                        {bidStep > s.n ? "✓ " : ""}{s.label}
                                    </div>
                                    <div style={{ fontSize: 9, color: bidStep === s.n ? "rgba(255,255,255,0.7)" : T.muted, marginTop: 2 }}>Step {s.n} of {steps.length}</div>
                                </div>
                            ))}
                        </div>

                        {/* Tender summary bar */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, background: T.faint, borderRadius: 10, padding: 14, marginBottom: 20, border: `1px solid ${T.border}` }}>
                            {[["Est. Value", fmtFull(bidModal.value), T.accent], ["EMD", bidModal.emd_amount ? fmtFull(bidModal.emd_amount) : "N/A", T.gold], ["Deadline", fmtDate(bidModal.deadline), T.text], ["Days Left", daysDiff(bidModal.deadline) + "d", daysDiff(bidModal.deadline) <= 7 ? T.red : T.green]].map(([l, v, c]) => (
                                <div key={l}><div style={{ fontSize: 9, color: T.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{l}</div><div style={{ fontSize: 13, fontWeight: 800, color: c, fontFamily: "'DM Sans', sans-serif" }}>{v}</div></div>
                            ))}
                        </div>

                        {/* ── STEP 1: TECHNICAL BID ── */}
                        {bidStep === 1 && (
                            <div style={{ animation: "fadeUp .2s ease" }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Technical Bid</div>
                                <div style={{ fontSize: 12, color: T.muted, marginBottom: 20, lineHeight: 1.6 }}>Confirm you have all required documents ready for submission. Tick each document to proceed. Physical copies must be submitted to the tender office within 48 hours of online submission.</div>

                                {/* Bidder identity */}
                                <div style={{ display: "flex", gap: 12, alignItems: "center", background: T.faint, borderRadius: 10, padding: "12px 14px", marginBottom: 20, border: `1px solid ${T.border}` }}>
                                    <Avatar name={user.name} size={40} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{user.name}</div>
                                        <div style={{ fontSize: 11, color: T.muted }}>{user.company}</div>
                                        {user.regNo && user.regNo !== "—" && <div style={{ fontSize: 10, color: T.accent, fontFamily: "'DM Sans', sans-serif", marginTop: 2 }}>Reg: {user.regNo}</div>}
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: T.accentLo, color: T.accent, fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.08em", border: `1px solid ${T.accent}25` }}>
                                            Bidder
                                        </span>
                                    </div>
                                </div>

                                {/* Document Upload Section */}
                                {docs.length > 0 && (
                                    <div style={{ marginBottom: 20 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                            <div style={{ fontSize: 10, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                                                Required Documents ({Object.keys(uploadedFiles).length}/{docs.length} uploaded)
                                            </div>
                                            <span style={{ fontSize: 10, color: T.muted }}>Upload each document as PDF/image</span>
                                        </div>
                                        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                                            {docs.map((doc, i) => {
                                                const file = uploadedFiles[doc];
                                                const hasFile = !!file;
                                                const fmt_size = s => s < 1024 ? s + " B" : s < 1048576 ? (s / 1024).toFixed(1) + " KB" : (s / 1048576).toFixed(1) + " MB";
                                                const vld = docValidation[doc]; // { status, message, suggestion }

                                                // Determine row background based on validation status (theme palette)
                                                const rowBg = !hasFile ? T.card
                                                    : vld?.status === "validating" ? "rgba(37,99,235,0.05)"
                                                        : vld?.status === "valid" ? "rgba(21,128,61,0.06)"
                                                            : vld?.status === "warning" ? "rgba(133,77,14,0.06)"
                                                                : vld?.status === "invalid" ? "rgba(220,38,38,0.06)"
                                                                    : "rgba(21,128,61,0.06)";

                                                // Left-side icon color
                                                const iconBorder = !hasFile ? T.border
                                                    : vld?.status === "validating" ? T.accent
                                                        : vld?.status === "valid" ? T.green
                                                            : vld?.status === "warning" ? T.gold
                                                                : vld?.status === "invalid" ? T.red
                                                                    : T.green;

                                                const iconBg = !hasFile ? "transparent"
                                                    : vld?.status === "validating" ? "rgba(37,99,235,0.15)"
                                                        : vld?.status === "valid" ? T.green
                                                            : vld?.status === "warning" ? T.gold
                                                                : vld?.status === "invalid" ? T.red
                                                                    : T.green;

                                                return (
                                                    <div key={i} style={{ borderBottom: i < docs.length - 1 ? `1px solid ${T.border}` : "none", background: rowBg, transition: "background .15s" }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}>
                                                            {/* Status icon */}
                                                            <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${iconBorder}`, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .15s" }}>
                                                                {!hasFile && <span style={{ color: T.muted, fontSize: 12, fontWeight: 700, lineHeight: 1 }}>+</span>}
                                                                {hasFile && vld?.status === "validating" && (
                                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="3" strokeLinecap="round" style={{ animation: "spin 0.8s linear infinite" }}>
                                                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                            {/* Doc name + file info */}
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: 12, color: !hasFile ? T.text : vld?.status === "invalid" ? T.red : vld?.status === "warning" ? T.gold : T.green, fontWeight: hasFile ? 600 : 400, lineHeight: 1.4, transition: "color .15s" }}>
                                                                    {doc}
                                                                    {vld?.status === "validating" && <span style={{ fontSize: 10, color: T.accent, fontWeight: 500, marginLeft: 8 }}>Verifying…</span>}
                                                                </div>
                                                                {hasFile && (
                                                                    <div style={{ fontSize: 10, marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                                        <span style={{ color: T.muted }}>{file.name}</span>
                                                                        <span style={{ color: T.muted }}>({fmt_size(file.size)})</span>
                                                                        <button onClick={() => {
                                                                            setUploadedFiles(p => { const n = { ...p }; delete n[doc]; return n; });
                                                                            setCheckedDocs(p => { const n = { ...p }; delete n[doc]; return n; });
                                                                            setDocValidation(p => { const n = { ...p }; delete n[doc]; return n; });
                                                                        }} style={{ background: "none", border: "none", cursor: "pointer", color: T.red, fontSize: 11, padding: "0 2px", fontFamily: "'DM Sans',sans-serif" }}>✕ Remove</button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {/* Upload button */}
                                                            <label style={{ cursor: "pointer", flexShrink: 0 }}>
                                                                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" style={{ display: "none" }}
                                                                    onChange={async e => {
                                                                        const f = e.target.files?.[0];
                                                                        if (!f) return;
                                                                        if (f.size > 10 * 1024 * 1024) { toast("File size must be under 10 MB.", "error"); return; }
                                                                        setUploadedFiles(p => ({ ...p, [doc]: f }));
                                                                        setCheckedDocs(p => ({ ...p, [doc]: true }));
                                                                        e.target.value = "";
                                                                        // Trigger AI validation
                                                                        setDocValidation(p => ({ ...p, [doc]: { status: "validating", message: "", suggestion: "" } }));
                                                                        try {
                                                                            const result = await validateDocumentWithAI(doc, f);
                                                                            if (!result) {
                                                                                setDocValidation(p => ({ ...p, [doc]: { status: "warning", message: "Could not verify this document automatically. Please ensure it is the correct file.", suggestion: "" } }));
                                                                            } else {
                                                                                const status = result.valid
                                                                                    ? (result.confidence === "high" ? "valid" : "warning")
                                                                                    : "invalid";
                                                                                setDocValidation(p => ({ ...p, [doc]: { status, message: result.message || "", suggestion: result.suggestion || "" } }));
                                                                            }
                                                                        } catch (err) {
                                                                            console.error("Validation handler error:", err);
                                                                            setDocValidation(p => ({ ...p, [doc]: { status: "warning", message: "Verification could not complete. Please double-check the document before submitting.", suggestion: "" } }));
                                                                        }
                                                                    }}
                                                                />
                                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", border: `1px solid ${!hasFile ? T.border : vld?.status === "invalid" ? T.red : vld?.status === "warning" ? T.gold : T.green}`, borderRadius: 7, fontSize: 11, fontWeight: 600, color: !hasFile ? T.accent : vld?.status === "invalid" ? T.red : vld?.status === "warning" ? T.gold : T.green, background: !hasFile ? T.accentLo : vld?.status === "invalid" ? "rgba(220,38,38,0.07)" : vld?.status === "warning" ? "rgba(133,77,14,0.08)" : "rgba(21,128,61,0.08)", transition: "all .15s", whiteSpace: "nowrap" }}>
                                                                    {!hasFile ? "Choose File" : vld?.status === "invalid" ? "Re-upload" : "Uploaded"}
                                                                </span>
                                                            </label>
                                                        </div>
                                                        {/* AI validation message banner */}
                                                        {hasFile && vld && vld.status !== "validating" && vld.message && (
                                                            <div style={{
                                                                margin: "0 14px 10px",
                                                                padding: "8px 12px",
                                                                borderRadius: 8,
                                                                fontSize: 11,
                                                                lineHeight: 1.6,
                                                                fontWeight: 500,
                                                                display: "block",
                                                                background: vld.status === "invalid" ? "rgba(220,38,38,0.07)" : vld.status === "warning" ? "rgba(133,77,14,0.07)" : "rgba(21,128,61,0.07)",
                                                                border: `1px solid ${vld.status === "invalid" ? "rgba(220,38,38,0.22)" : vld.status === "warning" ? "rgba(133,77,14,0.22)" : "rgba(21,128,61,0.22)"}`,
                                                                color: vld.status === "invalid" ? T.red : vld.status === "warning" ? T.gold : T.green,
                                                            }}>
                                                                <div>{vld.message}</div>
                                                                {vld.suggestion && <div style={{ marginTop: 3, opacity: 0.85 }}>{vld.suggestion}</div>}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {/* Summary footer */}
                                        {(() => {
                                            const invalidCount = docs.filter(d => docValidation[d]?.status === "invalid").length;
                                            const validatingCount = docs.filter(d => docValidation[d]?.status === "validating").length;
                                            const warnCount = docs.filter(d => docValidation[d]?.status === "warning").length;
                                            if (invalidCount > 0) return (
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, flexWrap: "wrap", gap: 6 }}>
                                                    <div style={{ fontSize: 11, color: T.red, fontWeight: 700 }}>{invalidCount} document{invalidCount > 1 ? "s" : ""} flagged. Please re-upload the correct file{invalidCount > 1 ? "s" : ""}.</div>
                                                    <div style={{ fontSize: 10, color: T.muted }}>Max 10 MB · PDF, JPG, PNG, DOC, XLS</div>
                                                </div>
                                            );
                                            if (validatingCount > 0) return (
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                                                    <div style={{ fontSize: 11, color: T.accent, fontWeight: 600 }}>Verifying {validatingCount} document{validatingCount > 1 ? "s" : ""}…</div>
                                                    <div style={{ fontSize: 10, color: T.muted }}>Max 10 MB · PDF, JPG, PNG, DOC, XLS</div>
                                                </div>
                                            );
                                            if (!allDocsChecked) return (
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                                                    <div style={{ fontSize: 11, color: T.gold, fontWeight: 600 }}>Upload all required documents before proceeding.</div>
                                                    <div style={{ fontSize: 10, color: T.muted }}>Max 10 MB · PDF, JPG, PNG, DOC, XLS</div>
                                                </div>
                                            );
                                            if (warnCount > 0) return (
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, flexWrap: "wrap", gap: 6 }}>
                                                    <div style={{ fontSize: 11, color: T.gold, fontWeight: 600 }}>{warnCount} document{warnCount > 1 ? "s" : ""} may need review. Check the notes above before proceeding.</div>
                                                    <div style={{ fontSize: 10, color: T.muted }}>Max 10 MB · PDF, JPG, PNG, DOC, XLS</div>
                                                </div>
                                            );
                                            return (
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                                                    <div style={{ fontSize: 11, color: T.green, fontWeight: 600 }}>All documents checked. You may proceed.</div>
                                                    <div style={{ fontSize: 10, color: T.muted }}>Max 10 MB · PDF, JPG, PNG, DOC, XLS</div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                <Input label="Years of Experience" required type="number" min="0" suffix="yrs"
                                    placeholder="e.g. 6"
                                    value={expYears} onChange={e => setExpYears(e.target.value)}
                                    hint="How many years has your company been doing this type of work?"
                                />

                                <Input label="Company Experience & Past Projects" required textarea rows={4}
                                    placeholder="Describe your company and 2–3 relevant completed projects: client/location, scope, value, and year. e.g. 'Wired the 3-storey ABC Complex (Rs 18L, 2024) for XYZ Builders; …'"
                                    value={pastProjects} onChange={e => setPastProjects(e.target.value)}
                                    hint="The AI weighs your experience and past projects in the bid score, so be specific."
                                />

                                <Input label="Technical Proposal / Cover Note" textarea rows={5}
                                    placeholder="Your approach to THIS project: methodology, key personnel, plant & machinery, quality systems, and how you'll complete it on time…"
                                    value={bidMsg} onChange={e => setBidMsg(e.target.value)}
                                />

                                {bidModal.emd_amount && (
                                    <Input label="EMD Payment Reference Number" required
                                        placeholder="e.g. DD No. 123456 / BG No. BG-2026-00891"
                                        value={emdRef} onChange={e => setEmdRef(e.target.value)}
                                        hint={`EMD of ${fmtFull(bidModal.emd_amount)} must be paid via Demand Draft or Bank Guarantee before submitting`}
                                    />
                                )}

                                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                                    <Btn variant="secondary" onClick={() => setBidModal(null)} style={{ flex: 1 }}>Cancel</Btn>
                                    <Btn variant="primary" style={{ flex: 2 }} onClick={() => {
                                        if (docs.length > 0 && !allDocsChecked) { toast("Please upload all required documents.", "error"); return; }
                                        if (!expYears.trim()) { toast("Please enter your years of experience.", "error"); return; }
                                        if (!pastProjects.trim()) { toast("Please describe your company experience and past projects.", "error"); return; }
                                        if (bidModal.emd_amount && !emdRef.trim()) { toast("EMD Reference Number is required.", "error"); return; }
                                        const invalidDocs2 = docs.filter(d => docValidation[d]?.status === "invalid");
                                        if (invalidDocs2.length > 0) { toast(`⚠️ AI flagged ${invalidDocs2.length} document(s) as incorrect. Please re-upload: ${invalidDocs2.join(", ")}`, "error"); return; }
                                        const stillValidating = docs.filter(d => docValidation[d]?.status === "validating");
                                        if (stillValidating.length > 0) { toast("Please wait, your documents are still being checked.", "info"); return; }
                                        setBidStep(2);
                                    }}>Next: Financial Bid →</Btn>
                                </div>
                            </div>
                        )}

                        {/* ── STEP 2: FINANCIAL BID ── */}
                        {bidStep === 2 && (
                            <div style={{ animation: "fadeUp .2s ease" }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Financial Bid (Price Bid)</div>
                                <div style={{ fontSize: 12, color: T.muted, marginBottom: 20, lineHeight: 1.6 }}>Enter your total bid amount for the complete scope of work. This amount is final and binding. The bid with the lowest evaluated cost and qualifying technical score will be awarded the contract.</div>

                                {(bidModal.min_bid_value || bidModal.max_bid_value) && (
                                    <Alert type="info">Allowed Bid Range: {bidModal.min_bid_value ? `Min ${fmtFull(bidModal.min_bid_value)}` : ""}{bidModal.max_bid_value ? ` · Max ${fmtFull(bidModal.max_bid_value)}` : ""}</Alert>
                                )}

                                <Input label="Your Total Bid Amount (₹)" required type="number"
                                    placeholder={`Suggested: ₹${Math.round(bidModal.value * .95).toLocaleString("en-IN")}`}
                                    value={bidAmt} onChange={e => setBidAmt(e.target.value)}
                                    hint="Enter the complete contract value inclusive of all taxes, duties, and charges"
                                />

                                {bidAmt && !isNaN(bidAmt) && parseFloat(bidAmt) > 0 && (() => {
                                    const pct = ((parseFloat(bidAmt) / bidModal.value) * 100).toFixed(1);
                                    const below = parseFloat(bidAmt) < bidModal.value;
                                    const diff = below ? (100 - pct).toFixed(1) : (pct - 100).toFixed(1);
                                    return (
                                        <div style={{ marginBottom: 16 }}>
                                            <Alert type={below ? "success" : "warning"}>
                                                {below ? "✓" : "⚠"} Your bid is <strong>{pct}%</strong> of estimated value — <strong>{diff}% {below ? "below" : "above"} estimate</strong>
                                            </Alert>
                                            <div style={{ background: T.faint, borderRadius: 10, padding: 14, border: `1px solid ${T.border}`, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                                                <div><div style={{ fontSize: 9, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Your Bid</div><div style={{ fontSize: 15, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmtFull(parseFloat(bidAmt))}</div></div>
                                                <div><div style={{ fontSize: 9, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Estimate</div><div style={{ fontSize: 15, fontWeight: 800, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>{fmtFull(bidModal.value)}</div></div>
                                                <div><div style={{ fontSize: 9, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Difference</div><div style={{ fontSize: 15, fontWeight: 800, color: below ? T.green : T.red, fontFamily: "'DM Sans', sans-serif" }}>{below ? "-" : "+"}₹{Math.abs(parseFloat(bidAmt) - bidModal.value).toLocaleString("en-IN")}</div></div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {bidModal.performance_guarantee && (
                                    <Alert type="warning">Performance Guarantee of <strong>{bidModal.performance_guarantee}%</strong> of contract value (≈ <strong>{fmtFull(Math.round((parseFloat(bidAmt) || bidModal.value) * bidModal.performance_guarantee / 100))}</strong>) will be required within 7 days of award.</Alert>
                                )}

                                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                                    <Btn variant="secondary" onClick={() => setBidStep(1)} style={{ flex: 1 }}>← Back</Btn>
                                    <Btn variant="primary" style={{ flex: 2 }} onClick={() => {
                                        if (!bidAmt || parseFloat(bidAmt) <= 0) { toast("Enter a valid bid amount.", "error"); return; }
                                        setBidStep(3);
                                    }}>Next: Declaration →</Btn>
                                </div>
                            </div>
                        )}

                        {/* ── STEP 3: DECLARATION & SUBMIT ── */}
                        {bidStep === 3 && (
                            <div style={{ animation: "fadeUp .2s ease" }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Declaration & Final Submission</div>
                                <div style={{ fontSize: 12, color: T.muted, marginBottom: 20, lineHeight: 1.6 }}>Review your bid summary and sign the declaration before final submission.</div>

                                {/* Bid Summary */}
                                <div style={{ background: T.faint, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, marginBottom: 20 }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>Bid Summary</div>
                                    {[
                                        ["Tender", bidModal.title],
                                        ["Reference No.", bidModal.reference_no || "—"],
                                        ["Department", bidModal.dept || "—"],
                                        ["Bidding Company", user.company],
                                        ["Registration No.", user.regNo || "—"],
                                        ["Bid Amount", fmtFull(parseFloat(bidAmt) || 0)],
                                        ["vs Estimate", `${((parseFloat(bidAmt) || 0) / bidModal.value * 100).toFixed(1)}% of ₹${fmt(bidModal.value)}`],
                                        ["EMD Reference", emdRef || "N/A"],
                                        ["Documents Uploaded", `${Object.keys(uploadedFiles).length} of ${docs.length} files`],
                                    ].map(([l, v]) => (
                                        <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, paddingBottom: 8, marginBottom: 8, borderBottom: `1px solid ${T.border}` }}>
                                            <span style={{ color: T.muted, fontWeight: 500 }}>{l}</span>
                                            <span style={{ color: T.text, fontWeight: 700, textAlign: "right", maxWidth: "55%" }}>{v}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Declaration box */}
                                <label style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "16px", background: declared ? "rgba(34,197,94,0.06)" : "rgba(239,68,68,0.04)", border: `1px solid ${declared ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.2)"}`, borderRadius: 10, marginBottom: 20, cursor: "pointer", transition: "all .2s" }}>
                                    <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${declared ? T.green : T.red}`, background: declared ? T.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all .2s" }}>
                                        {declared && <span style={{ color: "#fff", fontSize: 11, fontWeight: 800 }}>✓</span>}
                                    </div>
                                    <input type="checkbox" checked={declared} onChange={e => setDeclared(e.target.checked)} style={{ display: "none" }} />
                                    <div style={{ fontSize: 12, color: T.text, lineHeight: 1.7 }}>
                                        I, <strong>{user.name}</strong> on behalf of <strong>{user.company}</strong>, hereby declare that:
                                        <ul style={{ margin: "8px 0 0 16px", paddingLeft: 0 }}>
                                            {["All information provided in this bid is true and correct to the best of my knowledge.",
                                                "My company meets all eligibility criteria specified in the tender document.",
                                                "All required documents listed are genuine and available for verification.",
                                                "EMD of the required amount has been paid and the reference provided is valid.",
                                                "My company has not been blacklisted by any authority.",
                                                "I understand that submission of false documents will result in immediate disqualification and legal action."].map(d => (
                                                    <li key={d} style={{ marginBottom: 5, color: T.muted }}>{d}</li>
                                                ))}
                                        </ul>
                                    </div>
                                </label>

                                {!declared && <Alert type="error">You must accept the declaration to continue.</Alert>}

                                <div style={{ display: "flex", gap: 10 }}>
                                    <Btn variant="secondary" onClick={() => setBidStep(2)} style={{ flex: 1 }}>← Back</Btn>
                                    <Btn variant="primary" onClick={proceedFromDeclaration} loading={submitting && !hasFee} disabled={!declared} style={{ flex: 2 }}>
                                        {hasFee ? `Proceed to Payment →` : "Submit Bid Officially"}
                                    </Btn>
                                </div>
                            </div>
                        )}

                        {bidStep === 4 && hasFee && (
                            <div>
                                <div style={{ textAlign: "center", marginBottom: 20 }}>
                                    <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Tender Document Fee</div>
                                    <div style={{ fontSize: 40, fontWeight: 800, color: T.text, fontFamily: "'DM Sans', sans-serif", margin: "4px 0 2px" }}>{fmtFull(bidModal.bid_document_cost)}</div>
                                    <div style={{ fontSize: 12, color: T.muted }}>Payable to file your bid documents for this tender.</div>
                                </div>

                                {/* Order summary */}
                                <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 18 }}>
                                    {[
                                        ["Tender", bidModal.title],
                                        ["Your Bid Amount", fmtFull(parseFloat(bidAmt) || 0)],
                                        ["Document Fee", fmtFull(bidModal.bid_document_cost)],
                                    ].map(([k, v], idx) => (
                                        <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderTop: idx ? `1px solid ${T.border}` : "none", background: idx === 2 ? T.faint : T.card }}>
                                            <span style={{ fontSize: 12, color: T.muted, fontWeight: idx === 2 ? 700 : 500 }}>{k}</span>
                                            <span style={{ fontSize: 13, color: T.text, fontWeight: idx === 2 ? 800 : 600, textAlign: "right" }}>{v}</span>
                                        </div>
                                    ))}
                                </div>

                                <div style={{
                                    padding: "12px 16px", borderRadius: 10, marginBottom: 18, textAlign: "center", fontSize: 12, fontWeight: 700,
                                    background: payState === "paid" ? "rgba(21,128,61,0.08)" : payState === "processing" ? T.accentLo : T.faint,
                                    color: payState === "paid" ? T.green : payState === "processing" ? T.accent : T.muted,
                                    border: `1px solid ${payState === "paid" ? T.green + "40" : payState === "processing" ? T.accentMid : T.border}`
                                }}>
                                    {payState === "paid" ? "Payment received" : payState === "processing" ? "Processing payment…" : "Demo payment — no real charge is made."}
                                </div>

                                <div style={{ display: "flex", gap: 10 }}>
                                    <Btn variant="secondary" onClick={() => setBidStep(3)} style={{ flex: 1 }} disabled={payState === "processing" || submitting}>← Back</Btn>
                                    <Btn variant="primary" onClick={startPayment} loading={payState === "processing"} disabled={payState === "paid"} style={{ flex: 2 }}>
                                        {payState === "processing" ? "Processing payment…"
                                            : payState === "paid" ? "Payment done — enter details"
                                                : `Pay ${fmtFull(bidModal.bid_document_cost)}`}
                                    </Btn>
                                </div>
                            </div>
                        )}
                    </BidPage>
                );
            })()}

            {/* Payment details form — bidder enters transaction info for admin review */}
            {showPayForm && bidModal && (
                <Modal title="Enter Payment Details" subtitle="The authority will verify this before confirming your payment." onClose={() => !submitting && setShowPayForm(false)} maxWidth={480}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ marginBottom: 6, padding: "10px 14px", background: T.faint, border: `1px solid ${T.border}`, borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>Amount paid</span>
                            <span style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{fmtFull(bidModal.bid_document_cost)}</span>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: T.text, display: "block", marginBottom: 6 }}>Transaction Type <span style={{ color: T.red }}>*</span></label>
                            <select value={payForm.type} onChange={e => setPayForm(f => ({ ...f, type: e.target.value }))}
                                style={{ width: "100%", padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: T.text, background: T.card, marginBottom: 12 }}>
                                <option value="">Select type…</option>
                                {["UPI", "NEFT", "RTGS", "IMPS", "Net Banking", "Debit/Credit Card", "Demand Draft", "Cash", "Other"].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                        <Input label="Transaction ID / Reference No." required placeholder="e.g. UPI ref, UTR, or DD number"
                            value={payForm.txnId} onChange={e => setPayForm(f => ({ ...f, txnId: e.target.value }))} />
                        <Input label="Date & Time of Payment" required placeholder="e.g. 04 Sept 2026, 3:20 PM"
                            value={payForm.time} onChange={e => setPayForm(f => ({ ...f, time: e.target.value }))} />
                        <Input label="Name of Payer" required placeholder="Name as on the paying account"
                            value={payForm.payerName} onChange={e => setPayForm(f => ({ ...f, payerName: e.target.value }))} />

                        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                            <Btn variant="secondary" onClick={() => setShowPayForm(false)} style={{ flex: 1 }} disabled={submitting}>Cancel</Btn>
                            <Btn variant="primary" onClick={submitBid} loading={submitting} style={{ flex: 2 }}>Submit for Review</Btn>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Bid Sent confirmation popup */}
            {bidSent && (
                <Modal onClose={() => setBidSent(false)} maxWidth={420}>
                    <div style={{ textAlign: "center", padding: "12px 8px 4px" }}>
                        <div style={{ width: 68, height: 68, borderRadius: "50%", background: "rgba(21,128,61,0.10)", border: `2px solid ${T.green}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
                            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 8 }}>Bid Sent</div>
                        <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, marginBottom: 22 }}>
                            {payUnderReview
                                ? "Your bid has been submitted. Your payment details are now under review by the tendering authority — you'll see it marked as received once verified."
                                : "Your bid has been submitted successfully and is now with the tendering authority for review."}
                        </div>
                        <Btn variant="primary" onClick={() => { setBidSent(false); setPage && setPage("mybids"); }} style={{ width: "100%" }}>Done</Btn>
                    </div>
                </Modal>
            )}
        </div>
    );

};

// ─────────────────────────────────────────────────────────────────────────────
// BIDDER: MY BIDS
// ─────────────────────────────────────────────────────────────────────────────

const BidderMyBids = ({ bids, setBids, tenders, user, toast }) => {
    const { confirm, Dialog } = useConfirm();
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [cat, setCat] = useState("all");
    const [sort, setSort] = useState("newest");
    const [withdrawing, setWd] = useState(null);
    const [selectedBid, setSelectedBid] = useState(null);
    const [detailTab, setDetailTab] = useState("overview"); // "overview" | "documents"
    const [bidDocuments, setBidDocuments] = useState([]);   // uploaded files for selected bid
    const [docsLoading, setDocsLoading] = useState(false);

    const fetchBidDocs = useCallback(async (bidId) => {
        if (!bidId) return;
        setDocsLoading(true);
        const { data } = await supabase.from("bid_documents").select("*").eq("bid_id", bidId).order("uploaded_at", { ascending: true });
        setBidDocuments(data || []);
        setDocsLoading(false);
    }, []);

    // Auto-fetch documents whenever a bid modal is opened
    useEffect(() => {
        if (selectedBid) {
            fetchBidDocs(selectedBid.id);
        } else {
            setBidDocuments([]);
        }
    }, [selectedBid, fetchBidDocs]);

    // eslint-disable-next-line no-unused-vars
    const openDetail = (bid, tab = "overview") => {
        setSelectedBid(bid); setDetailTab(tab);
    };

    const enriched = useMemo(() => {
        let list = bids.map(b => ({ ...b, tender: tenders.find(t => t.id === b.tender_id) })).filter(b => b.tender);
        if (search) list = list.filter(b => b.tender?.title.toLowerCase().includes(search.toLowerCase()));
        if (status !== "all") list = list.filter(b => b.status === status);
        if (cat !== "all") list = list.filter(b => b.tender?.category === cat);
        return [...list].sort((a, b) => {
            if (sort === "newest") return new Date(b.submitted_at) - new Date(a.submitted_at);
            if (sort === "value_desc") return b.amount - a.amount;
            if (sort === "deadline") return new Date(a.tender?.deadline || 0) - new Date(b.tender?.deadline || 0);
            return 0;
        });
    }, [bids, tenders, search, status, cat, sort]);

    const withdrawBid = async bid => {
        const ok = await confirm("Withdraw this bid? This action cannot be undone.", "Withdraw Bid?");
        if (!ok) return;
        setWd(bid.id);
        const { error } = await supabase.from("bids").update({ status: "withdrawn" }).eq("id", bid.id);
        setWd(null);
        if (error) { toast("Failed to withdraw bid.", "error"); return; }
        // Update parent state in-place so all stats sync immediately
        if (setBids) setBids(prev => prev.map(b => b.id === bid.id ? { ...b, status: "withdrawn" } : b));
        toast("Bid withdrawn.", "info");
    };

    const awarded = bids.filter(b => b.status === "awarded").length;
    const submitted = bids.filter(b => b.status === "submitted").length;
    const withdrawn = bids.filter(b => b.status === "withdrawn").length;
    const totalVal = bids.filter(b => b.status !== "withdrawn").reduce((a, b) => a + b.amount, 0);


    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            {Dialog}

            {/* ── BID DETAIL MODAL ──────────────────────────────────────────────── */}
            {selectedBid && (() => {
                const b = selectedBid;
                const docs = (b.tender.documents_required || "").split("\n").map(s => s.trim()).filter(Boolean);
                const pct = ((b.amount / b.tender.value) * 100).toFixed(1);
                const below = b.amount <= b.tender.value;
                const { experience, projects, proposal, emdRef } = parseBidMessage(b.message);
                // eslint-disable-next-line no-unused-vars
                const tabs = [["overview", "📋 Overview"], ["documents", `📁 Documents${bidDocuments.length > 0 ? " (" + bidDocuments.length + ")" : docs.length ? " (" + docs.length + " req.)" : ""}`]];
                return (
                    <Modal
                        title={b.tender.title}
                        subtitle={`Bid submitted ${fmtDate(b.submitted_at)} · ${b.tender.dept}`}
                        onClose={() => { setSelectedBid(null); setDetailTab("overview"); }}
                        maxWidth={720}
                    >
                        {/* Status bar */}
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
                            <Badge status={b.status} />
                            <CatBadge cat={b.tender.category} />
                            {b.tender.reference_no && <span style={{ fontSize: 10, color: T.accent, fontFamily: "'DM Sans', sans-serif", background: T.accentLo, padding: "3px 9px", borderRadius: 20, border: `1px solid ${T.border}`, fontWeight: 700 }}>Ref: {b.tender.reference_no}</span>}
                            {bidDocuments.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: bidDocuments.some(d => d.status === "pending") ? "rgba(217,119,6,0.1)" : bidDocuments.every(d => d.status === "approved") ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)", color: bidDocuments.some(d => d.status === "pending") ? "#b45309" : bidDocuments.every(d => d.status === "approved") ? T.green : T.red, padding: "3px 9px", borderRadius: 20, border: `1px solid currentColor` }}>{bidDocuments.filter(d => d.status === "approved").length}/{bidDocuments.length} docs approved</span>}
                            {b.payment_status && (() => {
                                const map = { pending: [T.gold, "Payment under review"], approved: [T.green, "Payment received"], rejected: [T.red, "Payment rejected"] };
                                const [c, lbl] = map[b.payment_status] || map.pending;
                                return <span style={{ fontSize: 10, fontWeight: 700, color: c, background: `${c}15`, padding: "3px 9px", borderRadius: 20, border: `1px solid ${c}40` }}>{lbl}</span>;
                            })()}
                        </div>

                        {/* Financial summary card */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
                            {[{ l: "Your Bid", v: fmtFull(b.amount), c: T.accent, mono: true },
                            { l: "Estimate", v: fmtFull(b.tender.value), c: T.muted, mono: true },
                            { l: "vs Estimate", v: `${below ? "-" : "+"}${Math.abs(parseFloat(pct) - 100).toFixed(1)}%`, c: below ? T.green : T.red, mono: false }].map(item => (
                                <div key={item.l} style={{ background: T.faint, borderRadius: 10, padding: "14px 16px", border: `1px solid ${T.border}` }}>
                                    <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{item.l}</div>
                                    <div style={{ fontSize: 16, fontWeight: 800, color: item.c, fontFamily: "'DM Sans', sans-serif" }}>{item.v}</div>
                                </div>
                            ))}
                        </div>

                        {/* Key dates */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
                            {[["Submitted On", fmtDate(b.submitted_at), T.muted],
                            ["Tender Deadline", fmtDate(b.tender.deadline), daysDiff(b.tender.deadline) <= 7 ? T.red : T.muted],
                            ["Tender Status", b.tender.status?.toUpperCase(), b.tender.status === "active" ? T.green : T.muted]].map(([l, v, c]) => (
                                <div key={l} style={{ background: T.faint, borderRadius: 10, padding: "12px 14px", border: `1px solid ${T.border}` }}>
                                    <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5 }}>{l}</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "'DM Sans', sans-serif" }}>{v}</div>
                                </div>
                            ))}
                        </div>

                        {/* Status banner */}
                        {b.status === "awarded" && <div style={{ marginBottom: 16, padding: "12px 16px", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 10, fontSize: 13, color: T.green, fontWeight: 600 }}>🎉 Congratulations! Your bid was awarded the contract.</div>}
                        {b.status === "rejected" && <div style={{ marginBottom: 16, padding: "12px 16px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, fontSize: 13, color: "#f87171" }}>This bid was not selected. A different bid was awarded for this tender.</div>}

                        {/* Tabs */}
                        <div style={{ display: "flex", gap: 2, marginBottom: 18, padding: "3px", background: "#eef0f3", borderRadius: 9, width: "fit-content" }}>
                            {[["overview", "📋 Overview"], ["documents", `📁 Documents${bidDocuments.length > 0 ? " (" + bidDocuments.length + ")" : docs.length ? " (" + docs.length + " req.)" : ""}`]].map(([key, label]) => (
                                <button key={key} onClick={() => { setDetailTab(key); if (key === "documents") fetchBidDocs(b.id); }}
                                    style={{ padding: "6px 16px", borderRadius: 9, border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: detailTab === key ? 600 : 500, background: detailTab === key ? T.accent : "transparent", color: detailTab === key ? "#fff" : T.muted, transition: "all .15s" }}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* ── Overview Tab ── */}
                        {detailTab === "overview" && (
                            <div style={{ animation: "fadeUp .2s ease" }}>
                                {emdRef && (
                                    <div style={{ marginBottom: 14, background: "rgba(29,78,216,0.05)", border: "1px solid rgba(29,78,216,0.15)", borderRadius: 10, padding: "12px 16px" }}>
                                        <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5 }}>EMD Payment Reference</div>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{emdRef}</div>
                                    </div>
                                )}
                                {(experience || projects) && (
                                    <div style={{ marginBottom: 14, background: T.faint, borderRadius: 10, padding: "14px 16px", border: `1px solid ${T.border}` }}>
                                        <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Experience & Past Projects</div>
                                        {experience && <div style={{ fontSize: 13, color: T.text, marginBottom: projects ? 6 : 0 }}><span style={{ color: T.muted }}>Years of experience: </span><strong>{experience}</strong></div>}
                                        {projects && <p style={{ margin: 0, fontSize: 13, color: T.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{projects}</p>}
                                    </div>
                                )}
                                {proposal ? (
                                    <div style={{ background: T.faint, borderRadius: 10, padding: "14px 16px", border: `1px solid ${T.border}` }}>
                                        <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Technical Proposal / Cover Note</div>
                                        <p style={{ margin: 0, fontSize: 13, color: T.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{proposal}</p>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: "center", padding: "32px 20px", background: T.faint, borderRadius: 10, border: `1px dashed ${T.border}`, color: T.muted, fontSize: 13 }}>No technical proposal was submitted with this bid.</div>
                                )}
                            </div>
                        )}

                        {/* ── Documents Tab ── */}
                        {detailTab === "documents" && (
                            <div style={{ animation: "fadeUp .2s ease" }}>
                                {docsLoading ? (
                                    <div style={{ textAlign: "center", padding: 40, color: T.muted, fontSize: 13 }}>
                                        <div style={{ width: 24, height: 24, border: `2.5px solid ${T.border}`, borderTop: `2.5px solid ${T.accent}`, borderRadius: "50%", animation: "spin .75s linear infinite", margin: "0 auto 12px" }} />
                                        Loading uploaded documents…
                                    </div>
                                ) : bidDocuments.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "40px 20px", background: T.faint, borderRadius: 10, border: `1px dashed ${T.border}` }}>
                                        <div style={{ fontSize: 32, marginBottom: 10 }}>📂</div>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                                            {docs.length === 0 ? "No documents required" : "No files uploaded yet"}
                                        </div>
                                        <div style={{ fontSize: 12, color: T.muted }}>
                                            {docs.length === 0 ? "This tender had no mandatory document checklist." : "Documents required but no files were uploaded with this bid."}
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        {/* Summary bar */}
                                        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                                            {[
                                                ["Total", bidDocuments.length, T.accent],
                                                ["Approved", bidDocuments.filter(d => d.status === "approved").length, T.green],
                                                ["Pending Review", bidDocuments.filter(d => d.status === "pending").length, "#b45309"],
                                                ["Rejected", bidDocuments.filter(d => d.status === "rejected").length, T.red],
                                            ].filter(([, v]) => v > 0).map(([l, v, c]) => (
                                                <span key={l} style={{ fontSize: 11, fontWeight: 700, color: c, background: `${c}15`, padding: "3px 10px", borderRadius: 20, border: `1px solid ${c}30` }}>{v} {l}</span>
                                            ))}
                                        </div>
                                        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
                                            {bidDocuments.map((doc, i) => {
                                                const STATUS_DOC = {
                                                    pending: { icon: "⏳", bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.25)", color: "#b45309", label: "Pending Review" },
                                                    approved: { icon: "✓", bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.25)", color: T.green, label: "Approved" },
                                                    rejected: { icon: "✕", bg: "rgba(239,68,68,0.06)", border: "rgba(239,68,68,0.20)", color: T.red, label: "Rejected" },
                                                };
                                                const s = STATUS_DOC[doc.status] || STATUS_DOC.pending;
                                                const fmtSz = n => n < 1024 ? n + " B" : n < 1048576 ? (n / 1024).toFixed(1) + " KB" : (n / 1048576).toFixed(1) + " MB";
                                                return (
                                                    <div key={doc.id} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 18px", borderBottom: i < bidDocuments.length - 1 ? `1px solid ${T.border}` : "none", background: s.bg, transition: "background .15s" }}>
                                                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "transparent", border: `1.5px solid ${s.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                                                            <span style={{ color: s.color, fontSize: 14, fontWeight: 800 }}>{s.icon}</span>
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 2 }}>{doc.doc_name}</div>
                                                            <div style={{ fontSize: 11, color: T.muted, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                                <span>📄 {doc.file_name}</span>
                                                                {doc.file_size && <span>({fmtSz(doc.file_size)})</span>}
                                                                <span>· {fmtDate(doc.uploaded_at)}</span>
                                                            </div>
                                                            {doc.admin_note && (
                                                                <div style={{ marginTop: 6, fontSize: 11, color: doc.status === "rejected" ? T.red : T.muted, background: doc.status === "rejected" ? "rgba(239,68,68,0.06)" : T.faint, border: `1px solid ${doc.status === "rejected" ? "rgba(239,68,68,0.2)" : T.border}`, borderRadius: 6, padding: "5px 9px", fontStyle: "italic" }}>
                                                                    Admin note: "{doc.admin_note}"
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: s.color, background: `${s.color}15`, padding: "3px 9px", borderRadius: 20, border: `1px solid ${s.border}`, whiteSpace: "nowrap" }}>{s.label}</span>
                                                            <a href={doc.file_url} target="_blank" rel="noreferrer"
                                                                style={{ fontSize: 11, fontWeight: 600, color: T.accent, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.card }}>
                                                                ↗ View File
                                                            </a>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Withdraw action */}
                        {b.status === "submitted" && b.tender.status === "active" && (
                            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end" }}>
                                <Btn variant="ghost" style={{ color: T.red }} onClick={() => { setSelectedBid(null); withdrawBid(b); }}>↩ Withdraw Bid</Btn>
                            </div>
                        )}
                    </Modal>
                );
            })()}
            <PageHeader title="My Bids" sub="Track and manage all your submitted bids" />

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 22 }}>
                <Stat label="Submitted" value={submitted} color={T.accent} />
                <Stat label="Awarded" value={awarded} color={T.green} />
                <Stat label="Withdrawn" value={withdrawn} color={T.muted} />
                <Stat label="Bid Value" value={fmt(totalVal)} color={T.accent} />
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search by tender title…" />
                {[{ val: status, set: setStatus, opts: [["all", "All Status"], ["submitted", "Submitted"], ["awarded", "Awarded"], ["rejected", "Rejected"], ["withdrawn", "Withdrawn"]] }, { val: sort, set: setSort, opts: [["newest", "Newest"], ["value_desc", "Highest"], ["deadline", "Deadline"]] }].map((sel, i) => (
                    <select key={i} value={sel.val} onChange={e => sel.set(e.target.value)} style={{ padding: "9px 12px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 12, outline: "none", color: T.text, fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
                        {sel.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                ))}
            </div>
            <CatPills selected={cat} onChange={setCat} />

            {!enriched.length ? <Empty icon="📋" title={bids.length === 0 ? "No bids yet" : "No bids match filters"} sub={bids.length === 0 ? "Browse active tenders and submit your first bid." : "Try clearing filters."} /> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {enriched.map(b => {
                        const pct = ((b.amount / b.tender.value) * 100).toFixed(1);
                        const borderColor = b.status === "awarded" ? "rgba(34,197,94,0.25)" : b.status === "rejected" ? "rgba(239,68,68,0.2)" : T.border;
                        const docs = (b.tender.documents_required || "").split("\n").map(s => s.trim()).filter(Boolean);
                        return (
                            <div key={b.id} className="card-hover" onClick={() => { setSelectedBid(b); setDetailTab("overview"); }}
                                style={{ background: T.card, border: `1px solid ${borderColor}`, borderRadius: 14, overflow: "hidden", cursor: "pointer" }}>
                                <div style={{ padding: "16px 20px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                                                <Badge status={b.status} />
                                                <CatBadge cat={b.tender.category} />
                                                {b.tender.status === "active" && b.status === "submitted" && daysDiff(b.tender.deadline) <= 7 && daysDiff(b.tender.deadline) >= 0 && <span style={{ fontSize: 10, fontWeight: 800, background: "rgba(245,158,11,0.12)", color: "#fbbf24", padding: "3px 8px", borderRadius: 20, border: "1px solid rgba(245,158,11,0.2)", textTransform: "uppercase" }}>⚠ Closing</span>}
                                            </div>
                                            {b.tender.reference_no && <div style={{ fontSize: 10, color: T.accent, fontFamily: "'DM Sans', sans-serif", marginBottom: 3 }}>Ref: {b.tender.reference_no}</div>}
                                            <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: T.text }}>{b.tender.title}</h3>
                                            <div style={{ fontSize: 11, color: T.muted }}>{b.tender.dept}{b.tender.location && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}> · <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M12 21c-4-4-7-8-7-12a7 7 0 0 1 14 0c0 4-3 8-7 12z" /><circle cx="12" cy="9" r="2" /></svg>{b.tender.location}</span>}</div>
                                            <div style={{ fontSize: 10, color: T.muted, marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>
                                                Submitted: {fmtDate(b.submitted_at)} · Closes: <strong style={{ color: daysDiff(b.tender.deadline) <= 7 ? T.red : T.muted }}>{fmtDate(b.tender.deadline)}</strong>
                                            </div>
                                            {b.message && <div style={{ fontSize: 11, color: T.muted, marginTop: 8, fontStyle: "italic", background: T.faint, padding: "6px 10px", borderRadius: 9, borderLeft: `2px solid ${T.border}` }}>"{b.message}"</div>}
                                        </div>
                                        <div style={{ textAlign: "right", marginLeft: 20, flexShrink: 0 }}>
                                            <div style={{ fontSize: 10, color: T.muted, marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.08em" }}>Your Bid</div>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(b.amount)}</div>
                                            <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>Est: {fmt(b.tender.value)}</div>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: b.amount <= b.tender.value ? T.green : T.red, marginTop: 2 }}>{pct}% of estimate</div>
                                        </div>
                                    </div>
                                    {b.status === "awarded" && <div style={{ marginTop: 10, padding: "10px 14px", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 8, fontSize: 12, color: T.green, fontWeight: 600 }}>🎉 Congratulations! Your bid was selected and awarded the contract.</div>}
                                    {b.status === "rejected" && <div style={{ marginTop: 10, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#f87171" }}>This bid was not selected. A different bid was awarded for this tender.</div>}
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                        <Btn size="sm" variant="secondary" onClick={e => { e.stopPropagation(); setSelectedBid(b); setDetailTab("overview"); }} icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" /></svg>}>View Details</Btn>
                                        {docs.length > 0 && <Btn size="sm" variant="secondary" onClick={e => { e.stopPropagation(); setSelectedBid(b); setDetailTab("documents"); }} icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>}>Documents ({docs.length})</Btn>}
                                        {b.status === "submitted" && b.tender.status === "active" && (
                                            <Btn size="sm" variant="ghost" style={{ color: T.red }} onClick={e => { e.stopPropagation(); withdrawBid(b); }} loading={withdrawing === b.id} icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>}>Withdraw</Btn>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// BIDDER: SAVED
// ─────────────────────────────────────────────────────────────────────────────

const BidderSaved = ({ tenders, bids, user, toast }) => {
    const [savedIds, setSavedIds] = useState(() => { try { return JSON.parse(localStorage.getItem("saved_tenders") || "[]"); } catch { return []; } });
    const [detailT, setDetailT] = useState(null);
    const saved = tenders.filter(t => savedIds.includes(t.id));
    const removeSave = id => { const next = savedIds.filter(x => x !== id); setSavedIds(next); localStorage.setItem("saved_tenders", JSON.stringify(next)); toast("Removed from saved.", "info"); };
    const alreadyBid = id => bids.some(b => b.tender_id === id && b.bidder_id === user.id);
    const bidCount = id => bids.filter(b => b.tender_id === id).length;

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            <PageHeader title="Saved Tenders" sub={`${saved.length} saved tender${saved.length !== 1 ? "s" : ""}`} />
            {!saved.length ? <Empty icon="🔖" title="No saved tenders" sub="Bookmark interesting tenders while browsing to see them here." /> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {saved.map(t => {
                        const dd = daysDiff(t.deadline);
                        return (
                            <div key={t.id} className="card-hover" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
                                <div style={{ padding: "16px 20px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                                                <Badge status={t.status} />
                                                <CatBadge cat={t.category} />
                                                {t.tender_type && <span style={{ fontSize: 10, color: T.muted, background: T.faint, padding: "2px 8px", borderRadius: 20, border: `1px solid ${T.border}`, fontWeight: 600 }}>{t.tender_type}</span>}
                                                {dd <= 7 && dd >= 0 && <span style={{ fontSize: 10, fontWeight: 800, background: "rgba(245,158,11,0.12)", color: "#fbbf24", padding: "3px 9px", borderRadius: 20, border: "1px solid rgba(245,158,11,0.2)", textTransform: "uppercase" }}>⚠ {dd}d left</span>}
                                                {alreadyBid(t.id) && <span style={{ fontSize: 10, fontWeight: 800, background: "rgba(34,197,94,0.12)", color: "#4ade80", padding: "3px 9px", borderRadius: 20, border: "1px solid rgba(34,197,94,0.2)", textTransform: "uppercase" }}>✓ Bid Placed</span>}
                                            </div>
                                            {t.reference_no && <div style={{ fontSize: 10, color: T.accent, fontFamily: "'DM Sans', sans-serif", marginBottom: 3 }}>Ref: {t.reference_no}</div>}
                                            <h3 style={{ margin: "0 0 5px", fontSize: 14, fontWeight: 700, color: T.text }}>{t.title}</h3>
                                            <div style={{ fontSize: 11, color: T.muted }}>{t.dept}{t.location && <span> · 📍{t.location}</span>} · Deadline: {fmtDate(t.deadline)} · {bidCount(t.id)} bid{bidCount(t.id) !== 1 ? "s" : ""}</div>
                                        </div>
                                        <div style={{ textAlign: "right", marginLeft: 20, flexShrink: 0 }}>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{fmt(t.value)}</div>
                                            {t.emd_amount && <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>EMD: {fmt(t.emd_amount)}</div>}
                                            <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                                                <Btn size="sm" variant="secondary" onClick={() => setDetailT(t)}>Details</Btn>
                                                <button onClick={() => removeSave(t.id)} style={{ padding: "5px 10px", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, background: "rgba(239,68,68,0.08)", color: "#f87171", fontSize: 11, cursor: "pointer", fontWeight: 700, fontFamily: "'DM Sans', -apple-system, sans-serif" }}>✕</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {detailT && (
                <Modal title={detailT.title} subtitle={detailT.dept || ""} onClose={() => setDetailT(null)} maxWidth={860}>
                    <TenderDetailView t={detailT} bidCount={bidCount(detailT.id)} />
                </Modal>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// BIDDER: PROFILE
// ─────────────────────────────────────────────────────────────────────────────

const BidderProfile = ({ user, bids, tenders, toast }) => {
    const [f, setF] = useState({ full_name: user.name, company_name: user.company, reg_no: user.regNo !== "—" ? user.regNo : "", password: "", company_type: "" });
    const [saving, setSaving] = useState(false);

    // Fetch company_type from DB on mount (not stored in session)
    useEffect(() => {
        supabase.from("profiles").select("company_type").eq("id", user.id).maybeSingle()
            .then(({ data }) => { if (data?.company_type) setF(p => ({ ...p, company_type: data.company_type })); });
    }, [user.id]);

    const save = async () => {
        setSaving(true);
        const u = { full_name: f.full_name, company_name: f.company_name, reg_no: f.reg_no, company_type: f.company_type || null };
        if (f.password) u.password_hash = f.password;
        const { error } = await supabase.from("profiles").update(u).eq("id", user.id);
        setSaving(false); toast(error ? error.message : "Profile updated.", error ? "error" : "success");
    };

    const awarded = bids.filter(b => b.status === "awarded").length;
    const activeBids = bids.filter(b => b.status !== "withdrawn");
    const totalVal = activeBids.reduce((a, b) => a + b.amount, 0);
    const successPct = activeBids.length ? ((awarded / activeBids.length) * 100).toFixed(0) : 0;
    const catsBid = [...new Set(bids.map(b => tenders.find(t => t.id === b.tender_id)?.category).filter(Boolean))];

    return (
        <div style={{ animation: "fadeUp .3s ease" }}>
            <PageHeader title="My Profile" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 22 }}>
                <Stat label="Total Bids" value={activeBids.length} color={T.accent} />
                <Stat label="Awarded" value={awarded} color={T.green} />
                <Stat label="Success Rate" value={successPct + "%"} color="#a78bfa" />
                <Stat label="Bid Value" value={fmt(totalVal)} color={T.accent} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 26 }}>
                    <div style={{ display: "flex", gap: 14, marginBottom: 22, paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
                        <Avatar name={user.name} size={52} />
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{user.name}</div>
                            <div style={{ fontSize: 12, color: T.muted }}>{user.email}</div>
                            <div style={{ fontSize: 10, color: T.green, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.08em" }}>Active Bidder</div>
                        </div>
                    </div>
                    <Input label="Full Name" value={f.full_name} onChange={e => setF(p => ({ ...p, full_name: e.target.value }))} />
                    <Input label="Email" value={user.email} disabled />
                    <Input label="Change Password" type="password" placeholder="Leave blank to keep current" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} />
                    <Btn variant="primary" onClick={save} loading={saving}>Save Changes</Btn>
                </div>
                <div>
                    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24, marginBottom: 14 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 16 }}>Company Details</div>
                        <Input label="Company Name" value={f.company_name} onChange={e => setF(p => ({ ...p, company_name: e.target.value }))} />
                        <Input label="Registration / CIN / GSTIN" value={f.reg_no} onChange={e => setF(p => ({ ...p, reg_no: e.target.value }))} />
                        <Select label="Company Type" value={f.company_type} onChange={e => setF(p => ({ ...p, company_type: e.target.value }))}>
                            <option value="">Select type…</option>
                            <option>Private Limited</option>
                            <option>Public Limited</option>
                            <option>Partnership</option>
                            <option>Proprietorship</option>
                            <option>LLP</option>
                            <option>Government Enterprise</option>
                            <option>PSU</option>
                        </Select>
                        <Btn variant="secondary" onClick={save} loading={saving}>Update</Btn>
                    </div>
                    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Bid Activity</div>
                        {[["Categories Bid On", catsBid.length], ["Active Bids", bids.filter(b => b.status === "submitted").length], ["Rejected", bids.filter(b => b.status === "rejected").length], ["Withdrawn", bids.filter(b => b.status === "withdrawn").length]].map(([l, v]) => (
                            <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${T.border}` }}>
                                <span style={{ color: T.muted }}>{l}</span>
                                <span style={{ fontWeight: 800, color: T.text, fontFamily: "'DM Sans', sans-serif" }}>{v}</span>
                            </div>
                        ))}
                        {catsBid.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                                <div style={{ fontSize: 9, color: T.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Categories Participated</div>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{catsBid.map(c => <CatBadge key={c} cat={c} />)}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// BIDDER APP SHELL
// ─────────────────────────────────────────────────────────────────────────────

export function BidderApp({ user, onLogout }) {
    const [page, setPage] = useState("browse");
    const [tenders, setTenders] = useState([]);
    const [bids, setBids] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        const [{ data: t }, { data: b }] = await Promise.all([
            supabase.from("tenders").select("*").in("status", ["active", "closed"]).order("created_at", { ascending: false }),
            supabase.from("bids").select("*").eq("bidder_id", user.id),
        ]);
        setTenders(t || []); setBids(b || []);
        setLoading(false);
    }, [user.id]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Lift saved tenders state here so the nav badge updates reactively when user saves/unsaves
    const [savedIds, setSavedIds] = useState(() => { try { return JSON.parse(localStorage.getItem("saved_tenders") || "[]"); } catch { return []; } });
    const pendingBids = bids.filter(b => b.status === "submitted").length;

    const navItems = [
        { id: "browse", icon: "🔍", label: "Browse Tenders" },
        { id: "saved", icon: "🔖", label: "Saved" },
        { id: "mybids", icon: "📋", label: "My Bids" },
        { id: "profile", icon: "👤", label: "My Profile" },
    ];

    return (
        <ToastContext>
            {toast => (
                <div style={{ display: "flex", minHeight: "100vh", fontFamily: "'DM Sans', -apple-system, sans-serif", background: "#f4f5f7", padding: 20, gap: 18 }}>
                    <GlobalStyle />
                    <SideNav items={navItems} active={page} setActive={setPage} user={user} onLogout={onLogout} brand="Bidder Portal" badge={{ saved: savedIds.length, mybids: pendingBids }} accent="#16a34a" accentSoft="#4ade80" />
                    <div style={{ flex: 1, padding: "6px 2px 30px 2px", overflowY: "auto", minWidth: 0 }}>
                        {loading ? <Spinner /> : (() => {
                            const props = { tenders, bids, user, toast };
                            const pages = {
                                browse: <BidderBrowse {...props} onBidAdded={b => setBids(prev => [b, ...prev])} savedIds={savedIds} setSavedIds={setSavedIds} setPage={setPage} />,
                                saved: <BidderSaved  {...props} />,
                                mybids: <BidderMyBids {...props} setBids={setBids} />,
                                profile: <BidderProfile {...props} />,
                            };
                            return pages[page];
                        })()}
                    </div>
                </div>
            )}
        </ToastContext>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────────────────────

export default function App() {
    // Restore remembered session from localStorage
    const [user, setUser] = useState(() => {
        try { return JSON.parse(localStorage.getItem("ebidder_user") || "null"); } catch { return null; }
    });
    const [loginPage, setLoginPage] = useState("bidder");

    // Called by login pages; persist to localStorage only when "Remember me" is ticked
    const handleLogin = (userData, remember = false) => {
        setUser(userData);
        if (remember) localStorage.setItem("ebidder_user", JSON.stringify(userData));
        else localStorage.removeItem("ebidder_user");
    };
    const handleLogout = () => { setUser(null); localStorage.removeItem("ebidder_user"); };

    if (!user) {
        if (loginPage === "admin") return <AdminLogin onLogin={handleLogin} goToBidder={() => setLoginPage("bidder")} />;
        return <BidderLogin onLogin={handleLogin} goToAdmin={() => setLoginPage("admin")} />;
    }
    if (user.role === "admin") return <AdminApp user={user} onLogout={handleLogout} />;
    return <BidderApp user={user} onLogout={handleLogout} />;
}